/**
 * Versioned API contract (docs/api-contracts.md, SPEC.md §15).
 *
 * Shared by the API and the browser bundles, so a change that breaks a client breaks the build
 * rather than production. Two rules are visible in the shapes below:
 *
 *  - **Money crosses the wire as a decimal string of minor units.** JSON has no bigint and
 *    `Number` would reintroduce the float that ADR 0009 exists to prevent.
 *  - **The client never sends a total.** It sends intent — amount, method, whether cost support
 *    is on — and receives server-computed money. There is no request shape here in which a
 *    client-supplied total could reach a processor.
 */
import { z } from "zod";
import { screenLayoutSchema } from "./screen-layout.js";
import { giftCaptureSchema } from "./card-capture.js";
import { processorApprovalSchema } from "./processor-approval.js";
import { cardFailureReasonSchema } from "./card-failure.js";
import { MEMBERSHIP_JOINED_AT_SIGN_IN, signUpAccessFields } from "./access.js";
/**
 * Re-exported so the browser bundles can reach them.
 *
 * `apps/donor-web` depends on this package and nothing else (ADR 0002) — that boundary is what
 * makes "the donor bundle never computes money" a property of the dependency graph rather than a
 * habit. Position coarsening and the result count are wire-shaping concerns: they describe what a
 * client may send and how much it may ask for, which is exactly what this package is for. The
 * definitions stay single-sourced in `@givehere/domain`.
 */
export { coarsenPosition, formatDistance, formatEin, parseEin, MAX_DIRECTORY_RESULTS, NEAREST_RESULT_COUNT, POSITION_GRID_DECIMALS, 
/**
 * The charitable-purpose finding text (DEC-028). Re-exported because the console must render the
 * **exact** sentence a reviewer affirms, and a second copy in the UI is a copy that drifts — at
 * which point people are certifying to one wording while another is stored against their name.
 */
GRANT_PURPOSE_FINDING, MIN_PURPOSE_STATEMENT_LENGTH, 
/**
 * DEC-046 answer 2: the terms must say who holds a gift to an unclaimed organization and what
 * happens on day N. The donor web's Terms page quotes this function rather than a copy of its
 * words (GH-812), so the page and the give screen cannot drift apart.
 */
heldGiftTermsSentence, DEFAULT_HOLD_DAYS as HELD_GIFT_DEFAULT_HOLD_DAYS, } from "@givehere/domain";
export const API_VERSION = "v1";
/** Decimal string of minor units. Rejects "25.50" and "2.5e3". */
export const minorUnitsSchema = z
    .string()
    .regex(/^\d+$/, "amount must be a whole number of minor units");
export const currencySchema = z.enum(["USD"]);
export const moneySchema = z.object({
    amountMinor: minorUnitsSchema,
    currency: currencySchema,
});
export const methodClassSchema = z.enum(["card", "wallet", "ach"]);
/**
 * The longest name a gift may be shown with, counted in Unicode code points (as
 * `DONOR_NOTE_MAX_LENGTH` is). iOS restates it as `DonorDisplayName.maximumLength`;
 * `apps/give-card-copy.test.ts` holds the two to the same number (GH-878).
 */
export const RECOGNITION_NAME_MAX_LENGTH = 80;
/**
 * Control characters a shown name may not carry: every C0 and C1 control (a name is one line) and
 * the bidirectional overrides and isolates that make a string render in a different order from the
 * one it is stored in. A name goes on a public screen, so a character whose only use is to make
 * that screen lie is refused rather than stripped.
 */
// eslint-disable-next-line no-control-regex -- matching control characters is the point.
const RECOGNITION_NAME_FORBIDDEN = /[\u0000-\u001F\u007F-\u009F\u202A-\u202E\u2066-\u2069]/u;
/**
 * A name typed for the public board (GH-878: the give card's name field, on every give screen).
 * Free text from a client, so the server — not the field — decides: trimmed, never blank, at most
 * 80 code points, plain text. It is only ever **display**: the server shows it where a donor chose
 * to be named (the public feed, the big screen's thank-you, the receipt's recognition line) and
 * never treats it as an identity. A donor who chose public anonymity sends `anonymous` and no name.
 */
export const recognitionNameSchema = z
    .string()
    .transform((value) => value.trim())
    .refine((value) => value.length > 0, { message: "A name to show cannot be blank." })
    .refine((value) => [...value].length <= RECOGNITION_NAME_MAX_LENGTH, {
    message: `A name to show can be at most ${RECOGNITION_NAME_MAX_LENGTH} characters.`,
})
    .refine((value) => !RECOGNITION_NAME_FORBIDDEN.test(value), {
    message: "A name to show must be plain text.",
});
export const recognitionSchema = z.discriminatedUnion("mode", [
    z.object({ mode: z.literal("anonymous") }),
    z.object({ mode: z.literal("donor_name"), displayName: recognitionNameSchema }),
    z.object({ mode: z.literal("family_or_organization"), displayName: recognitionNameSchema }),
    z.object({
        mode: z.literal("dedication"),
        kind: z.enum(["in_honor_of", "in_memory_of"]),
        displayName: recognitionNameSchema,
        honoreeName: recognitionNameSchema,
    }),
]);
/**
 * The organization's giving season, as donor-facing surfaces are told about it.
 *
 * Only the label, and only while the window is open — the server sends `null` at every other
 * moment, so a client never holds a date range it might compare itself and never learns that an
 * organization has a season coming. The label is the organization's own words for its own
 * calendar; nothing here names a faith, and nothing infers one (CLAUDE.md).
 *
 * Its presence is what switches the surfaces into the design's peak layout.
 */
export const givingSeasonSchema = z.object({
    label: z.string().max(60),
});
/* ------------------------------------------------------------------- context */
/* ----------------------------------------------------------- payment methods */
export const paymentMethodIdSchema = z.enum([
    "apple_pay",
    "google_pay",
    "card",
    "bank",
    "pledge",
    "check",
]);
/**
 * What the donor may choose, decided by the server.
 *
 * The browser feature-detects wallets, but it never decides whether a rail is *permitted* — that
 * follows from feature gates and unresolved decisions, and a client that computed it could show a
 * button for a capability nobody has approved.
 */
export const paymentMethodSchema = z.object({
    id: paymentMethodIdSchema,
    available: z.boolean(),
    /**
     * `simulated` means the synthetic rail: the flow is real, the money is not. The donor page must
     * say so on screen — a payment button that quietly moves nothing is the worst failure this
     * product can have.
     */
    mode: z.enum(["live", "simulated"]),
    /** Shown to the donor when unavailable. Never a stack trace, never a gate name. */
    unavailableReason: z.string().nullable(),
});
/**
 * Which Stripe mode this API is running in (GH-823): `test`, `live`, or `none` where the
 * deployment uses the synthetic rail or holds no recognisable key.
 *
 * Derived on the server from the configured secret key's prefix and **never** the key itself.
 * The app holds a publishable key compiled into its bundle; it opens Stripe's card sheet (and
 * Apple Pay through Stripe) only when that key's mode matches this one — `pk_test_` with `test`,
 * `pk_live_` with `live`. A mismatch is refused because a test key against a live intent, or the
 * reverse, reads to a donor as their own card being declined.
 *
 * Defaulted to `none` so a client reading an older server that sends nothing refuses the sheet,
 * which is the conservative answer. The value is informational to a client and decides nothing
 * on the server: no request carries a mode, and none would be believed.
 */
export const STRIPE_MODES = ["test", "live", "none"];
export const stripeModeSchema = z.enum(STRIPE_MODES);
/**
 * One thing a gift can be restricted to, in the organization's own words (DEC-043).
 *
 * Never called a *fund* on any donor surface. `giving-lane.ts` spends that word on Lane A and
 * DEC-040 made the Foundation a real §4966 sponsor, so two different things called a fund would
 * make the distinction DEC-028 rests on invisible to the donor.
 */
export const designationSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(60),
    description: z.string().max(240).nullable(),
    isGeneral: z.boolean(),
});
/**
 * What a surface draws before a gift is held for an organization that has not claimed its listing
 * (GH-798, DEC-046). Composed by the server — every sentence here is counsel's direction with the
 * configured hold length and recipient filled in — so no surface can invent wording that tells a
 * donor the organization already has the money.
 */
export const heldGiftDisclosureSchema = z.object({
    /** Who the receipt names as recipient (configuration; DEC-001 is open). */
    recipientName: z.string().min(1),
    /** The organization the gift is targeted for. */
    organizationName: z.string().min(1),
    /** Days until an unclaimed organization's held gift is sent by check. Always below 45. */
    holdDays: z.number().int().min(1).max(44),
    /** "X has not claimed its listing on GiveHere. Your gift is made to … and targeted for X." */
    notice: z.string().min(1),
    /** The terms sentence: GiveHere holds the funds until it claims; after N days, a check to the IRS address. */
    terms: z.string().min(1),
});
/**
 * `GET /v1/public/context/:token` query (GH-821). `pledgeId` says the checkout is paying the
 * signed-in donor's own open promise, which keeps the paying rails on after the event. The server
 * ignores it for anyone else, so it is never a way to learn that a pledge exists.
 */
export const contextQuerySchema = z.object({
    pledgeId: z.string().uuid().optional(),
    /**
     * The pay link this checkout came from (GH-845), as the signed token itself. Like `pledgeId`, it
     * keeps the paying rails on for a request whose pledge's event has ended; ignored — never refused
     * — for a token that does not name an open request of this organization and event.
     */
    paymentRequest: z.string().min(1).max(2048).optional(),
});
export const contextResponseSchema = z.object({
    organization: z.object({
        id: z.string().uuid(),
        displayName: z.string(),
        legalName: z.string(),
        /** False for an unclaimed record: discoverable, but donation-disabled (SPEC.md §6.1). */
        canAcceptDonations: z.boolean(),
        verificationLabel: z.string(),
        /**
         * Optional. Absent for most directory records, so the donor page draws a monogram instead —
         * the placeholder is the ordinary path, not an error path.
         */
        logoUrl: z.string().nullable(),
        /** "Springfield, IL". Disambiguates two organizations with similar names on the donor's screen. */
        locality: z.string().nullable(),
        /**
         * Formatted `NN-NNNNNNN`, or null when the directory record has none.
         *
         * Shown in the smallest type under the give button, as an identifier and nothing more. It is
         * deliberately unaccompanied by any claim about deductibility or exempt status — an EIN is
         * evidence of neither (DEC-001, DEC-011).
         */
        ein: z.string().nullable(),
    }),
    /** Which ways of giving this donor may actually use, in the order to offer them. */
    paymentMethods: z.array(paymentMethodSchema),
    /**
     * Donor-advised-fund giving via Chariot, when this organization has it enabled. `connectId`
     * is the publishable CID the page mounts the DAFpay component with. Null (the ordinary case)
     * draws nothing. Donations only — never offered on ticket purchases (IRS Notice 2017-73
     * forbids DAF grants paying for anything with donor benefits, even split).
     */
    daf: z.object({ connectId: z.string() }).nullable().default(null),
    /**
     * The event this context is addressed to, or **null when there is none to address**.
     *
     * Null is the unclaimed organization (DEC-042). Such an organization has no events at all — the
     * general-fund event is created on approval, and minting one earlier would be enabling an
     * organization by code, which `CLAUDE.md` forbids. A null event is therefore the structural
     * signal that **nothing here can be priced**: the server refuses to quote such a context, so no
     * donation and no payment can be addressed to one. It is the condition every surface reads to
     * decide that the only thing on offer is a statement of what somebody *would* give.
     *
     * Nullable rather than absent so a client that has not been rebuilt still parses the field.
     */
    event: z
        .object({
        id: z.string().uuid(),
        name: z.string(),
        version: z.number().int(),
        currency: currencySchema,
        defaultAmount: moneySchema.nullable(),
        suggestedAmounts: z.array(moneySchema),
        acceptingDonations: z.boolean(),
        /**
         * True where this event takes a **pledge** — a promise to pay later — whether or not it
         * takes money now (owner-directed 2026-10-01, GH-800). An ended or archived event is
         * `acceptingDonations: false, acceptingPledges: true`: the give screen offers Pledge in
         * place of the refusal, and `paymentMethods` marks every paying rail unavailable. Defaulted
         * so an older server reads as "pledges only where donations are accepted".
         */
        acceptingPledges: z.boolean().default(false),
    })
        .nullable(),
    /**
     * What the amount control offers where there is no event to take it from (DEC-042).
     *
     * Present exactly when `event` is null, and never otherwise — the two are complements, not
     * alternatives, and no surface should ever have to reconcile a value in both. The amounts are
     * still the **server's**, for the same reason an event's are: presets are never client-derived
     * (`docs/ios/DESIGN.md` §5), and a surface that invented them here would be inventing the
     * numbers a real organization is later quoted.
     */
    interest: z
        .object({
        currency: currencySchema,
        suggestedAmounts: z.array(moneySchema),
    })
        .nullable()
        .default(null),
    /**
     * Non-null exactly when this checkout holds the gift for an organization that has not claimed
     * its listing (GH-798). The surface must draw `notice` and `terms` before the pay button, and
     * must never say the organization has received anything. Null on every ordinary context and
     * everywhere while `FEATURE_UNCLAIMED_ORG_GIFTS` is off.
     */
    heldGift: heldGiftDisclosureSchema.nullable().default(null),
    campaignId: z.string().uuid().nullable(),
    /** The designation this context pinned, if any. Null is the ordinary case. */
    designationId: z.string().uuid().nullable(),
    /**
     * What this organization publishes, active only, in its own order.
     *
     * **A single-element array is the signal, and the client does not compute it.** The picker
     * renders only for two or more; with one — every organization on day one — nothing is drawn.
     * A control offering a single choice is a question with no answer, and on a donation flow it
     * reads as a step somebody forgot to remove.
     *
     * Defaulted so a client built before this field existed still parses a newer response.
     */
    designations: z.array(designationSchema).default([]),
    /** True where the event fixes the designation and a donor-supplied one is refused, not overridden. */
    designationLocked: z.boolean().default(false),
    /** Running season, if any — checkout raises the amount and drops the header to its short form. */
    season: givingSeasonSchema.nullable().default(null),
    suggestedAmount: moneySchema.nullable(),
    source: z.enum(["qr", "nfc", "short_link", "nearby", "direct"]),
    /**
     * The Stripe mode the API runs in (GH-823) — see `stripeModeSchema`. The app's card and Apple
     * Pay sheets open only when its publishable key's mode matches.
     */
    stripeMode: stripeModeSchema.default("none"),
});
/* --------------------------------------------------------------------- quote */
export const quoteRequestSchema = z.object({
    contextToken: z.string().min(1),
    /** The donation only. The total is derived server-side and returned. */
    amountMinor: minorUnitsSchema,
    methodClass: methodClassSchema,
    /** Defaults on, removable in one action (SPEC.md §10.4). */
    costSupportSelected: z.boolean(),
    recognition: recognitionSchema.default({ mode: "anonymous" }),
    /**
     * The promise this gift is meant to fund, if any.
     *
     * Only an identifier travels. The *amount* is still the donor's own `amountMinor` above and is
     * still checked against the signed quote — funding a pledge does not mean the client may name
     * a number, and a donor may deliberately pay part of a large promise. The server verifies the
     * pledge belongs to the caller's own session and is still open; the database verifies it
     * belongs to the same organization and event.
     */
    pledgeId: z.string().uuid().optional(),
    /**
     * What the donor chose to support.
     *
     * Only an identifier travels — `pledgeId` above is the precedent. The server resolves the
     * default (the event's, else the organization's general designation), refuses one belonging to
     * another organization with the same `tenant_mismatch` a stranger gets, refuses an archived
     * one, and refuses any value at all where the event is `designationLocked`. **The client never
     * decides the default**, and a mismatch is never silently coerced.
     */
    designationId: z.string().uuid().nullable().default(null),
    /**
     * The appeal rung this gift or pledge answers (GH-828), when the donor responded to the amount
     * being called from the stage.
     *
     * Only an identifier travels, as with `pledgeId`. The server refuses a rung that is not the one
     * being called on this event's running appeal, an `amountMinor` that is not the rung's amount,
     * and a rung together with a `pledgeId` (paying an earlier promise is not a new commitment).
     * The pledge or payment that follows is the ordinary one; the ladder counts it through the
     * quote. Optional, so every client built before it is unaffected.
     */
    appealRungId: z.string().uuid().optional(),
    /**
     * The payment request this gift answers (GH-845), as the signed pay-link token.
     *
     * When present the **server** decides the designation (the request's) and the pledge (the
     * request's) — `designationId` above is ignored rather than compared. `amountMinor` is the gift
     * the recipient chose (GH-849, owner 2026-10-02: part of what was asked, or more): any amount
     * from $1, checked on the server, which credits the request with at most what it still asks —
     * the rest of a larger gift is an ordinary gift. The charge is priced by the server from that
     * amount, exactly as any gift is. Refused for a request that is cancelled, expired, paid, or
     * belongs to another organization or event. Never combined with `pledgeId` or `appealRungId`.
     */
    paymentRequestToken: z.string().min(1).max(2048).optional(),
});
export const quoteResponseSchema = z.object({
    quoteId: z.string().uuid(),
    /** Signed. The payment command accepts this, never an amount. */
    quoteToken: z.string(),
    donation: moneySchema,
    costSupport: moneySchema,
    total: moneySchema,
    costSupportSelected: z.boolean(),
    costSupportLabel: z.string(),
    /**
     * The same sentence for a surface with one line. The long label is the *offer* a donor is
     * shown when deciding; this is the *statement* that sits beside an amount already on screen.
     * Server-supplied like its long twin, because how this is characterized is DEC-007's call and
     * not a view's.
     */
    costSupportShortLabel: z.string(),
    /**
     * The *offer* in five words or fewer — the button, when the donor has not taken it.
     *
     * `costSupportLabel` remains the full sentence and remains what a surface shows when it has room
     * for it; this is what the give page renders, with the long label and the decline note behind an
     * ⓘ (owner-directed 2026-08-31). Server-supplied like both of its twins, because DEC-028 decides
     * how this is characterized and shortening a sentence is a way of re-characterizing it.
     *
     * Optional so an older client still parses; a surface that has this field should prefer it.
     */
    costSupportOfferShortLabel: z.string().optional(),
    /**
     * What declining costs the organization (DEC-034).
     *
     * The platform fee applies to every gift; the donor's choice decides who funds it. Before
     * DEC-034 declining had no consequence worth naming, so no surface named one — and a screen
     * that offers to make "100% reach them" while staying silent about the alternative implies the
     * alternative is free. Server-supplied for the same reason the two labels above are: how the
     * fee is characterized is not a view's call.
     *
     * Optional so that a client built before this field existed still parses. A surface that shows
     * the cost-support choice at all should render it.
     */
    costSupportDeclineNote: z.string().optional(),
    /**
     * The one name for the covered amount wherever it is itemised — "Processing fee" (owner-directed
     * 2026-10-02, GH-839). Every surface uses this rather than its own wording, so the bill, the
     * receipt and the email cannot drift into three names for one figure. Optional: an older server.
     */
    costSupportItemLabel: z.string().optional(),
    /** The schedule this quote was priced with, in words: "2.9% + $0.30 per gift". */
    feeScheduleLabel: z.string().optional(),
    expiresAt: z.string(),
    organizationDisplayName: z.string(),
    eventName: z.string(),
    /**
     * What the server decided, not what the client asked for. A confirmation surface shows this.
     * Optional for the same reason `costSupportDeclineNote` is: an older client still parses.
     */
    designationId: z.string().uuid().nullable().optional(),
    designationName: z.string().nullable().optional(),
});
/* ------------------------------------------------------------------ payments */
/** The button the donor actually pressed. Display metadata, never a credential. */
export const chosenPaymentMethodSchema = z.enum(["card", "apple_pay", "google_pay"]);
/**
 * The longest note a donor may attach to a gift (GH-793), counted in Unicode code points — the
 * unit PostgreSQL's `char_length` counts, so the contract and the column's CHECK agree about one
 * emoji. iOS restates it as `DonorNote.maximumLength`; `apps/ios-donor-note.test.ts` holds the two
 * to the same number.
 */
export const DONOR_NOTE_MAX_LENGTH = 280;
/**
 * Control characters a plain-text note may not carry: C0 except tab, line feed and carriage return,
 * DEL and C1, and the bidirectional overrides and isolates that let a string render in a different
 * order from the one it is stored in. A note is shown to staff verbatim, so a character whose only
 * use is to make the screen lie about the text is refused rather than stripped.
 */
// eslint-disable-next-line no-control-regex -- matching control characters is the point.
const DONOR_NOTE_FORBIDDEN = /[\u0000-\u0008\u000B\u000C\u000E-\u001F\u007F-\u009F‪-‮⁦-⁩]/u;
/**
 * A note from the donor to the organization's staff (GH-793). Plain text, at most 280 code points.
 *
 * **Free text is treated as sensitive wherever it goes.** It can carry a name being remembered, a
 * medical reason, or a faith reference — and religious affiliation is special-category data. So:
 * stored on the donation, shown to the organization's staff on the console's gift detail, never
 * in a log line (`donorNote` is on the redaction key list), and never in the CSV export.
 *
 * **It is private unless the donor asks otherwise, and a host agrees** (GH-887, DEC-053 decided
 * by the owner 2026-10-04). A note reaches the public feed and the event's big screen only when
 * the donor ticked `showNoteOnScreen` on that gift *and* a host approved that note in the
 * console's queue — never otherwise, never for a donor hidden from organizations (DEC-049), and
 * an anonymous gift's note carries no name. See `screenNoteForPublic`.
 *
 * Trimmed, and an empty or whitespace-only note becomes null — "no note" has one spelling.
 * Validation messages name the field and never echo the text (`request-validation.ts`).
 */
export const donorNoteSchema = z
    .string()
    .transform((value) => value.trim())
    .refine((value) => [...value].length <= DONOR_NOTE_MAX_LENGTH, {
    message: `A note can be at most ${DONOR_NOTE_MAX_LENGTH} characters.`,
})
    .refine((value) => !DONOR_NOTE_FORBIDDEN.test(value), {
    message: "A note must be plain text.",
})
    .transform((value) => (value === "" ? null : value));
/**
 * The note as it may leave the server on the public feed (GH-887), or null.
 *
 * The text was checked by `donorNoteSchema` when it was written and by the column's CHECK, but the
 * public projection checks again rather than trusting storage: trimmed, at most
 * `DONOR_NOTE_MAX_LENGTH` code points, and refused outright (null — the gift is still shown,
 * without its words) if it carries a control or bidirectional-override character. Nothing is
 * added to it: no name, no id.
 */
export function screenNoteForPublic(stored) {
    if (stored === null)
        return null;
    const checked = donorNoteSchema.safeParse(stored);
    return checked.success ? checked.data : null;
}
export const paymentIntentRequestSchema = z.object({
    quoteToken: z.string().min(1),
    /**
     * What the donor actually saw. Checked against the signed quote; a mismatch is refused rather
     * than silently charging the server's number (SPEC.md §18.1).
     */
    displayedTotal: moneySchema,
    /**
     * Which way the donor chose to pay, recorded so their next Give button can lead with it.
     * Optional and advisory — it changes nothing about the charge itself.
     */
    chosenMethod: chosenPaymentMethodSchema.optional(),
    /**
     * One-tap reuse of a saved instrument (GH-330). Requires the donor's session bearer on the
     * same request; the server verifies ownership and confirms the charge itself — the id alone
     * authorizes nothing.
     */
    savedInstrumentId: z.string().uuid().optional(),
    /**
     * A note for the organization's staff (GH-793). See `donorNoteSchema` for where it may and may
     * not go. Optional and nullable so every client built before it still parses; absent, null and
     * blank all store no note.
     *
     * On this request rather than the quote because every rail — Apple Pay, a new card, a saved
     * instrument, a bank — opens its payment here, and the quote is re-priced on every amount change
     * where a note would ride along for nothing. A pledge and a fund grant carry none: neither is a
     * gift yet.
     */
    donorNote: donorNoteSchema.nullable().optional(),
    /**
     * The donor ticked "Show on the event screen" beside their note (GH-887). Unticked by default
     * and optional, so every older client parses and means "private". It asks; it does not publish:
     * the note waits for a host's approval in the console before the public feed carries it. True
     * with no note stores nothing — there is nothing to show.
     */
    showNoteOnScreen: z.boolean().optional(),
});
export const paymentIntentResponseSchema = z.object({
    donationId: z.string().uuid(),
    paymentAttemptId: z.string().uuid(),
    /**
     * Not a secret key; it authorizes only this one payment from the browser. Null on the Plaid
     * bank rail, where authorization happens in Plaid Link instead of a Stripe element.
     */
    clientSecret: z.string().nullable(),
    /** Set for a bank (ACH) payment: which rail the platform chose for it (DEC-003). */
    bankRail: z.enum(["plaid", "stripe"]).nullable(),
    /** Plaid rail only: the Link token the browser opens Plaid Link with. */
    plaidLinkToken: z.string().nullable(),
    /**
     * Short-lived, single-purpose token letting this browser read *this* donation's status and
     * receipt. It is what makes a receipt reachable with no account (SPEC.md §5.3) without
     * exposing donation data in a URL (§11.2).
     */
    accessToken: z.string(),
    /**
     * Always a pre-confirmation state. A client redirect can never complete a payment
     * (SPEC.md §5.4) — only a verified webhook can.
     */
    state: z.enum(["created", "requires_action", "processing"]),
});
/** Completing Plaid Link: the server reads the authorization decision from Plaid, never the client. */
export const bankLinkCompleteResponseSchema = z.object({
    state: z.enum(["processing", "failed", "pending"]),
});
/** The ACH rail switch (DEC-003): Plaid primary, Stripe backup, platform-staff controlled. */
export const achRailSchema = z.object({ rail: z.enum(["plaid", "stripe"]) });
/**
 * The **payout** rail switch (DEC-023, owner-decided 2026-08-23): Plaid primary, Stripe secondary.
 *
 * Structurally identical to `achRailSchema` and deliberately not the same schema. They describe
 * two different levers over two different vendor approvals — debit origination and credit
 * origination are separately consented — and sharing one type is how a later widening of one
 * silently widens the other.
 */
export const payoutRailSchema = z.object({ rail: z.enum(["plaid", "stripe"]) });
/**
 * Removed 2026-08-21: a second, subtly different shape for `GET /v1/donations/:id/status`.
 *
 * It carried a flat `receiptNumber` where the route actually returns a nested `receipt` object,
 * and nothing but the iOS fixture generator ever referenced it — so the fixtures the iOS
 * contract-drift gate checks against were generated from a schema the server does not emit. The
 * iOS app modelled the fixture, decoded a key that never arrives, and silently showed no receipt
 * number on any real gift, because a missing key decodes to nil rather than failing.
 *
 * The lesson is worth more than the fix: a fixture generator that can name a schema no route uses
 * is a gate that can pass while proving nothing. `donationStatusSchema` below is the one the route
 * parses, and it is now the one the fixtures are built from.
 */
/* ------------------------------------------------------------- activity feed */
/**
 * One gift on the public board.
 *
 * What is *absent* is the contract: no donation id, no donor id, no receipt number, no contact,
 * nothing that joins to another endpoint. A viewer of the projector screen learns an amount, a
 * kind, and whatever name the donor chose to publish — and for an anonymous donor, `displayName`
 * is null and there is nothing else in the shape that could distinguish them from any other
 * anonymous gift (SPEC.md §11.2).
 */
export const publicGiftSchema = z.object({
    /**
     * Position in this event's public sequence. Ordering only — the display uses it to tell which
     * entries are new since the last poll, which it cannot do from an amount and a timestamp alone.
     */
    sequence: z.number().int(),
    amount: moneySchema,
    /** A pledge is a promise and is labelled as one, never merged into received money (ADR 0012). */
    kind: z.enum(["received", "pledged"]),
    /** The name the donor chose to publish. Null means they chose anonymity; show "Anonymous". */
    displayName: z.string().nullable(),
    /**
     * The donor's note (GH-887), only when they ticked "Show on the event screen" on this gift and a
     * host approved it; null otherwise. Plain text, ≤ 280 code points, re-checked on the way out
     * (`screenNoteForPublic`). Optional so a response from an older server parses as "no note".
     */
    note: z
        .string()
        .max(DONOR_NOTE_MAX_LENGTH * 2)
        .nullable()
        .optional(),
    /**
     * GH-958: the opaque handle the "Just now" row's Report action sends back — a salted one-way
     * hash of the gift's id, never the id. Optional so a response from an older server parses.
     */
    reportRef: z.string().max(512).nullable().optional(),
    at: z.string(),
});
/** How many recent gifts the board shows. */
export const PUBLIC_FEED_SIZE = 5;
/* ------------------------------------------------------------- appeal ladder */
/**
 * The live appeal ladder as the room sees it (GH-828) — on the big screen, and in the goal payload
 * a donor's phone already polls, so iOS and donor-web can say "The room is at $1,000 — pledge or
 * give" without a new read.
 *
 * Counts and amounts only: no donor, no pledge or donation id, and no split between raised hands,
 * pledges and gifts (that breakdown is the console's). A rung's `id` is an operational identifier
 * a donor's quote names (`appealRungId`) to answer it.
 *
 * **`committed` is not money received.** It is every commitment at its rung's amount — raised
 * hands, pledges and completed gifts — summed on the server, and it is reported here and nowhere
 * else. A surface labels it "committed" and keeps it apart from the thermometer's received total;
 * it must never be added to `received`, `processing` or `pledged`.
 */
/**
 * A named target (GH-882): what one commitment at a rung provides, in the organizer's own words,
 * so the room hears "Feed 10 orphans @ $50.00 each" rather than "10 @ $50.00". One commitment is
 * one unit — the count the ladder already keeps is the number of units, and nothing about money
 * changes. The words are tenant-authored free text; the product supplies none of them, so no noun
 * or verb in the code assumes a cause, a community or a faith.
 */
export const APPEAL_UNIT_NOUN_MAX_LENGTH = 40;
/** The verb before the count ("Feed", "Sponsor", "Build"). Optional; a short phrase at most. */
export const APPEAL_UNIT_VERB_MAX_LENGTH = 24;
/**
 * One line of plain text from an organizer, decided on the server: trimmed, never blank, at most
 * `max` code points, and no control or bidirectional-override character — the same refusal the
 * public name gets, because these words go on a projector.
 */
const appealUnitText = (what, max) => z
    .string()
    .transform((value) => value.trim())
    .refine((value) => value.length > 0, { message: `The ${what} cannot be blank.` })
    .refine((value) => [...value].length <= max, {
    message: `The ${what} can be at most ${max} characters.`,
})
    .refine((value) => !RECOGNITION_NAME_FORBIDDEN.test(value), {
    message: `The ${what} must be plain text.`,
});
/** A named target as the console sends it. A blank or absent verb is no verb; the noun is required. */
export const appealUnitInputSchema = z.object({
    verb: z
        .string()
        .nullish()
        .transform((value) => value === undefined || value === null || value.trim() === "" ? null : value)
        .pipe(appealUnitText("verb", APPEAL_UNIT_VERB_MAX_LENGTH).nullable()),
    noun: appealUnitText("unit", APPEAL_UNIT_NOUN_MAX_LENGTH),
});
/** A named target as the server reports it — already checked on the way in, so only its shape. */
export const appealUnitSchema = z.object({
    verb: z.string().nullable(),
    noun: z.string(),
});
export const appealRungPublicSchema = z.object({
    id: z.string().uuid(),
    amount: moneySchema,
    /** How many commitments the organizer hopes for at this amount; null when none was set. */
    targetCount: z.number().int().positive().nullable(),
    /**
     * What one commitment provides, when the organizer named it (GH-882); null for a plain amount.
     * Defaulted, so a response from a server that predates it reads as "not named".
     */
    unit: appealUnitSchema.nullable().default(null),
    /** Raised hands + pledges + completed gifts at this amount. */
    committedCount: z.number().int().nonnegative(),
    current: z.boolean(),
});
export const appealPublicSchema = z.object({
    /** Only a running appeal is published; a closed or unstarted one is absent (`null`). */
    status: z.literal("running"),
    /** The amount being called right now. */
    currentRung: appealRungPublicSchema,
    /** The whole ladder in call order, the current rung marked. */
    rungs: z.array(appealRungPublicSchema),
    /** Every commitment at its rung's amount, summed on the server. Not received money. */
    committed: moneySchema,
    commitmentCount: z.number().int().nonnegative(),
});
/* ------------------------------------------------------------------ goal */
export const goalResponseSchema = z.object({
    eventId: z.string().uuid(),
    eventName: z.string(),
    organizationDisplayName: z.string(),
    goal: moneySchema,
    /** Reported separately and never pre-summed (ADR 0012, SPEC.md §7.2). */
    received: moneySchema,
    processing: moneySchema,
    pledged: moneySchema,
    countsPending: z.boolean(),
    countsPledges: z.boolean(),
    donationCount: z.number().int(),
    /**
     * The event's lifecycle state, so the big screen can close the night down honestly: an
     * "ended" event renders a thank-you-and-summary screen instead of a live thermometer.
     * Defaulted for compatibility with responses minted before the field existed.
     *
     * The **effective** status (GH-800): `published` is announced and not yet started, `live` is on
     * by its window or because an organizer started it, `ended` is past its end or ended by an
     * organizer. Never the stored value, which no longer says whether anything is on.
     */
    eventStatus: z
        .enum(["draft", "reviewable", "published", "live", "ended", "archived", "suspended"])
        .default("live"),
    /** Age is always visible so a stale screen cannot look confident (SPEC.md §7.2). */
    asOf: z.string(),
    /**
     * True only when a change to this total (a counted gift, refund, reversal, offline entry,
     * dispute, pledge, or money entering or leaving processing) has been missing from it for more
     * than 30 s (GH-822). Read by the iOS admin only; donor surfaces never show it (GH-817).
     */
    stale: z.boolean(),
    /**
     * The most recent gifts, newest first. Defined below the money fields on purpose: this is
     * decoration for the room, and the totals above it remain the record.
     */
    recentGifts: z.array(publicGiftSchema).default([]),
    /**
     * The organization's giving season while it is running. The display reads it to grow the hero
     * and to stand down the feed between gifts; it carries no identity and no dates.
     */
    season: givingSeasonSchema.nullable().default(null),
    /**
     * The live appeal ladder while it is running (GH-828), else null. Optional so every client
     * built before it still parses, and so a response from an older server (no field) reads as "no
     * appeal". See `appealPublicSchema` — its `committed` is never received.
     */
    appeal: appealPublicSchema.nullish(),
});
/* -------------------------------------------------------------- event page */
/**
 * `GET /v1/public/events/:eventId` — who is holding this event, and what it is (GH-781).
 *
 * ## Why this is not more fields on `/goal`
 *
 * The donor's event page needs two unrelated things: a **number that moves every few seconds**
 * and an **identity that does not move at all**. `/goal` is the first of those — polled every
 * four seconds by three surfaces, cached five, and deliberately narrow: `Display.tsx` states
 * outright that its safety comes from a response shape with no donation id, donor id, or contact
 * in it. Widening it to carry an organization id and a logo would hand the hall's wall an
 * identifier it has no use for, and would make a slow-moving payload ride a fast cache.
 *
 * So this is a separate read at `max-age=60`, the same shelf life the organization detail route
 * already uses for the same kind of facts.
 *
 * ## What is deliberately absent
 *
 * **No money.** Not the goal, not the received total, not a suggested amount. Every figure on the
 * event page comes from the goal projection, which is the one place the arithmetic lives; a
 * second source for the same number is how two parts of one screen come to disagree.
 *
 * **No EIN, legal name, address, or website.** The ⓘ panel fetches the organization's own detail
 * payload when a donor opens it. A page that pre-loaded the identity panel would be sending an
 * organization's registry record to every phone that glanced at the screen.
 *
 * **Nothing about who is in the room.** No attendee count, no check-in count, no ticket buyer, no
 * donor of any kind. This is a public, unauthenticated, identifier-free read like every other
 * route in the public directory, and it stays one.
 */
export const eventPageResponseSchema = z.object({
    organization: z.object({
        id: z.string().uuid(),
        displayName: z.string(),
        logoUrl: z.string().nullable(),
        /** The server's own wording about this organization's standing, rendered verbatim. */
        verificationLabel: z.string(),
        locality: z.string().nullable(),
        /**
         * The gate on the Give button, decided by the server and never by the client. False is the
         * unclaimed record (DEC-042) and the page must draw no give control at all — the quote route
         * would refuse the link this page would otherwise mint.
         */
        canAcceptDonations: z.boolean(),
        /** The label only, and only while the declared window is open — never the dates. */
        season: givingSeasonSchema.nullable().default(null),
    }),
    event: z.object({
        id: z.string().uuid(),
        name: z.string(),
        /** The organizer's one-line reason. Null is ordinary; the page then shows the name alone. */
        purpose: z.string().nullable(),
        description: z.string().nullable(),
        /**
         * The full lifecycle, unlike `publicOrganizationEventSchema`'s three-value enum.
         *
         * This route resolves one event a donor already has the id of — from a link, a printed code,
         * or the organization's own page — rather than advertising a list, so hiding a status here
         * would answer 404 for an event that plainly exists and leave the donor with nothing to read.
         * The page decides what each state may offer: `draft`/`reviewable` draw no give button,
         * `ended`/`archived`/`suspended` draw no controls at all.
         */
        status: z.enum(["draft", "reviewable", "published", "live", "ended", "archived", "suspended"]),
        /**
         * True for the always-on general fund every approved organization gets on migration 0026.
         * Not a happening: no room to arrive at, no admission to sell, nothing running. The page
         * sends a donor who lands on one to the organization's page, which is what it actually is.
         */
        isGeneralFund: z.boolean(),
        startsAt: z.string().nullable(),
        endsAt: z.string().nullable(),
        /** Free text as the organizer wrote it; never geocoded (migration 0006). */
        location: z.string().nullable(),
        /** The venue's postal address — a public place being advertised, not a donor's position. */
        address: z.string().nullable(),
        /**
         * Where people join from afar, for a hybrid event (GH-866): an `https://` link the organizer
         * published. Defaulted so an older server's answer still parses.
         */
        onlineUrl: z.string().nullable().default(null),
    }),
    /**
     * The cheapest ticket a donor could actually buy right now, or null when there is nothing to
     * sell. Present so the page can offer "Tickets from $25" without a second request for a tier
     * list it would then throw away — and absent, rather than zero, when sales are closed, because
     * a card offering admission that cannot be bought is a control the server would refuse.
     */
    tickets: z.object({ from: moneySchema }).nullable(),
    /**
     * The event's speakers in the organizer's order, with photos (GH-862). The same list the
     * tickets page carries; defaulted so an older server's answer still parses.
     */
    // Lazy: the speaker schema is declared with the rest of the event page content further down.
    speakers: z.array(z.lazy(() => publicLineupEntrySchema)).default([]),
});
/**
 * A server-minted checkout link for a donor who arrived without one.
 *
 * The directory and the goal page cannot construct a checkout URL themselves: only the server
 * signs context (SPEC.md §5.1), and that is what keeps a forged "donate to me" link from
 * resolving. This response carries the signed token; the caller appends it to its own origin as
 * `/g/<token>`, so the API never states what domain the donor page lives on.
 */
export const donationLinkResponseSchema = z.object({
    token: z.string().min(1),
    /** When the token stops resolving, so a long-lived tab can refresh it instead of failing. */
    expiresAt: z.string(),
});
/**
 * How loudly the room should react to a gift.
 *
 * A fixed ladder in minor units rather than a fraction of the goal: a $1,000 gift is a $1,000 gift
 * whether the goal is $5,000 or $5,000,000, and scaling by goal would make every gift to an
 * ambitious campaign feel small — which is the opposite of what a fundraiser needs at the moment
 * someone gives.
 */
export const CELEBRATION_TIERS = [
    { minMinor: 100000n, tier: "landmark" },
    { minMinor: 50000n, tier: "major" },
    { minMinor: 10000n, tier: "notable" },
    { minMinor: 0n, tier: "gift" },
];
export function celebrationTierFor(amount) {
    const minor = BigInt(amount.amountMinor);
    return CELEBRATION_TIERS.find((step) => minor >= step.minMinor)?.tier ?? "gift";
}
/* ----------------------------------------------------------------- directory */
/** One of an organization's next events on a directory row (GH-807). */
export const directoryUpcomingEventSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /** Always stated: an undated event is never upcoming. ISO 8601, an instant. */
    startsAt: z.string(),
    endsAt: z.string().nullable(),
});
export const directoryEntrySchema = z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    legalName: z.string(),
    /** "Springfield, IL", already composed, so two organizations with one name are told apart. */
    locality: z.string().nullable(),
    logoUrl: z.string().nullable(),
    canAcceptDonations: z.boolean(),
    /** "Verified organization" or "Unclaimed — donations disabled" (SPEC.md §6.1). */
    verificationLabel: z.string(),
    /**
     * Pre-formatted ("< 1 mi", "12 mi") rather than a number.
     *
     * The donor's position was coarsened to roughly a kilometre before it was sent, so a raw
     * distance would invite a client to render a precision the input never had.
     */
    distanceLabel: z.string().nullable(),
    /**
     * Where "Give" goes: a real event when one is running, otherwise the organization's always-on
     * general fund. Null when it has neither.
     */
    liveEventId: z.string().uuid().nullable(),
    liveEventName: z.string().nullable(),
    /**
     * True when `liveEventId` is the general fund rather than something happening.
     *
     * The field the two donor surfaces split their directory on — events above, organizations
     * below. Migration 0026 gives every approved organization one always-on `published` general
     * fund so a gift is possible between campaigns; without this flag a client cannot tell that
     * apart from a fundraiser tonight, and every approved organization reads as if something is on.
     *
     * A general fund is somewhere to *send* a gift and not an occasion: it has no admission to sell
     * and no room to arrive at, so a client reads this before offering Tickets or Enter.
     *
     * Defaulted rather than required, so a client reading an older server sees `false` — the same
     * behaviour it had before the field existed — instead of failing the whole page.
     */
    liveEventIsGeneralFund: z.boolean().default(false),
    /**
     * Whether something is on here right now — the server's answer, never a client's (GH-790).
     *
     * True only for a claimed organization that can accept donations whose landing event is a real
     * event (not the general fund) whose effective status is `live` (GH-800). It is the one field a client
     * reads to file a row under Events, draw it LIVE, or open the event room rather than the
     * organization. Deriving it from `liveEventId` and `liveEventIsGeneralFund` is the build-22
     * defect: an unclaimed listing, or a fundraiser published for next month, read as live.
     *
     * Defaulted to `false` so a client reading an older server files every row as an organization —
     * the safe direction, since an organization row opens a page that states its own status.
     */
    isHappening: z.boolean().default(false),
    /** The live event's own venue address, when the organizer set one. Display text only. */
    liveEventAddress: z.string().nullable(),
    /**
     * Where this row **is**, so a client can draw it on a map: the live event's venue when it has
     * one, else the organization's own public position. Null when neither is geocoded.
     *
     * ## Why a coordinate is publishable here when a distance is not
     *
     * The two nearby fields below are deliberately shy, and it is worth being precise about what
     * they are shy *of*. `distanceLabel` is pre-formatted and `withinNearbyRadius` is a boolean
     * because both are functions of **the donor's** position — coarsened to roughly a kilometre
     * before it was sent — so any number derived from them would invite a precision the input never
     * had.
     *
     * This field is not a function of the donor at all. **An organization's own location is not the
     * donor's.** `packages/db/src/repositories/events.ts` already reasons exactly this about the
     * venue half of it: geocoded venue coordinates are *"stored as returned (not coarsened — a venue
     * is a public place being advertised, unlike a donor's position)"*. It is the same fact
     * `liveEventAddress` above and the detail page's `address` already publish in prose, in the form
     * a map can draw.
     *
     * What does not change, and what this field must never become the loophole for:
     *
     *  - the donor's position is never published, by this route or any other;
     *  - the donor's position never leaves a client uncoarsened;
     *  - no client combines the two into a distance and prints it. `ADDENDUM.md` screen 2 puts **no
     *    distance on any directory row**, and that rule is unchanged — a map says *where things
     *    are*, not how far away they are.
     *
     * A pair rather than two nullable numbers, because half a coordinate is no coordinate — the
     * same invariant the writer in `events.ts` keeps and the reader in `directory.ts` mirrors.
     *
     * Defaulted rather than required on the wire so a client reading an older server sees `null`
     * (a row with no map pin) instead of a parse failure; the server side is still obliged to send
     * it, because `toWire` is typed against the parsed shape where it is not optional.
     */
    position: z.object({ latitude: z.number(), longitude: z.number() }).nullable().default(null),
    /**
     * True when this row is within about a mile of the donor's shared position (measured from the
     * live event's venue when it has one, else the organization). A boolean rather than a raw
     * distance for the same reason `distanceLabel` is pre-formatted: the input was coarsened, and
     * a number would invite precision it never had. Always false when no position was shared.
     */
    withinNearbyRadius: z.boolean(),
    /**
     * The organization's next events, soonest first, at most three (GH-807; owner, build 25:
     * *"Search results should show future events when I search for a specific org or location …
     * Put first 3 upcoming events for an org under it"*).
     *
     * Announced and not started by the effective status (GH-800) — never one that is on (that is
     * `isHappening`), ended, archived, a draft, undated, or the general fund — and always empty for
     * an organization that cannot take a gift. Public fields only: the same announcement the
     * organization's own events list already makes.
     *
     * Defaulted so a client reading an older server sees no events rather than failing the page;
     * an older client ignores the key.
     */
    upcomingEvents: z.array(directoryUpcomingEventSchema).max(3).default([]),
});
export const directoryResponseSchema = z.object({
    results: z.array(directoryEntrySchema),
    /** True when more exist beyond what was returned, so "Show more" is never a dead control. */
    hasMore: z.boolean(),
    /** Echoes how the results were found, so the page can say "Nearest you" or "Results for …". */
    mode: z.enum(["nearby", "search", "browse"]),
    /**
     * GH-871a (DEC-052): present, and true, only while membership is on and the caller is not a
     * member — every row's event fields are then empty, and a `requireEvent` list is empty.
     */
    eventsHidden: z.literal(true).optional(),
});
/**
 * One person an organization has chosen to name on its own public page (GH-770).
 *
 * The organization authors this list; the platform never derives it. In particular it is not
 * built from `organization_members`, because that would publish *who administers the money* — a
 * targeting list for social engineering against the people who can move payouts.
 *
 * Name and role text and nothing else, and each omission is load-bearing rather than a stub to
 * fill in later. No photograph: a face beside a name at a named religious organization is the
 * special-category inference of CLAUDE.md, drawn. No email, phone, or any other contact: the
 * organization already publishes one number, and a page listing five people's is a
 * contact-harvesting surface that would break `directory-routes.ts`'s guarantee that no public
 * read reaches a contact. No biography: prose about a person is a far larger consent question
 * than a title is. See DEC-044 for the consent question this shape narrows but does not close.
 */
export const organizationPersonSchema = z.object({
    /** As the organization writes it, in whatever script. Never parsed, never split into parts. */
    name: z.string().min(1).max(120),
    /**
     * "Imam", "Board chair", "Volunteer coordinator" — the organization's own words.
     *
     * Free text rather than an enumeration on purpose: a fixed vocabulary of titles would be a
     * vocabulary from some one tradition, and offering it would make the product take a position
     * on how an organization is structured (CLAUDE.md, denomination-neutrality).
     */
    roleText: z.string().min(1).max(80),
});
/**
 * The console's whole-list save.
 *
 * A replace rather than a per-row patch because **order is part of the content** — the array
 * index is the sort order, assigned server-side, so the client never sends an integer anyone has
 * to trust or reconcile. The same shape `events.updateEventPage` uses for the same reason.
 *
 * ## Two limits, and only one of them is here
 *
 * The bound below is a **boundary guard**: it stops an unbounded array from being parsed,
 * validated and held in memory, and nothing more. The product limit is 24, and it lives in
 * `organizationPeople.replacePeople` rather than here, because a schema refusal arrives as
 * "Some of the details were not valid" — the generic sentence `parseBody` composes for a list of
 * Zod issues — while an organization manager who typed a 25th name needs to be told the number
 * and what to do about it. A limit whose refusal cannot be read is a limit enforced twice and
 * explained never.
 */
export const organizationPeopleSchema = z.object({
    people: z
        .array(organizationPersonSchema.extend({ published: z.boolean().default(true) }))
        .max(200),
});
/**
 * One event on an organization's own page: what it is, when, and where (GH-770).
 *
 * ## What this deliberately does not carry, and why
 *
 * **No money.** No goal, no raised figure, no gift count. A goal with no numerator invites a
 * client to render "of $50,000" with nothing before it, and one `/goal` read per row would be an
 * N+1 on a public route. Progress stays on `GET /v1/public/events/:id/goal`, read once for the
 * event the page leads with.
 *
 * **No general fund.** `is_general_fund` rows are excluded in SQL from both buckets. A general
 * fund is somewhere to *send* a gift and not an occasion to attend, check into, or buy admission
 * to — the same sentence `directoryEntrySchema.liveEventIsGeneralFund` already makes. It is
 * named once, separately, as `generalFundEventId` on the detail payload.
 *
 * **No version, no default designation, no designation lock.** Financial configuration; the
 * signed checkout context carries it, and a public list is not where a client learns it.
 *
 * **No coordinates.** The directory row already publishes a position for the one event it
 * advertises. A pin per historical event buys nothing any surface draws today.
 *
 * **Nothing about who was there.** No attendee count, no check-in count, no ticket-buyer, no
 * recent-gift feed. That is the constraint the whole public directory file is built around.
 */
export const publicOrganizationEventSchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /** The organizer's one-line reason. Null is ordinary; the row then renders the name alone. */
    purpose: z.string().nullable(),
    /**
     * Only these three ever reach the wire. `draft` and `reviewable` are unpublished states,
     * `archived` is withdrawn, and `suspended` is emergency suspension for impersonation,
     * sanctions or a legal demand (SPEC.md §6.4) — re-advertising a suspended event under
     * "Recent" would be the product republishing the thing it suspended. The enum is the guard: a
     * widened `IN` list in SQL becomes a parse failure here rather than a leak.
     */
    status: z.enum(["published", "live", "ended"]),
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
    /** Free text as the organizer wrote it; never geocoded (migration 0006). */
    location: z.string().nullable(),
    /** The venue's postal address — a public place being advertised, not a donor's position. */
    address: z.string().nullable(),
    /** Versioned public URL, or null. The same contract as the ticket page's hero image. */
    heroImageUrl: z.string().nullable(),
});
/**
 * `GET /v1/public/organizations/:organizationId/events` — what this organization has on.
 *
 * Two buckets rather than one list with a flag, because the page renders them under two headings
 * and the split is decided by the clock as well as by `status`: an event whose stated `ends_at`
 * has passed is over even though `status` only moves when a human presses "End event". The
 * buckets are exact complements, so no event can appear in both.
 */
export const organizationEventsResponseSchema = z.object({
    organizationId: z.string().uuid(),
    /** Running now, or announced for later. Soonest first; undated events sort last. */
    upcoming: z.array(publicOrganizationEventSchema),
    /** Finished. Most recently finished first. */
    recent: z.array(publicOrganizationEventSchema),
    /** GH-871a: true only while the lists are withheld from a non-member (both then empty). */
    eventsHidden: z.literal(true).optional(),
});
/**
 * One announced event that has not started, near the donor (GH-800).
 *
 * The owner, build 24: *"Published events that are not live yet should show up when logged in,
 * ordered by date, in geofence on search results."* Never live: an event an organizer started
 * early is on the nearby list's `isHappening` rows instead, and nothing here may be drawn LIVE.
 *
 * The same public facts an organization's own page already lists — name, dates, venue text —
 * plus the organization's name and mark so the row stands on its own. No money, no coordinates,
 * no distance figure: the list is the radius, and a per-row readout would echo where the reader
 * is standing at a precision the coarse input never had (the same rule as `distanceLabel`).
 */
export const upcomingEventSchema = z.object({
    eventId: z.string().uuid(),
    eventName: z.string(),
    organizationId: z.string().uuid(),
    organizationDisplayName: z.string(),
    logoUrl: z.string().nullable(),
    locality: z.string().nullable(),
    /** Always stated: an undated event is never upcoming, it is opened by hand or not at all. */
    startsAt: z.string(),
    endsAt: z.string().nullable(),
    location: z.string().nullable(),
    address: z.string().nullable(),
});
/**
 * `GET /v1/public/events/upcoming?lat&lon&radiusKm` — upcoming events inside the radius, soonest
 * first. Public and identifier-free like the rest of the directory: the coarse position is never
 * joined to a session, so the "signed in" condition is the app's presentation, not a server gate.
 */
export const upcomingEventsResponseSchema = z.object({
    events: z.array(upcomingEventSchema),
    /** GH-871a: true only while the list is withheld from a non-member (it is then empty). */
    eventsHidden: z.literal(true).optional(),
});
export const organizationDetailSchema = z.object({
    organization: directoryEntrySchema.extend({
        /**
         * The IRS employer identification number, formatted `NN-NNNNNNN`.
         *
         * A bare identifier and nothing more. Deliberately not accompanied by any statement about
         * tax deductibility or exempt status — an EIN is not evidence of either, and asserting it
         * would be a legal claim this product is not in a position to make (DEC-001, DEC-011).
         */
        ein: z.string().nullable(),
        description: z.string().nullable(),
        websiteUrl: z.string().nullable(),
        address: z.string().nullable(),
        /** Non-null only while the season is running; see `givingSeasonSchema`. */
        season: givingSeasonSchema.nullable().default(null),
        /**
         * The always-on general fund (migration 0026), which is where a "General fund" press goes.
         *
         * Distinct from `liveEventId`, and that distinction is the whole point. `liveEventId` is what
         * a donor arriving cold should land on, so it prefers tonight's fundraiser and falls back to
         * the general fund; a button labelled "General fund" that minted a checkout for tonight's
         * event would put a fund on a receipt the donor did not choose. Two names, two destinations.
         *
         * Null for an organization that is not approved — 0026 creates the row on approval — so a
         * client hides the button rather than offering one that cannot mint. Defaulted so a client
         * reading an older server sees null and does exactly that instead of failing the page.
         */
        generalFundEventId: z.string().uuid().nullable().default(null),
        /*
         * The organization's Overview (GH-805): what a donor checks before trusting a name. Every
         * field below is public already — published by the organization, by a public registry, or by
         * this same route's directory row — and none of them is staff, donor, bank, payout or claim
         * data. `public-organization-overview.integration.test.ts` asserts the payload carries no key
         * outside this schema. Each is defaulted so a client reading an older server draws nothing.
         */
        /**
         * Whether the organization has claimed its listing — taken responsibility for this page.
         * Distinct from `canAcceptDonations`: a claimed organization whose payments are not yet set
         * up is still claimed. A boolean only; who claimed it and when are not public.
         */
        claimed: z.boolean().default(false),
        /** The public phone number the organization (or its reviewer) entered for this page. */
        publicPhone: z.string().nullable().default(null),
        /**
         * Where the organization itself is, for a map preview and directions — its own position,
         * never the live event's venue (that is `position`, which prefers the venue). Null when the
         * record is not geocoded, which is ordinary.
         */
        addressPosition: z
            .object({ latitude: z.number(), longitude: z.number() })
            .nullable()
            .default(null),
        /**
         * Candid's public Seal of Transparency, only once a reviewer has confirmed it against the
         * Candid profile (`candid_verified_at`, migration 0024). The seal level is Candid's own
         * wording; the profile is a public https page. Says nothing about deductibility.
         */
        candid: z.object({ sealLevel: z.string(), profileUrl: z.string() }).nullable().default(null),
        /**
         * The organization's brand colour, `#rrggbb` lower case, or null (GH-850, shown on the public
         * page since GH-853). It tints the page's 3px identity keyline and nothing else — DESIGN.md:
         * "A tenant's color never becomes the accent. It may tint the 3px identity keyline." A colour
         * is presentation the organization chose to publish, not personal or sensitive data. Defaulted
         * so a client reading an older server draws no keyline.
         */
        brandColor: z
            .string()
            .regex(/^#[0-9a-f]{6}$/)
            .nullable()
            .default(null),
    }),
    /**
     * The people the organization has chosen to name on its own page: name and role text, nothing
     * else (migration 0062).
     *
     * A sibling of `organization` rather than a field inside it, because `organization` is
     * `directoryEntrySchema.extend(...)` and every search and nearby row shares that shape — a
     * `people` key in there would imply a directory result carries one, and it never does. That
     * exclusion is deliberate and stated again in `packages/db/src/repositories/directory.ts`: if
     * a donor could type a person's name into discovery and find them, this product would have
     * become a person-finder for members of faith organizations.
     *
     * Published rows only. Defaulted so a client reading an older server sees an empty list, which
     * renders as nothing at all — see `organizationPersonSchema` for why "nobody listed" is not an
     * empty state worth drawing.
     */
    people: z.array(organizationPersonSchema).default([]),
    /**
     * Non-null when a donor may give to this unclaimed organization and have the gift held for it
     * (GH-798): the page offers Give, mints from `…/held-gift-link`, and shows this disclosure. Null
     * for a claimed organization (it has the ordinary path), for an archived one, and for every
     * organization while `FEATURE_UNCLAIMED_ORG_GIFTS` is off — when the unclaimed page keeps its
     * "Unclaimed — donations disabled" wording.
     */
    heldGifts: heldGiftDisclosureSchema.nullable().default(null),
    event: z
        .object({
        id: z.string().uuid(),
        name: z.string(),
        purpose: z.string().nullable(),
        description: z.string().nullable(),
        goal: moneySchema,
        status: z.string(),
    })
        .nullable(),
});
/**
 * A 501(c)(3) as the IRS lists it, offered to somebody claiming their organization.
 *
 * ## Why the claim flow searches the registry and not our directory
 *
 * Owner-directed 2026-08-23. The directory began as an imported list of organizations (HIFLD), which
 * makes it a fine source of *supplementary* data — coordinates, a display name somebody
 * recognises — and a poor gate on who may sign up: an organization absent from that list could
 * not claim itself at all, and the list is organizations only, while the product admits any 501(c)(3).
 * So the search here is the IRS registry, filtered to subsection 3 at the registry, and our own
 * record is joined onto the result rather than being the thing searched.
 *
 * `organizationId` is therefore *nullable*: most registry results are organizations we hold no
 * row for yet, and claiming one is how the row gets created.
 */
export const claimSearchCandidateSchema = z.object({
    ein: z.string(),
    legalName: z.string(),
    city: z.string().nullable(),
    region: z.string().nullable(),
    subsectionCode: z.number().int().nullable(),
    /** Our directory record for this EIN, when one exists. Null means we hold nothing yet. */
    organizationId: z.string().uuid().nullable(),
    /** Our record's claim status, when we hold one — so a claimant sees "already claimed" early. */
    claimStatus: z.string().nullable(),
    /** The name we show, when we hold a record. Often friendlier than the registry's legal name. */
    displayName: z.string().nullable(),
});
export const claimSearchResponseSchema = z.object({
    results: z.array(claimSearchCandidateSchema),
    /**
     * True when the registry itself could not be reached. Distinct from an empty `results`: "we
     * could not check" and "the IRS lists nothing under that name" need different sentences, and
     * conflating them tells a real nonprofit it does not exist.
     */
    registryUnavailable: z.boolean(),
});
/**
 * Everything the IRS holds for one EIN, plus whatever we hold ourselves.
 *
 * ## Why a claimant sees this
 *
 * The claim form's hard problem is not "is this a real nonprofit" — the registry answers that. It
 * is "does this person actually belong to it", and the platform has no document to check. What it
 * has is the registry's own contact details: a mailing address and a care-of name, plus a phone
 * and website from our own record. Showing them turns an unanswerable question into a checkable
 * one, for the claimant *and* for the reviewer reading the claim afterwards.
 *
 * ## What is deliberately not here
 *
 * **No statement about deductibility, and no tax advice.** `deductibilityCode` is the registry's
 * own code, passed through unlabelled, exactly as `ein` is on `organizationDetailSchema` — an
 * interface that turned it into "donations here are tax-deductible" would be making a legal claim
 * this product does not make (DEC-001, DEC-011).
 *
 * **No phone or domain from the IRS**, because the IRS Business Master File carries neither. The
 * `onFile` values are our own record, kept in a separate object so an interface cannot present
 * them as something the IRS said.
 */
export const organizationIrsRecordSchema = z.object({
    ein: z.string(),
    /**
     * False when the registry has no record under this EIN. Distinguished from an *ineligible*
     * record below, and from `registryUnavailable`: "we could not check", "there is no such
     * record", and "we checked and it does not qualify" must never read the same.
     */
    found: z.boolean(),
    registry: z
        .object({
        legalName: z.string(),
        addressLine1: z.string().nullable(),
        city: z.string().nullable(),
        region: z.string().nullable(),
        postalCode: z.string().nullable(),
        careOfName: z.string().nullable(),
        subsectionCode: z.number().int().nullable(),
        /** The month the determination letter was issued, as the registry states it. */
        rulingDate: z.string().nullable(),
        deductibilityCode: z.number().int().nullable(),
        exemptStatusCode: z.number().int().nullable(),
        nteeCode: z.string().nullable(),
    })
        .nullable(),
    /** The eligibility rule of DEC-005, evaluated server-side so no client re-implements it. */
    eligibility: z
        .object({
        is501c3: z.boolean(),
        hasDeterminationLetter: z.boolean(),
        isCurrentlyExempt: z.boolean(),
    })
        .nullable(),
    /** Our own directory record for this EIN, when one exists. */
    held: z
        .object({
        organizationId: z.string().uuid(),
        displayName: z.string(),
        claimStatus: z.string(),
        /** NOT from the IRS — see the note above. */
        publicPhone: z.string().nullable(),
        websiteUrl: z.string().nullable(),
    })
        .nullable(),
    registryUnavailable: z.boolean(),
});
/* -------------------------------------------------------------------- errors */
export const errorResponseSchema = z.object({
    error: z.object({
        code: z.string(),
        message: z.string(),
        retryability: z.enum(["retryable", "not_retryable", "retry_after_change"]),
        correlationId: z.string(),
    }),
});
/* ------------------------------------------------------------------- helpers */
export const formatMoney = (value, locale = "en-US") => {
    const minor = BigInt(value.amountMinor);
    const whole = minor / 100n;
    const cents = (minor % 100n).toString().padStart(2, "0");
    return new Intl.NumberFormat(locale, { style: "currency", currency: value.currency }).format(Number(`${whole}.${cents}`));
};
/**
 * The same amount with a trailing ".00" dropped.
 *
 * For the four suggested amounts only. "$25" is read at a glance where "$25.00" is read as a
 * number, and four of them side by side is the difference between choosing and scanning. Anywhere
 * an amount is a *fact* rather than an offer — a total, a receipt line, a pledge — use
 * `formatMoney`, where the cents are part of the record.
 */
export const formatMoneyCompact = (value, locale = "en-US") => {
    const formatted = formatMoney(value, locale);
    return BigInt(value.amountMinor) % 100n === 0n ? formatted.replace(/\.00$/, "") : formatted;
};
/**
 * Parse donor input into minor units without ever constructing a float.
 *
 * Thousands separators are stripped first: people typing a four-figure gift write "1,000", and
 * rejecting that reads to the donor as "your amount is invalid" on the one screen where a moment
 * of doubt costs the gift.
 */
export function parseAmountToMinor(input) {
    const withoutSeparators = input.replace(/,/g, "");
    const match = /^\s*\$?\s*(\d+)(?:\.(\d{1,2}))?\s*$/.exec(withoutSeparators);
    if (!match)
        return null;
    const [, whole = "0", fraction = ""] = match;
    return (BigInt(whole) * 100n + BigInt(fraction.padEnd(2, "0") || "0")).toString(10);
}
/**
 * How many suggested amounts the donor page will show.
 *
 * A hard cap rather than a styling choice. Past four, a donor stops choosing and starts scanning,
 * and the tap targets shrink below the 44px minimum on a small phone (SPEC.md §17.2). An event
 * configured with more than four gets the first four.
 */
export const MAX_SUGGESTED_AMOUNTS = 4;
/* ------------------------------------------------------------------ receipt */
export const consentPurposeSchema = z.enum([
    "event_updates",
    "organization_updates",
    "future_giving",
    "pledge_reminders",
    "recurring_notices",
    "product_updates",
]);
/**
 * A postal address, shared by the donor profile and the receipt snapshot that freezes one.
 *
 * Declared here rather than beside the donor profile below because `receiptDonorSchema` uses it
 * at module-evaluation time, and a `const` referenced before its initializer throws on import
 * rather than failing a typecheck.
 */
export const postalAddressSchema = z.object({
    line1: z.string().min(1).max(200),
    line2: z.string().max(200).nullable(),
    city: z.string().min(1).max(120),
    region: z.string().min(1).max(60),
    postalCode: z.string().min(3).max(20),
});
/**
 * The donee's own identity, as printed on a receipt.
 *
 * Every field except the name is nullable, because an operator may not have supplied them yet
 * and a receipt with a name alone is honest where one with an invented address is not.
 */
export const platformIdentitySchema = z.object({
    legalName: z.string().min(1).max(200),
    ein: z.string().max(20).nullable(),
    addressLine1: z.string().max(200).nullable(),
    addressLine2: z.string().max(200).nullable(),
    city: z.string().max(120).nullable(),
    region: z.string().max(60).nullable(),
    postalCode: z.string().max(20).nullable(),
    phone: z.string().max(32).nullable(),
});
/** What the console reads: the operator's identity, plus the configured fallback behind it. */
export const platformIdentityResponseSchema = z.object({
    identity: platformIdentitySchema.nullable(),
    /** `ORGANIZATION_LEGAL_NAME`, shown so an empty form still says whose receipts these are. */
    configuredLegalName: z.string().nullable(),
});
/** The donor, frozen at issuance. Every field optional: "if available" is the requirement. */
export const receiptDonorSchema = z.object({
    displayName: z.string().nullable(),
    email: z.string().nullable(),
    phone: z.string().nullable(),
    postalAddress: postalAddressSchema.nullable(),
});
export const receiptSchema = z.object({
    receiptId: z.string().uuid(),
    receiptNumber: z.string(),
    /** Snapshotted at issuance, so a later rename cannot rewrite a receipt a donor holds. */
    organizationLegalName: z.string(),
    organizationDisplayName: z.string(),
    eventName: z.string(),
    designationName: z.string().nullable(),
    donation: moneySchema,
    costSupport: moneySchema,
    /** The server's name for `costSupport` — the same label the quote and the email use (GH-839). */
    costSupportLabel: z.string().optional(),
    total: moneySchema,
    issuedAt: z.string(),
    supportContact: z.string().nullable(),
    /**
     * Who the donee is, in the words that go on the document.
     *
     * Not the organization — under **IRS Notice 2012-52** a gift to a wholly-owned disregarded SMLLC is
     * a gift to a branch of its charity parent, so the charity is the donee for §170(f) and
     * §6115 (DEC-020). Operator-editable, because an address changes for ordinary reasons.
     */
    platform: platformIdentitySchema.nullable(),
    /**
     * The donor's own details **as they were when this receipt was issued**.
     *
     * Fixed at issuance and never re-read from the profile. A contemporaneous written
     * acknowledgment is contemporaneous: if the donor moves house next year, this receipt must
     * still show the address that substantiates *this* year's deduction. Enforced by an
     * append-only table rather than by convention (migration 0030).
     *
     * Null where the donor gave nothing — an anonymous guest gift still has a receipt, and it
     * simply carries no donor block, which is the honest rendering.
     */
    donor: receiptDonorSchema.nullable(),
    /**
     * The §170(f)(8) acknowledgment, composed server-side (DEC-024, decided 2026-08-23).
     *
     * This is what the comment that used to sit here said was missing. Every line states either a
     * fact we hold — what was paid, when, by whom it was received, whether goods or services were
     * provided — or the rule as the statute states it. None of them tells a donor what to put on a
     * return, what a deduction is worth, or whether to itemise: DEC-011 forbids tax advice and a
     * receipt is exactly where an engineering default would become a legal statement by accident.
     *
     * An array rather than a paragraph, because the lines are separately required by separately
     * numbered subparagraphs and a client that wants to render one of them prominently should not
     * have to split prose to do it.
     */
    acknowledgment: z.array(z.string()),
    /**
     * Present on a gift held for an organization that had not claimed its listing (GH-798, DEC-046
     * answer 4): the receipt names the configured recipient and notes the organization the gift was
     * targeted for. Frozen at issuance like every other receipt field. Null on an ordinary gift.
     */
    heldGift: z
        .object({
        recipientName: z.string(),
        targetedOrganizationName: z.string(),
        notation: z.string(),
    })
        .nullable()
        .default(null),
    /**
     * Present only once a refund of this gift has posted (GH-840). Receipts are append-only, so a
     * refund is a notation beside the receipt rather than an edit of it: when, how much went back,
     * and — on a gift refunded in full — the platform fee that was kept. `notation` is the sentence
     * to show. Absent (not null) on every other receipt, so nothing changes for a client that has
     * never seen a refund.
     */
    refund: z
        .object({
        refundedAt: z.string(),
        returned: moneySchema,
        platformFeeKept: moneySchema.nullable(),
        notation: z.string(),
    })
        .optional(),
});
/** Poll target after paying. The donor holds a short-lived, one-purpose access token. */
export const donationStatusSchema = z.object({
    donationId: z.string().uuid(),
    state: z.enum([
        "draft",
        "processing",
        "completed",
        "failed",
        "returned",
        "refunded",
        "partially_refunded",
        "reversed",
    ]),
    /** Donor-facing wording. "Processing" must never look final (docs/ux-flows.md). */
    displayStatus: z.enum(["processing", "received", "failed", "refunded"]),
    total: moneySchema,
    organizationDisplayName: z.string(),
    eventName: z.string(),
    receipt: receiptSchema.nullable(),
    /**
     * Why a failed card payment failed (GH-945), from the latest attempt's stored Stripe code; null
     * when the gift did not fail, was not a card, or Stripe gave no code. Words come from
     * `CARD_FAILURE_DONOR_COPY`. Optional on the wire for clients built before the field.
     */
    failureReason: cardFailureReasonSchema.nullable().optional(),
    /**
     * DEC-056: the card is authorized and the organization captures at the end of its day. The gift
     * still reads `processing`; this only lets the screen say the charge completes later today.
     * Optional on the wire for clients built before the field. A gift staff cancelled before
     * capture reads `failed` (it never became money) — the wire state set is unchanged.
     */
    heldForEndOfDay: z.boolean().optional(),
});
export const enrichRequestSchema = z
    .object({
    displayName: z.string().min(1).max(80).optional(),
    email: z.string().min(3).max(254).optional(),
    phone: z.string().min(7).max(20).optional(),
    /** Explicit list. An omitted purpose is not granted — consent is never inferred. */
    consents: z.array(consentPurposeSchema).max(6).optional(),
})
    .refine((value) => Boolean(value.displayName || value.email || value.phone || (value.consents ?? []).length), { message: "Nothing to save" });
/**
 * `POST /v1/donations/:donationId/receipt-email` (GH-947): a guest's receipt address for a gift
 * held for the end of the day, before its receipt exists. Email only — there is no text-message
 * receipt — and an optional name; no consents (those are asked once the gift is received).
 */
export const heldReceiptEmailRequestSchema = z
    .object({
    email: z.string().min(3).max(254),
    displayName: z.string().min(1).max(80).optional(),
})
    .strict();
export const heldReceiptEmailResponseSchema = z.object({ saved: z.literal(true) });
export const enrichResponseSchema = z.object({
    saved: z.boolean(),
    consentsRecorded: z.number().int(),
});
export const phoneVerificationStartSchema = z.object({ phone: z.string().min(7).max(20) });
export const phoneVerificationStartResponseSchema = z.object({
    verificationId: z.string().uuid(),
    last4: z.string(),
    /**
     * Present only when messaging is suppressed and the environment is not production. In any
     * environment that can actually send, returning the code would defeat verification entirely.
     */
    developmentCode: z.string().nullable(),
});
export const phoneVerificationConfirmSchema = z.object({
    verificationId: z.string().uuid(),
    code: z.string().length(6),
});
export const phoneVerificationConfirmResponseSchema = z.object({ verified: z.boolean() });
/**
 * The email half of the same door, for a guest.
 *
 * Added 2026-09-15 from TestFlight feedback on the pledge screen ("some people want email, it can
 * be verified that way"). It matters more than parity: on the live stack `MESSAGE_SENDER` is
 * `suppressed` and SES is configured, so an email code is the one verification a guest there can
 * actually complete. There is deliberately no `last4` — the client typed the address and shows it
 * back itself; the server has nothing to mask that the donor does not already have.
 */
export const emailVerificationStartSchema = z.object({ email: z.string().min(3).max(254) });
export const emailVerificationStartResponseSchema = z.object({
    verificationId: z.string().uuid(),
    /** Same rule as the phone form: only where nothing can send and this is not production. */
    developmentCode: z.string().nullable(),
});
/* ------------------------------------------------------------------ pledge */
/**
 * A promise to give, taken at checkout.
 *
 * The shape carries the verification challenge rather than a "verified: true" flag, because the
 * server consumes the challenge itself. A client asserting that it verified a number is not
 * evidence of anything.
 */
export const pledgeRequestSchema = z
    .object({
    quoteToken: z.string().min(1),
    /**
     * The donation component alone — a pledge carries no processing cost support, because nothing
     * is being processed. Checked against the signed quote server-side.
     */
    displayedAmount: moneySchema,
    /**
     * Where to remind them. At most one of the two — see the refinements below. Neither (GH-939)
     * is a signed-in donor pledging on their session's own verified contact, resolved by the
     * server; a guest body with neither is refused there.
     *
     * `phone` was the only option until 2026-09-15; `email` was added from TestFlight feedback
     * on the pledge screen ("some people want email, it can be verified that way"). Both are
     * proven the same way, by a challenge on that channel consumed in the pledge transaction or
     * by a verified contact already on the signed-in donor's own profile, and the server decides
     * which. The channel of the challenge must match the channel of the address, so a phone
     * code cannot stand behind an email address or the reverse.
     */
    phone: z.string().min(7).max(20).optional(),
    email: z.string().min(3).max(254).optional(),
    /**
     * The verification challenge, when there is one to consume — SMS or email, matching the
     * address above.
     *
     * Optional since 2026-09-01. A pledge needs a **verified phone** (SPEC.md §13.3), not a fresh
     * code — and SPEC.md §6 asks the donor to enter one only "if no usable phone is returned". A
     * signed-in donor already proved this number by SMS to get their session, and the proof is
     * still on file in `verified_contacts`; asking again is asking them to re-prove something the
     * server can look up. Where the session cannot supply it — a guest, or a number the profile
     * does not hold — the challenge is still the only way through, which is why both fields
     * remain and why the server, not the client, decides which path applies.
     */
    verificationId: z.string().uuid().optional(),
    code: z.string().length(6).optional(),
    displayName: z.string().min(1).max(80).optional(),
})
    // Both or neither. A `verificationId` with no `code` is a client bug, and letting it through
    // would land on the session path — silently pledging under a proof the caller thought it was
    // supplying and did not.
    .refine((body) => (body.verificationId === undefined) === (body.code === undefined), {
    message: "verificationId and code travel together",
    path: ["code"],
})
    // At most one address, on one channel. Both is a request the server cannot prove, because a
    // single challenge only ever proves one of them.
    .refine((body) => body.phone === undefined || body.email === undefined, {
    message: "at most one of phone or email",
    path: ["phone"],
})
    // Neither (GH-939): a signed-in donor's one-click pledge. The server takes the address from the
    // session profile's own verified contacts (a verified phone first, else a verified email) and
    // refuses when there is no session or nothing verified — the client never names it, so it
    // cannot name a wrong one. A challenge always proves a named address, so neither + a challenge
    // is refused here.
    .refine((body) => body.phone !== undefined || body.email !== undefined || body.verificationId === undefined, {
    message: "a verification challenge needs the phone or email it was sent to",
    path: ["phone"],
});
export const pledgeResponseSchema = z.object({
    pledgeId: z.string().uuid(),
    amount: moneySchema,
    organizationDisplayName: z.string(),
    eventName: z.string(),
    /**
     * Server-composed confirmation sentence, including "nothing has been charged". Sent as text so
     * the guarantee cannot be lost to a copy edit in the browser bundle.
     */
    summary: z.string(),
});
/* ------------------------------------------------- organization sign-up (/org) */
/**
 * What an organization submits to claim its directory record.
 *
 * Contact details and assertions only. Identity documents, bank details, and KYB/KYC belong to
 * the processor (SPEC.md §6.3.3) and must never be collected here — re-collecting them would put
 * this product in possession of exactly the data it is designed not to hold.
 */
export const claimSubmissionSchema = z
    .object({
    /**
     * The directory record being claimed, when we already hold one.
     *
     * Optional since 2026-08-23: the claim form searches the IRS registry rather than our
     * directory (owner-directed), so most organizations claiming themselves are ones we hold no
     * row for yet. Exactly one of this and `irsEin` must be present — see the refinement below.
     */
    organizationId: z.string().uuid().optional(),
    /**
     * The EIN of the registry record being claimed, when we hold no directory row for it.
     *
     * The server re-reads the registry from this EIN and creates the organization itself. It
     * deliberately does **not** accept a name or address alongside: everything about the
     * organization comes from the IRS, so a claimant cannot conjure a directory record with
     * details of their choosing.
     */
    irsEin: z.string().min(9).max(10).optional(),
    claimantName: z.string().min(1).max(120),
    claimantEmail: z.string().min(3).max(254),
    claimantPhone: z.string().min(7).max(20).optional(),
    /** "Director", "Board treasurer" — how they say they are authorised, in their words. */
    claimantRole: z.string().min(1).max(120),
    assertedLegalName: z.string().min(1).max(200),
    /** Accepts `12-3456789` or `123456789`; normalised server-side. */
    assertedEin: z.string().min(9).max(10).optional(),
    assertedWebsite: z.string().max(300).optional(),
    notes: z.string().max(2000).optional(),
    /**
     * The claimant states that they are an officer or director of the organization, with their
     * name and title (GH-813, DEC-005 owner direction 2026-10-01). Required: a claim without it is
     * refused. Stored on the claim; it is a statement, and it is what a reviewer — or the
     * automatic match — weighs alongside what the IRS filing says.
     */
    officerAttestation: z.object({
        isOfficerOrDirector: z.literal(true),
        name: z.string().trim().min(1).max(120),
        title: z.string().trim().min(1).max(120),
    }),
})
    /**
     * Exactly one target, never both and never neither.
     *
     * Both would be ambiguous in the one case that matters — an EIN whose registry record and whose
     * directory row disagree — and resolving it silently is how a claim gets recorded against the
     * wrong organization. Rejecting is cheap; the form only ever sends one.
     */
    .refine((value) => (value.organizationId === undefined) !== (value.irsEin === undefined), "Provide either an organizationId or an irsEin, not both.");
export const claimSubmissionResponseSchema = z.object({
    claimId: z.string().uuid(),
    state: z.string(),
    /**
     * Server-composed, for the same reason the pledge confirmation is: the sentence has to say that
     * submitting is not approval, and that guarantee must not be lost to a copy edit in a bundle.
     */
    summary: z.string(),
    /**
     * True when the claim approved itself on a verified IRS domain or phone match (GH-813). Payments
     * are still off: `summary` says so, and what turns them on is the platform's payments step.
     */
    approvedAutomatically: z.boolean().default(false),
});
/* ------------------------------------------------------------- staff sign-in */
export const staffSignInStartSchema = z.object({
    email: z.string().min(3).max(254),
    /**
     * The operator-held access code, required on shared deployments where the sign-in code comes
     * back in the response body. Optional in the schema because local development has no secret;
     * the server decides whether one is demanded.
     */
    accessSecret: z.string().max(200).optional(),
});
export const staffSignInStartResponseSchema = z.object({
    challengeId: z.string().uuid(),
    /**
     * Present only where nothing can deliver a code and the environment is not production — the
     * same rule the donor's phone verification follows. In production with DEC-009 unresolved this
     * whole route is refused, so there is no environment in which a code is both returned here and
     * capable of authorising anything real.
     */
    developmentCode: z.string().nullable(),
});
export const staffSignInConfirmSchema = z.object({
    challengeId: z.string().uuid(),
    code: z.string().length(6),
});
/** The browser hands over the ID token Google issued; the server trusts nothing else about it. */
export const staffGoogleSignInSchema = z.object({
    credential: z.string().min(20).max(4096),
});
export const staffSessionSchema = z.object({
    token: z.string(),
    expiresAt: z.string(),
    /**
     * What actually proved this session. The console uses it to decide whether the
     * development-sign-in banner must be shown — a session proved by a real identity provider
     * does not get labelled as a demo.
     */
    authMethod: z.enum(["development_code", "oidc", "passkey", "donor_account"]),
    user: z.object({
        email: z.string(),
        displayName: z.string(),
        isPlatformStaff: z.boolean(),
    }),
    organizations: z.array(z.object({
        id: z.string().uuid(),
        displayName: z.string(),
        claimStatus: z.string(),
        canAcceptDonations: z.boolean(),
        roles: z.array(z.string()),
        /**
         * What those roles actually reach, resolved by the server.
         *
         * The console needs this to decide which links are worth offering — an event manager sent to
         * the accounting screen gets a red banner naming no role and offering no way back, which is
         * a worse experience than not being invited. It could not compute the answer itself: the
         * role-to-permission table lives in `@givehere/authz`, which ADR 0002 keeps out of every
         * browser bundle, and transcribing it into the console would be a second copy that drifts
         * from the one the server enforces.
         *
         * This is a courtesy and never a control. Every request is authorized again server-side, and
         * a client that edited this array would gain a link and then a 403.
         */
        permissions: z.array(z.string()),
    })),
    /**
     * Set when this session acts as an organization member on a platform operator's behalf
     * (GH-620). `user` above is the member being impersonated; this names the operator, so the
     * console can keep a banner up naming both people for as long as the session lasts.
     */
    impersonation: z
        .object({
        organizationId: z.string().uuid(),
        actorDisplayName: z.string(),
        actorEmail: z.string(),
    })
        .nullable(),
});
/**
 * "You are also staff, for these organizations" — the answer behind the Account switch on the
 * phone (GH-795). `GET /v1/donor/staff-access`, on a donor session.
 *
 * **It grants nothing.** It is read with a donor session, which never carries a staff role, and
 * acting on any of these organizations still needs a staff session minted by the staff sign-in
 * routes. What it decides is only whether the app offers the way in.
 *
 * One shape for everyone. A donor who is not staff, whose address matches nobody, whose staff
 * account has no role left, or whose matching address is unverified gets exactly
 * `{ staff: false, email: null, organizations: [], signIn: [] }` — so the answer cannot be used
 * to learn which addresses administer which organizations, beyond the caller's own proven ones.
 */
export const donorStaffAccessResponseSchema = z.object({
    staff: z.boolean(),
    /**
     * The donor's own verified address that matched a staff account — the address to sign in
     * with. Never anyone else's: it is one of the caller's proven mailboxes, told back to them.
     */
    email: z.string().nullable(),
    organizations: z.array(z.object({
        id: z.string().uuid(),
        displayName: z.string(),
    })),
    /**
     * Which staff sign-in doors this deployment opens to the phone, so the app does not offer a
     * button that ends in a 404. `google` only when the server accepts the app's own OAuth client;
     * `code` only where the development sign-in exists. Empty for a donor who is not staff.
     */
    signIn: z.array(z.enum(["google", "code"])),
    /**
     * The signed-in account is itself enough (owner-directed 2026-10-01): `POST
     * /v1/donor/staff-session` mints the staff session from this donor session, with no access code
     * and no second sign-in. True only for a staff account holding an organization role, and only
     * where the deployment delivers email codes rather than showing them — a verified address there
     * proves the mailbox. The doors in `signIn` remain the fallback.
     */
    accountSignIn: z.boolean().default(false),
});
/** Opening an impersonated session requires saying why; the reason is recorded permanently. */
export const impersonateRequestSchema = z.object({
    staffUserId: z.string().uuid(),
    reason: z.string().min(1).max(500),
});
/* ------------------------------------------------------- event pages (GH-350) */
/*
  The public event page an organizer builds: the facts Eventbrite calls "good to know", plus
  FAQs, a lineup, and an agenda. Display-only content — nothing here is money, identity, or
  authorization — validated at the boundary and stored as jsonb.
*/
export const AGE_POLICIES = ["all_ages", "age_restricted", "guardian_required"];
export const AGE_POLICY_LABELS = {
    all_ages: "All ages welcome",
    age_restricted: "There's an age restriction",
    guardian_required: "Parent or guardian needed",
};
export const PARKING_OPTIONS = ["free", "paid", "none"];
export const PARKING_LABELS = {
    free: "Free parking",
    paid: "Paid parking",
    none: "No parking on site",
};
export const eventFaqSchema = z.object({
    question: z.string().min(1).max(200),
    answer: z.string().min(1).max(1000),
});
/**
 * One speaker, host or performer on the event page (GH-350; photo and bio editing GH-862).
 *
 * Field names are the original GH-350 ones and stay so every stored page keeps parsing: `tagline`
 * is the **role or title** ("Keynote", "Host"), `description` the **short bio**. `photoId` names
 * an image uploaded through `POST /v1/events/:eventId/speaker-photos` for this same event; the
 * server refuses an id that is not this event's. A speaker is a person the organizer is
 * advertising by their own choice, so a photo is optional and never required (owner, GH-862).
 */
export const eventLineupEntrySchema = z.object({
    name: z.string().min(1).max(120),
    tagline: z.string().max(160).nullable().default(null),
    description: z.string().max(500).nullable().default(null),
    headliner: z.boolean().default(false),
    photoId: z.string().uuid().nullable().default(null),
});
/** Times are organizer-typed display text ("8:00 PM"), not instants — no timezone math. */
export const eventAgendaItemSchema = z.object({
    title: z.string().min(1).max(160),
    startTime: z.string().max(40).nullable().default(null),
    endTime: z.string().max(40).nullable().default(null),
    description: z.string().max(300).nullable().default(null),
});
export const eventPageContentSchema = z.object({
    /** The long-form "about this event" text (the events.description column). */
    overview: z.string().max(2000).nullable(),
    doorsOpenMinutes: z.number().int().min(0).max(1440).nullable(),
    agePolicy: z.enum(AGE_POLICIES).nullable(),
    parking: z.enum(PARKING_OPTIONS).nullable(),
    faqs: z.array(eventFaqSchema).max(20),
    lineup: z.array(eventLineupEntrySchema).max(20),
    agenda: z.array(eventAgendaItemSchema).max(30),
});
/** Console editor payload: send only what changed. */
export const updateEventPageSchema = eventPageContentSchema.partial();
/**
 * A speaker as every reader receives it: the stored entry plus the public URL of its photo, which
 * the server derives from `photoId` (GH-862). Null when there is no photo. Defaulted so a client
 * reading an older server draws initials.
 */
export const publicLineupEntrySchema = eventLineupEntrySchema.extend({
    photoUrl: z.string().nullable().default(null),
});
/** What the console editor loads: the content plus where the hero image lives, if any. */
export const eventPageAdminResponseSchema = eventPageContentSchema.extend({
    heroImageUrl: z.string().nullable(),
    lineup: z.array(publicLineupEntrySchema).max(20),
});
/**
 * A built-in cover photo the organizer can pick instead of uploading one (GH-843). Choosing one
 * copies its bytes into the event's own hero image, so every donor surface reads it exactly as
 * it reads an upload. No identifiable faces, no text, no logos anywhere in the set.
 *
 * Collections (GH-867): `featured` is the grid shown by default and is denomination-neutral by
 * rule (no religious symbols, buildings or dress of any faith); `scenes` keeps the same rule and
 * sits behind "More"; `places` is the one opt-in exception — sacred architecture from several
 * traditions in balance, shown only after "More", never featured and never a default.
 */
export const EVENT_COVER_COLLECTIONS = ["featured", "scenes", "places"];
export const eventCoverCollectionSchema = z.enum(EVENT_COVER_COLLECTIONS);
export const EVENT_COVER_PRESET_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const eventCoverPresetIdSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(EVENT_COVER_PRESET_ID_PATTERN);
export const eventCoverPresetSchema = z.object({
    id: eventCoverPresetIdSchema,
    /** Short name for the picker ("String lights over a dinner"). */
    label: z.string().min(1).max(80),
    /** Neutral description of what the photo shows, for screen readers. */
    alt: z.string().min(1).max(200),
    /** Small preview, served by the API (`/v1/public/event-cover-presets/<id>/thumbnail`). */
    thumbnailUrl: z.string(),
    collection: eventCoverCollectionSchema,
    credit: z.object({
        photographer: z.string().min(1).max(120),
        /** The photo's page; null for a photo with no public page (one the owner supplied). */
        sourceUrl: z.string().url().nullable(),
        /** `unsplash`: the Unsplash License. `supplied`: provided by the GiveHere owner (GH-867). */
        license: z.enum(["unsplash", "supplied"]),
    }),
});
export const eventCoverPresetsResponseSchema = z.object({
    presets: z.array(eventCoverPresetSchema),
});
/** JSON body of `POST /v1/events/:eventId/hero` when choosing a preset instead of uploading. */
export const chooseEventCoverPresetSchema = z
    .object({ presetId: eventCoverPresetIdSchema })
    .strict();
/**
 * An organization's saved cover photo (GH-867): a cover it uploaded once and kept for future
 * events. Private to that organization's staff — `imageUrl` is an authenticated console route,
 * never a public one; the bytes reach a donor only after they are copied into an event's hero.
 */
export const savedCoverPhotoSchema = z.object({
    id: z.string().uuid(),
    contentType: z.enum(["image/jpeg", "image/png", "image/webp"]),
    byteSize: z.number().int().positive(),
    createdAt: z.string(),
    /** `GET` with the staff session; served `private`, never to an anonymous request. */
    imageUrl: z.string(),
});
/** Most saved photos one organization keeps (GH-867); the next save is refused, not evicted. */
export const SAVED_COVER_PHOTO_LIMIT = 50;
export const savedCoverPhotosResponseSchema = z.object({
    photos: z.array(savedCoverPhotoSchema).max(SAVED_COVER_PHOTO_LIMIT),
    limit: z.number().int().positive(),
});
/** Save the cover an event already has into its organization's photos. */
export const saveCoverPhotoRequestSchema = z.object({ fromEventId: z.string().uuid() }).strict();
/** `created: false` when the same photo was already saved: saving twice keeps one copy. */
export const saveCoverPhotoResponseSchema = z.object({
    photo: savedCoverPhotoSchema,
    created: z.boolean(),
});
/** JSON body of `POST /v1/events/:eventId/hero` when choosing one of the organization's photos. */
export const chooseSavedCoverPhotoSchema = z.object({ savedPhotoId: z.string().uuid() }).strict();
/** What the hero routes answer: the new versioned public URL, or null after a removal. */
export const eventHeroResponseSchema = z.object({ heroImageUrl: z.string().nullable() });
/* ------------------------------------------------------- organization console */
export const createEventSchema = z.object({
    name: z.string().min(1).max(160),
    /** Optional since GH-350: when omitted the server uses the event name, so creating an event
     * asks for as little as possible. */
    purpose: z.string().min(1).max(300).optional(),
    description: z.string().max(2000).optional(),
    /**
     * Free text — a hall, a street address, whatever the organizer would print on a flyer. Never
     * geocoded and never mailed to; the coarse organization position stays the only coordinate the
     * system holds (SPEC.md §6.2).
     */
    location: z.string().min(1).max(200).optional(),
    /**
     * The venue's postal address — where the event happens, not necessarily the organization's
     * address. Unlike `location` this IS geocoded (best-effort, server-side) so the find-events
     * screen can rank by venue proximity. A venue is a public place being advertised, so the
     * privacy posture of SPEC.md §6.2 (donor positions) does not apply to it.
     */
    address: z.string().min(1).max(300).optional(),
    startsAt: z.string().datetime({ offset: true }).optional(),
    endsAt: z.string().datetime({ offset: true }).optional(),
    goalAmountMinor: minorUnitsSchema,
    /** Server clamps to MAX_SUGGESTED_AMOUNTS; the donor page shows four. */
    suggestedAmountsMinor: z.array(minorUnitsSchema).max(8).default([]),
    defaultAmountMinor: minorUnitsSchema.optional(),
    /* Event-page facts collectable at creation (GH-350). Everything else on the page — FAQs,
       lineup, agenda, the hero image — is edited afterwards on the Event page screen. */
    doorsOpenMinutes: z.number().int().min(0).max(1440).optional(),
    agePolicy: z.enum(AGE_POLICIES).optional(),
    parking: z.enum(PARKING_OPTIONS).optional(),
});
export const eventSummarySchema = z.object({
    id: z.string().uuid(),
    name: z.string(),
    /**
     * The effective lifecycle status (GH-800) — what the event is now, from the stored decision,
     * the clock and any organizer override — so the console and the phone group and badge events
     * by one rule. `published` is upcoming; `live` is on; `ended` is past its end or ended by hand.
     */
    status: z.string(),
    version: z.number().int(),
    location: z.string().nullable(),
    /** The venue's postal address as staff entered it, or null when none was set. */
    address: z.string().nullable(),
    /**
     * Whether the address resolved to coordinates. The console uses this to say "shown by
     * distance" versus "couldn't locate this address" — an un-geocoded address still displays to
     * donors, it just cannot rank by venue distance. Always false when `address` is null.
     */
    addressGeocoded: z.boolean(),
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
    goal: moneySchema,
    suggestedAmounts: z.array(moneySchema),
});
/**
 * Organization console: set or clear an event's venue address after creation. Null clears it.
 * The server geocodes; the client never sends coordinates for an event.
 */
export const setEventAddressSchema = z.object({
    address: z.string().min(1).max(300).nullable(),
    /** The organizer confirmed the warning that the move reaches ticket holders (GH-862). */
    confirmAffected: z.boolean().optional(),
});
/* ------------------------------------------------------------ admin console */
export const createOrganizationSchema = z.object({
    legalName: z.string().min(1).max(200),
    displayName: z.string().min(1).max(160),
    city: z.string().max(120).optional(),
    region: z.string().max(60).optional(),
    addressLine1: z.string().max(200).optional(),
    postalCode: z.string().max(20).optional(),
    phone: z.string().max(30).optional(),
    description: z.string().max(4000).optional(),
    websiteUrl: z.string().url().startsWith("https://").optional(),
    ein: z.string().min(9).max(10).optional(),
    /**
     * Optional, and coarsened server-side before storage — the directory holds nothing finer than
     * ~1 km (SPEC.md §6.2). This is the organization's own public position, not anyone's location.
     */
    latitude: z.number().min(-90).max(90).optional(),
    longitude: z.number().min(-180).max(180).optional(),
    /**
     * Activate the record in the same breath: a platform reviewer creating an organization they
     * have already vetted can approve it immediately. Still a recorded human decision — the
     * server routes it through the claims machinery, writing the decision rows — never a silent
     * status write. `paymentsEnabled` stays untouched either way (DEC-002).
     */
    activate: z.boolean().optional(),
    /**
     * Absent on purpose: `claimStatus` and `paymentsEnabled`. An organization created here is
     * `unclaimed` unless the reviewer activates it, and payments never turn on here
     * (SPEC.md §6.1, DEC-005).
     */
});
/**
 * The EIN-first add-organization flow: what the IRS registry says about a nine-digit EIN,
 * plus whether the directory already holds it. Registry facts only — never a statement
 * about deductibility or exempt status (DEC-001, DEC-011).
 */
export const einLookupResponseSchema = z.object({
    ein: z.string(),
    /** True when the IRS registry returned a record. */
    found: z.boolean(),
    legalName: z.string().nullable(),
    addressLine1: z.string().nullable(),
    city: z.string().nullable(),
    region: z.string().nullable(),
    postalCode: z.string().nullable(),
    /** Set when this EIN is already in the directory — create nothing, point at this instead. */
    existingOrganization: z
        .object({
        id: z.string().uuid(),
        displayName: z.string(),
        claimStatus: z.string(),
        /** What the directory currently holds, so the reviewer sees IRS data next to it. */
        addressLine1: z.string().nullable().default(null),
        city: z.string().nullable().default(null),
        region: z.string().nullable().default(null),
        postalCode: z.string().nullable().default(null),
        phone: z.string().nullable().default(null),
    })
        .nullable(),
});
/**
 * The name side of the same add-organization door: a reviewer without an EIN in hand searches
 * the IRS registry by name and picks the right record, which then drives the same EIN lookup
 * (SPEC.md §6.1). Candidates only — no address, since ProPublica's search endpoint does not
 * return one; picking a result re-fetches the full record by EIN.
 */
export const irsSearchResponseSchema = z.object({
    query: z.string(),
    results: z.array(z.object({
        ein: z.string(),
        legalName: z.string(),
        city: z.string().nullable(),
        region: z.string().nullable(),
    })),
});
/** Platform console: promote an unclaimed directory record to an approved organization. */
export const activateOrganizationSchema = z.object({
    /** Why the reviewer is vouching for this record; lands in the decision log. */
    rationale: z.string().min(1).max(2000).optional(),
    /**
     * Public contact details applied in the same transaction as the approval — the EIN-first
     * flow prefills these from the IRS registry (address; the registry has no phone) and the
     * reviewer confirms or corrects them before activating. Only provided fields are written.
     */
    details: z
        .object({
        addressLine1: z.string().max(200).optional(),
        city: z.string().max(120).optional(),
        region: z.string().max(60).optional(),
        postalCode: z.string().max(20).optional(),
        phone: z.string().max(30).optional(),
    })
        .optional(),
});
export const activateOrganizationResponseSchema = z.object({
    id: z.string().uuid(),
    claimStatus: z.string(),
});
/** Platform console: switch an approved organization's payments on or off (audited). */
export const setOrganizationPaymentsSchema = z.object({
    enabled: z.boolean(),
});
/** Run-the-room control: whether the big screen's headline total folds active pledges in. */
export const setGoalPledgesSchema = z.object({
    includePledges: z.boolean(),
});
/**
 * Platform console: place a directory record on the map (coarsened server-side to ~1 km,
 * SPEC.md §6.2). Without coordinates a record cannot rank by distance and sorts last.
 */
export const setOrganizationLocationSchema = z.object({
    latitude: z.number().min(-90).max(90),
    longitude: z.number().min(-180).max(180),
    city: z.string().min(1).max(120).optional(),
    region: z.string().min(1).max(120).optional(),
});
/**
 * The platform view of one directory record. `claimStatus` is the raw state — the platform
 * console is exactly the audience the donor-facing verification label exists to summarize *for*,
 * so it gets the real value.
 */
/**
 * The organization's public face as its console edits it. Identity fields (names, EIN, claim
 * status) are read-only here — they move only through claim review and platform administration.
 */
/**
 * The season as its own console reads it back: the words, the window, and whether today is in it.
 *
 * `active` is the server's answer and not the client's arithmetic. The console renders a
 * "running now" state from it, and a browser whose clock is wrong — or whose timezone is not the
 * organization's — cannot disagree with the database about what day it is.
 */
export const organizationSeasonSchema = z.object({
    label: z.string().min(1).max(60),
    /** `YYYY-MM-DD`, inclusive. */
    startsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    /** `YYYY-MM-DD`, inclusive, and never before `startsOn`. */
    endsOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    active: z.boolean(),
});
/**
 * Declare or clear the season. `season: null` clears it — all three columns together, because a
 * window with no label or a label with no window is not a state any reader could render.
 *
 * The order check lives here as well as in the database: a form that transposed the dates should
 * be told so by the field it typed them into, not by a constraint violation.
 */
export const setOrganizationSeasonSchema = z.object({
    season: organizationSeasonSchema
        .omit({ active: true })
        .refine((value) => value.endsOn >= value.startsOn, {
        message: "The season cannot end before it starts.",
        path: ["endsOn"],
    })
        .nullable(),
});
export const organizationProfileSchema = z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    legalName: z.string(),
    description: z.string().nullable(),
    websiteUrl: z.string().nullable(),
    addressLine1: z.string().nullable(),
    city: z.string().nullable(),
    region: z.string().nullable(),
    postalCode: z.string().nullable(),
    phone: z.string().nullable().default(null),
    /** Nine digits, formatted client-side. Editable (audited); grant addressing follows it. */
    ein: z.string().nullable().default(null),
    /** Https URL, or an API-relative `/v1/public/...` path for an uploaded logo. */
    logoUrl: z.string().nullable(),
    /**
     * The declared season with its dates, and whether today is inside it. Unlike the donor-facing
     * projection this is the whole record: the console is where it is set, so it has to be able to
     * show a window that has not opened yet or has already closed.
     */
    season: organizationSeasonSchema.nullable().default(null),
    /**
     * The organization's brand colour, `#rrggbb` lower case, or null (GH-850). It tints the 3px
     * identity keyline at the top of the give box — DESIGN.md allows a tenant's colour there and
     * nowhere else — and is the console Embed tab's default tint.
     */
    brandColor: z
        .string()
        .regex(/^#[0-9a-f]{6}$/)
        .nullable()
        .default(null),
});
/** Partial on purpose: the editor sends what changed; explicit null clears a field. */
export const updateOrganizationProfileSchema = z.object({
    description: z.string().max(4000).nullable().optional(),
    websiteUrl: z.string().url().startsWith("https://").nullable().optional(),
    addressLine1: z.string().max(200).nullable().optional(),
    city: z.string().max(120).nullable().optional(),
    region: z.string().max(60).nullable().optional(),
    postalCode: z.string().max(20).nullable().optional(),
    phone: z.string().max(30).nullable().optional(),
    /**
     * The EIN, "12-3456789" or bare digits; explicit null clears it. Handled apart from the
     * cosmetic fields server-side: digit-validated, unique-checked, audited with old and new.
     */
    ein: z.string().min(9).max(10).nullable().optional(),
    /**
     * The brand colour as typed — `#1C5B45` or `1c5b45`; explicit null clears it. The server
     * normalizes it with `normalizeBrandColor` and refuses anything that is not six hex digits by
     * name, then audits old and new (`organization.brand_color.updated`).
     */
    brandColor: z.string().max(20).nullable().optional(),
});
/**
 * Capture a DAF grant intent after the DAFpay component reports CHARIOT_SUCCESS. The amount is
 * what the donor authorized in Chariot's flow — the server forwards it for capture and stores
 * only what Chariot answers, never this client claim.
 */
export const dafGrantRequestSchema = z.object({
    contextToken: z.string().min(8),
    workflowSessionId: z.string().uuid(),
    amountMinor: z.string().regex(/^[0-9]{1,12}$/),
});
export const dafGrantResponseSchema = z.object({
    id: z.string().uuid(),
    status: z.enum(["intent", "initiated", "completed", "canceled", "failed"]),
    /** Rides the sponsor's payment; the donor can quote it to the organization. */
    trackingId: z.string().nullable(),
    amount: moneySchema,
    /**
     * Composed server-side, deliberately: a DAF acknowledgment must never carry tax-deduction
     * language (the deduction happened when the donor funded their DAF) and must never reference
     * a pledge. Keeping the sentence out of the client bundle keeps it un-editable.
     */
    acknowledgment: z.string(),
});
/** One DAF grant as the organization console lists them (donation.view_masked). */
export const dafGrantSummarySchema = z.object({
    id: z.string().uuid(),
    createdAt: z.string(),
    amount: moneySchema,
    status: z.enum(["intent", "initiated", "completed", "canceled", "failed"]),
    trackingId: z.string().nullable(),
    fundId: z.string().nullable(),
    paymentChannel: z.string().nullable(),
    donorName: z.string().nullable(),
    donorEmail: z.string().nullable(),
    note: z.string().nullable(),
});
/** Platform console: archive (hide from donors, keep history) or restore a record. */
/**
 * Platform console: hand a claimed organization back to the directory (owner-directed 2026-09-07).
 *
 * Distinct from both neighbours it sits between. Archiving hides a record from donors and keeps
 * whoever holds it; deletion is for junk and refuses anything with history. Releasing keeps the
 * record listed and visible and takes away only the claim — the organization returns to
 * `unclaimed`, payments and DAF go off, the people who had roles lose them, and the next person to
 * come along can file a claim as if this one had never happened.
 *
 * The rationale is required rather than optional. Every other transition in the claim log carries
 * one, and this is the only one that takes an approval away; a release with no stated reason is
 * indistinguishable later from a mistake.
 */
export const releaseOrganizationClaimSchema = z.object({
    rationale: z.string().min(1).max(2000),
});
export const releaseOrganizationClaimResponseSchema = z.object({
    id: z.string().uuid(),
    claimStatus: z.string(),
    /** How many role grants were removed, so the console can say what it actually did. */
    rolesRemoved: z.number().int().nonnegative(),
});
/**
 * Deleting an organization (owner-directed 2026-09-08, DEC-045).
 *
 * Two confirmations, and the second one is a value rather than a second click: the caller sends
 * the organization's display name back. A confirmation only the browser enforces protects nobody
 * holding a session token, so the server compares it too.
 *
 * `reason` is required and lands in the retained legal record beside the data.
 */
export const deleteOrganizationSchema = z.object({
    confirmName: z.string().min(1).max(300),
    reason: z.string().min(1).max(2000),
});
export const deleteOrganizationResponseSchema = z.object({
    id: z.string().uuid(),
    /**
     * `true` when the row itself was removed. `false` means immutable rows — ledger, receipts,
     * audit, consent, claim decisions — still reference it, so what remains is a scrubbed tombstone
     * that no surface shows. The distinction is the honest half of "delete", and the console says it.
     */
    rowDeleted: z.boolean(),
    recordKept: z.boolean(),
});
const deletionCountSchema = z.object({ table: z.string(), count: z.number().int().nonnegative() });
/** What deletion would and would not be able to remove — the first confirmation's numbers. */
export const deletionPreviewResponseSchema = z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    /** Rows a delete will remove outright: access, presentation, scheduling. */
    cleared: z.array(deletionCountSchema),
    /** Rows a delete leaves alone — events, gifts, tickets, payouts. */
    retained: z.array(deletionCountSchema),
    /** Rows no code can remove: the six append-only tables that reference an organization. */
    immutable: z.array(deletionCountSchema),
    /** The one conclusion drawn from all of the above: whether a scrubbed row will remain. */
    keepsRecord: z.boolean(),
});
export const archiveOrganizationSchema = z.object({
    archived: z.boolean(),
});
export const archiveOrganizationResponseSchema = z.object({
    id: z.string().uuid(),
    archivedAt: z.string().nullable(),
});
/** Platform console: switch DAF giving on or off for an approved organization (audited). */
export const setOrganizationDafSchema = z.object({
    enabled: z.boolean(),
    /**
     * A Connect ID pasted from the organization's own Chariot dashboard — the fallback when the
     * platform key cannot create Connects (Chariot provisions that "application" capability per
     * partner). Chariot's model explicitly supports a nonprofit handing its CID to a platform.
     */
    connectId: z.string().min(4).max(120).optional(),
});
export const setOrganizationDafResponseSchema = z.object({
    enabled: z.boolean(),
    /** Present once the organization is registered with Chariot. */
    connectId: z.string().nullable(),
});
/**
 * A platform staff override of the listing rule for one organization (migration 0123, GH-892):
 * `force_listed` lists it regardless of Candid/human status, `force_hidden` hides it regardless.
 * Request/response shapes for the route that sets it live in `candid.ts`.
 */
export const appListingOverrideDirectionSchema = z.enum(["force_listed", "force_hidden"]);
/** The override in effect for one organization, as the console shows it (GH-892). */
export const appListingOverrideStateSchema = z.object({
    override: appListingOverrideDirectionSchema,
    reason: z.string(),
    byStaffName: z.string().nullable(),
    at: z.string(),
});
/**
 * One organization's Apple approval as the platform console sees it (GH-870): Candid's last
 * definitive answer, the person's assertion beside it (never merged), and the listing verdict the
 * server computed (`candidSeals.currentCandidSealSql`). Platform staff only; never on a donor
 * surface. Wire details live in `candid.ts`.
 */
export const candidStatusWireSchema = z.object({
    /** Would the app list it with the filter on, right now. Decided by the server, never here. */
    listableInApp: z.boolean(),
    check: z
        .object({
        outcome: z.enum(["eligible", "ineligible", "not_found"]),
        message: z.string().nullable(),
        profileLevel: z.string().nullable(),
        profileYear: z.number().int().nullable(),
        profileUrl: z.string().nullable(),
        source: z.string(),
        checkedAt: z.string(),
        /** False once the record's EIN changed after this answer: it was about another EIN. */
        aboutCurrentEin: z.boolean(),
    })
        .nullable(),
    /** A person verified a seal (migration 0024); counts for a year unless Candid later disagrees. */
    humanVerifiedAt: z.string().nullable(),
    /** The last lookup tried, answered or not. */
    lastAttemptedAt: z.string().nullable(),
    /** A staff override in effect for this organization (GH-892). Null is the ordinary case. */
    override: appListingOverrideStateSchema.nullable().default(null),
});
export const adminOrganizationSchema = z.object({
    id: z.string().uuid(),
    legalName: z.string(),
    displayName: z.string(),
    city: z.string().nullable(),
    region: z.string().nullable(),
    claimStatus: z.string(),
    paymentsEnabled: z.boolean(),
    canAcceptDonations: z.boolean(),
    addressLine1: z.string().nullable().default(null),
    postalCode: z.string().nullable().default(null),
    ein: z.string().nullable().default(null),
    /** Import provenance ('Tier C — validate before outreach'). Reviewer-facing only. */
    sourceNote: z.string().nullable().default(null),
    dafEnabled: z.boolean().default(false),
    /** ISO timestamp when a platform reviewer archived the record; null = active. */
    archivedAt: z.string().nullable().default(null),
    /** Apple approval through Candid (GH-870). Null from a server that predates it. */
    candid: candidStatusWireSchema.nullable().default(null),
});
export const adminOrganizationListResponseSchema = z.object({
    organizations: z.array(adminOrganizationSchema),
});
/* ----------------------------------------------------------------- membership */
/**
 * The roles an organization console may hand out. Deliberately excludes
 * `sensitive_data_administrator` and `platform_security_administrator`: those are platform-level
 * appointments (SPEC.md §3.2), and an organization must not be able to mint a role that can
 * unmask donors or edit security policy.
 */
export const GRANTABLE_ORGANIZATION_ROLES = [
    "organization_owner",
    "organization_admin",
    "event_manager",
    "finance_reconciler",
    "volunteer_entry_clerk",
];
export const addMemberSchema = z.object({
    email: z.string().min(3).max(254),
    /** Used only when the address is new to the system; an existing user keeps their own name. */
    displayName: z.string().min(1).max(120).optional(),
    role: z.enum(GRANTABLE_ORGANIZATION_ROLES),
});
export const revokeMemberRoleSchema = z.object({
    role: z.enum(GRANTABLE_ORGANIZATION_ROLES),
});
/**
 * How a staff member has proved who they are (GH-827), read from `staff_sessions.auth_method`.
 * `google` is the stored `oidc` method — Google is the only issuer the console accepts.
 */
export const STAFF_SIGN_IN_METHODS = [
    "google",
    "passkey",
    "donor_account",
    "development_code",
];
/**
 * A staff member of one organization. Staff, never donors.
 *
 * The GH-827 fields are optional on the schema and always sent by this server (`memberToWire`).
 * Optional rather than defaulted so that a client built against the four-field shape (the phone's
 * `OrgAdminMember`, the console's fixtures) keeps compiling and decoding, and a newer client reading
 * an older server sees "absent" rather than a parse failure.
 */
export const memberSchema = z.object({
    staffUserId: z.string().uuid(),
    email: z.string(),
    displayName: z.string(),
    roles: z.array(z.string()),
    /**
     * When this person last opened a staff session, in any organization. Sessions carry no
     * last-request time, so this is the last sign-in, not the last click. Sessions an operator opened
     * *as* them (impersonation) do not count: that was the operator. Null when they never signed in.
     */
    lastActiveAt: z.string().nullable().optional(),
    /** Every way they have signed in, most recent first. Empty when they have never signed in. */
    signInMethods: z.array(z.enum(STAFF_SIGN_IN_METHODS)).optional(),
    /**
     * True when a passkey has proved one of their sessions. Null means *unknown*, never "no": nothing
     * in the staff tables records an enrolled passkey or Google's own second factor, so the absence of
     * a passkey session is not evidence that they have none.
     */
    passkeyRegistered: z.literal(true).nullable().optional(),
    /**
     * An invitation not yet taken up: the role was granted but the account has never signed in.
     * There is no separate invitation record — granting a role is the invitation.
     */
    pending: z.boolean().optional(),
    /** When this person was first given a role in this organization. */
    addedAt: z.string().nullable().optional(),
});
export const memberListResponseSchema = z.object({ members: z.array(memberSchema) });
/* -------------------------------------------------------------- activity (GH-827) */
/** The four shelves the Team screen's activity feed sorts every audit row onto. */
export const ACTIVITY_TAGS = ["money", "access", "event", "settings"];
/**
 * One line of an organization's activity feed: an `audit_events` row rendered as a sentence.
 *
 * The sentence is built on the server from an allow-list (`services/api/src/activity-sentences.ts`)
 * and the payload never reaches the wire — a payload is where an email, a ticket code or a donor's
 * name would ride along. `who` is a staff member's display name, or "System", or "Donor"; a donor
 * is never named.
 */
export const activityEntrySchema = z.object({
    id: z.string().uuid(),
    occurredAt: z.string(),
    who: z.string(),
    /** The staff actor's id, so a console can filter by person. Null for System and Donor. */
    actorStaffUserId: z.string().uuid().nullable(),
    /** Set when a platform operator acted inside a member's session: that member's name. */
    actingAs: z.string().nullable(),
    what: z.string(),
    /** The reason recorded with a staff action, when there was one. Emails are scrubbed out. */
    why: z.string().nullable(),
    tag: z.enum(ACTIVITY_TAGS),
    /** The stored event type, for the full log. Never shown in place of `what`. */
    eventType: z.string(),
});
export const activityFeedResponseSchema = z.object({
    entries: z.array(activityEntrySchema),
    /** Opaque keyset cursor, or null at the end. Newest first, stable while rows are appended. */
    nextCursor: z.string().nullable(),
});
/** Query string for `GET /v1/organizations/:id/activity` and its `activity.csv` sibling. */
export const activityFeedQuerySchema = z.object({
    tag: z.enum(ACTIVITY_TAGS).optional(),
    /** A staff user id, or `system` (system and provider rows), or `donor`. */
    actor: z.union([z.string().uuid(), z.literal("system"), z.literal("donor")]).optional(),
    /** ISO-8601 instants bounding the window: `since` inclusive, `until` exclusive. */
    since: z.string().datetime({ offset: true }).optional(),
    until: z.string().datetime({ offset: true }).optional(),
    cursor: z.string().max(200).optional(),
    limit: z.coerce.number().int().min(1).max(200).default(50),
});
export const claimSummarySchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    organizationDisplayName: z.string(),
    state: z.string(),
    claimantName: z.string(),
    claimantEmail: z.string(),
    claimantRole: z.string(),
    assertedLegalName: z.string(),
    assertedEin: z.string().nullable(),
    submittedAt: z.string(),
    /** The officer-or-director attestation (GH-813). Null on claims recorded before it existed. */
    officerAttestation: z
        .object({ name: z.string(), title: z.string(), attestedAt: z.string() })
        .nullable()
        .default(null),
    /** True when the system, not a reviewer, approved this claim (GH-813). */
    approvedAutomatically: z.boolean().default(false),
    /**
     * The automatic check, as recorded when the claim arrived (GH-813): what it compared, against
     * which IRS filing, read when, and what it concluded. Null on claims recorded before it existed.
     */
    autoApproval: z
        .object({
        outcome: z.enum(["approved", "manual"]),
        description: z.string(),
        rule: z.enum(["email_domain", "phone"]).nullable(),
        reason: z.string().nullable(),
        matchedEmailDomain: z.string().nullable(),
        filingDomain: z.string().nullable(),
        filingPhone: z.string().nullable(),
        filingSource: z.string().nullable(),
        filingReturnId: z.string().nullable(),
        filingTaxPeriodEnd: z.string().nullable(),
        filingReadAt: z.string().nullable(),
    })
        .nullable()
        .default(null),
    /**
     * What the platform's fund holds for this organization from gifts made before it claimed
     * (GH-798), derived from ledger entries. On approval it moves into the organization's ordinary
     * lane. Null while `FEATURE_UNCLAIMED_ORG_GIFTS` is off and nothing is held.
     */
    heldBalance: z
        .object({
        held: moneySchema,
        gifts: z.number().int().nonnegative(),
        oldestReceivedAt: z.string().nullable(),
        releaseDueAt: z.string().nullable(),
    })
        .nullable()
        .default(null),
});
/** One check owed to an unclaimed organization's IRS address of record (GH-798). */
export const heldGiftCheckSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    organizationDisplayName: z.string(),
    amount: moneySchema,
    payeeName: z.string(),
    /** Two lines, frozen when the check was queued: street, then "City, ST 12345". */
    mailingAddress: z.array(z.string()),
    state: z.enum(["queued", "cut", "mailed"]),
    checkNumber: z.string().nullable(),
    gifts: z.number().int().nonnegative(),
    queuedAt: z.string(),
    cutAt: z.string().nullable(),
    mailedAt: z.string().nullable(),
});
/** The operations queue: checks to cut and mail, and every holding organization with no address. */
export const heldGiftOperationsSchema = z.object({
    enabled: z.boolean(),
    holdDays: z.number().int(),
    recipientName: z.string(),
    checks: z.array(heldGiftCheckSchema),
    addressGaps: z.array(z.object({
        organizationId: z.string().uuid(),
        organizationDisplayName: z.string(),
        /** False means the registry cannot be read for it: an EIN has to be added first. */
        hasEin: z.boolean(),
        gifts: z.number().int().nonnegative(),
        held: moneySchema,
        oldestReceivedAt: z.string(),
        releaseDueAt: z.string(),
        overdue: z.boolean(),
    })),
});
/** What one run of the day-40 job did. */
export const heldGiftReleaseRunSchema = z.object({
    organizationsExamined: z.number().int().nonnegative(),
    checksQueued: z.number().int().nonnegative(),
    giftsReleasedToChecks: z.number().int().nonnegative(),
    giftsReleasedOnClaim: z.number().int().nonnegative(),
    addressGaps: z.number().int().nonnegative(),
});
/** The IRS mailing address of record, as the registry read just recorded it (GH-798). */
export const irsMailingAddressResponseSchema = z.object({
    organizationId: z.string().uuid(),
    name: z.string(),
    line1: z.string(),
    city: z.string(),
    state: z.string(),
    postalCode: z.string(),
    recordedAt: z.string(),
});
export const markCheckCutSchema = z.object({
    checkNumber: z
        .string()
        .trim()
        .min(1)
        .max(32)
        .regex(/^[A-Za-z0-9-]+$/, "a check number is letters, digits and dashes"),
});
export const claimDecisionSchema = z.object({
    toState: z.enum(["reviewing", "more_information", "approved", "rejected"]),
    /** Required, and required to be non-empty: a decision with no stated reason is not evidence. */
    rationale: z.string().min(1).max(2000),
    evidenceSummary: z.string().max(2000).optional(),
    limitations: z.string().max(2000).optional(),
});
/*
 * Response envelopes for the console.
 *
 * Defined here rather than assembled with `z.object` inside the admin bundle, so that bundle
 * depends on this package alone — the same boundary the donor bundle keeps (ADR 0002). A browser
 * app that reaches for a validation library starts being able to define its own idea of a
 * response shape, and the point of this file is that there is only one.
 */
export const eventListResponseSchema = z.object({ events: z.array(eventSummarySchema) });
export const claimListResponseSchema = z.object({ claims: z.array(claimSummarySchema) });
export const createOrganizationResponseSchema = z.object({
    id: z.string().uuid(),
    displayName: z.string(),
    /** Always "unclaimed" on creation. Present so the console can show it rather than assume it. */
    claimStatus: z.string(),
    canAcceptDonations: z.boolean(),
});
export const claimDecisionResponseSchema = z.object({
    claimId: z.string().uuid(),
    state: z.string(),
});
/* ----------------------------------------------- donor check-in & engagement */
/**
 * A donor identifying themselves is the opposite default from the gift path, which is anonymous
 * unless asked. Everything in this section is donor-initiated: sign in, check in, respond. None
 * of it touches money, and no schema here carries a donation, receipt, or payment identifier.
 */
export const donorSignInStartSchema = z.object({
    phone: z.string().min(7).max(20),
});
export const donorSignInStartResponseSchema = z.object({
    verificationId: z.string().uuid(),
    /** Present only where nothing can deliver a code and the environment is not production. */
    developmentCode: z.string().nullable(),
    last4: z.string(),
});
export const donorSignInConfirmSchema = z.object({
    verificationId: z.string().uuid(),
    code: z.string().min(4).max(10),
    /**
     * The same number the donor typed at the start. Re-sent so the server can store the address for
     * the verified fingerprint; it is checked against what the challenge actually proved, so
     * confirming one number cannot attach a different one.
     */
    phone: z.string().min(7).max(20),
    /** GH-871a: an invite code or the big-screen join token, for a sign-up (see `access.ts`). */
    ...signUpAccessFields,
});
/**
 * Changing the phone a donor signs in with (owner-directed 2026-08-22).
 *
 * The same two-step shape as sign-in, and deliberately so: proving control of a number is proving
 * control of a number, whether it mints a session or moves one. What differs is the gate at each
 * end — both routes require an existing donor session, so this is "I am already this donor and I
 * also hold this number", never "this number, therefore this donor".
 *
 * The number is **not** in the response. `donorProfileSchema` carries `phoneLast4` and nothing
 * more, which is all a donor needs to recognise their own number and all this app is willing to
 * hold.
 */
/**
 * Confirming an email address (owner-directed 2026-08-25).
 *
 * A separate pair from the phone flow rather than an alias of it, because the two prove different
 * things and one of them is not an identity. A phone number is the donor's sign-in anchor: proving
 * it can mint a session and can merge two profiles. An email address is contact information —
 * proving it marks one `verified_contacts` row and does nothing else. Reusing the phone shapes
 * would have invited a caller to spend an email challenge on a sign-in, which is exactly what
 * `consumedBy` exists to prevent, so the shapes stay apart too.
 *
 * The address is repeated at confirm and checked against the fingerprint the challenge recorded.
 * The challenge names the contact it proved; the caller does not get a vote.
 */
export const donorEmailVerificationStartSchema = z.object({
    email: z.string().email().max(254),
});
export const donorEmailVerificationStartResponseSchema = z.object({
    verificationId: z.string().uuid(),
    /**
     * The code, returned on screen only where no email can be delivered and this is not production
     * — the same posture the SMS flow takes, and for the same reason: a deployment that cannot send
     * must still be testable, and a deployment that can send must never leak the code.
     */
    developmentCode: z.string().nullable(),
});
export const donorEmailVerificationConfirmSchema = z.object({
    verificationId: z.string().uuid(),
    code: z.string().min(4).max(10),
    email: z.string().email().max(254),
});
export const donorPhoneChangeStartSchema = donorSignInStartSchema;
export const donorPhoneChangeStartResponseSchema = donorSignInStartResponseSchema;
export const donorPhoneChangeConfirmSchema = donorSignInConfirmSchema;
/**
 * Donor sign-in with an identity provider (GH-520).
 *
 * The `credential` is an OpenID Connect **ID token** minted by Apple or Google for this app. It is
 * not trusted here in any degree: the server verifies its signature against the issuer's published
 * keys, its audience against our own client id, and its expiry, before reading a single claim.
 * Nothing in this shape can assert an identity — it can only carry a token that either survives
 * that check or does not.
 *
 * `displayName` is a *separate* field on purpose. Apple returns the person's name exactly once,
 * outside the token, at first authorization; there is nowhere in the ID token to find it later. So
 * the client passes it along as a suggestion, and the server treats it as one — it seeds an empty
 * profile name and never overwrites a name the donor has already chosen.
 */
export const donorOidcSignInSchema = z.object({
    credential: z.string().min(16),
    displayName: z.string().min(1).max(80).optional(),
    /**
     * GH-957: Sign in with Apple's single-use authorization code, sent beside the ID token. The
     * server exchanges it at Apple for a token it keeps only so it can revoke the donor's Apple
     * authorization when they delete their account (Guideline 5.1.1(v)). Optional: builds before
     * GH-957 do not send it, and Google's door ignores it.
     */
    authorizationCode: z.string().min(8).max(1024).optional(),
    /** GH-871a: an invite code or the big-screen join token, for a sign-up (see `access.ts`). */
    ...signUpAccessFields,
});
/**
 * Sign in with Apple **on the web** (GH-856, DEC-015).
 *
 * A different audience from the iPhone app's: Sign in with Apple JS mints its ID token for an Apple
 * *Services ID*, never for the app's bundle identifier, so the web door has its own verifier and
 * its own route and a token minted for one is refused at the other.
 *
 * `nonce` is the value `POST /v1/donor/sign-in/apple/web/nonce` issued. The token must carry it,
 * and it is spent on first use, so a token lifted from a browser cannot be replayed to mint a
 * second session — and a token minted for somebody else's page, which never asked us for a nonce,
 * cannot be presented here at all.
 */
export const donorAppleWebSignInSchema = z.object({
    credential: z.string().min(16),
    nonce: z.string().min(32).max(128),
    displayName: z.string().min(1).max(80).optional(),
    /** GH-871a: an invite code or the big-screen join token, for a sign-up (see `access.ts`). */
    ...signUpAccessFields,
});
export const donorAppleWebNonceResponseSchema = z.object({
    nonce: z.string().min(32).max(128),
    expiresAt: z.string(),
});
/**
 * Every single-use provider door (GH-859 generalised GH-856's): Apple on the web, Google on the
 * web (`/v1/donor/sign-in/google/web`), Google in the iPhone app (`/v1/donor/sign-in/google`) and
 * Google in the Android app (`/v1/donor/sign-in/google/android`, GH-963). Each
 * door issues its own nonce at `<door>/nonce`, and a nonce is spendable only at the door that
 * issued it. The Apple names above are kept as aliases so the GH-856 client keeps compiling.
 */
export const donorNonceSignInSchema = donorAppleWebSignInSchema;
export const donorSignInNonceResponseSchema = donorAppleWebNonceResponseSchema;
/** A web door's public facts: the audience its token is minted for, and where it may return. */
const donorWebDoorSchema = z.object({
    clientId: z.string().min(3),
    redirectUris: z.array(z.string().url()).min(1),
});
/**
 * Which ways in this deployment offers a donor on the web (GH-856).
 *
 * Read by the sign-in sheet before it draws anything, so a door that is not configured is never
 * drawn and then refused. `apple` is null until an operator provisions a Services ID and at least
 * one return address (DEC-015); the page offers Apple only when one of `redirectUris` is on its
 * own origin, because Apple refuses any other.
 *
 * Both values are public by nature — a Services ID appears in every Sign in with Apple request a
 * browser makes, and a return address is a page anybody can open.
 */
export const donorSignInOptionsSchema = z.object({
    phone: z.boolean(),
    apple: donorWebDoorSchema.nullable(),
    /**
     * Continue with Google (GH-859): the donor web client id and the return addresses registered on
     * it, or null. Defaulted so a page served beside an older API — which does not send the key —
     * reads "no Google door" rather than failing to parse the whole options answer.
     */
    google: donorWebDoorSchema.nullable().default(null),
});
export const donorProfileSchema = z.object({
    displayName: z.string().nullable(),
    email: z.string().nullable(),
    /**
     * Whether the donor has proved they hold that inbox (owner-directed 2026-08-25).
     *
     * Defaulted rather than required so every existing caller — the iOS fixtures, the donor
     * session, three test files — keeps compiling, and so the honest answer for a client that has
     * not been taught about this yet is "not proved" rather than a parse failure.
     */
    emailVerified: z.boolean().default(false),
    /** Enough for the donor to recognise their own number; never the full number. */
    phoneLast4: z.string().nullable(),
    /**
     * Whether a mobile number on the profile was proved by an SMS code (GH-883). Checking in and the
     * door prize skip the "verify your mobile" step when this is true. Defaulted for the same reason
     * as `emailVerified`: an older server's answer still parses, as "not proved".
     */
    phoneVerified: z.boolean().default(false),
    postalAddress: postalAddressSchema.nullable(),
});
/**
 * The outcome of confirming a number, including whether it folded in another account (DEC-019).
 *
 * `merged` is present only when the number turned out to belong to a second profile of the same
 * donor and that profile was absorbed. It is reported rather than done quietly for two reasons:
 * the donor's giving history is about to change in front of them, and `instrumentsStranded` is a
 * thing they have to act on — a saved card attached to the absorbed profile's payment-provider
 * customer cannot be charged as this one, so it does not come across and has to be re-added.
 */
export const donorPhoneChangeConfirmResponseSchema = z.object({
    profile: donorProfileSchema,
    merged: z
        .object({
        /** Completed gifts that moved onto this account. */
        giftsMoved: z.number().int().nonnegative(),
        /** Saved cards and bank accounts that could not follow and must be added again. */
        instrumentsStranded: z.number().int().nonnegative(),
    })
        .nullable(),
});
export const donorCheckInSchema = z.object({
    eventId: z.string().uuid(),
});
/** Leaving an event is explicit, so it names the event rather than "whatever is latest". */
export const donorCheckOutSchema = z.object({
    eventId: z.string().uuid(),
});
export const donorCheckInStateSchema = z.object({
    eventId: z.string().uuid(),
    organizationId: z.string().uuid(),
    eventName: z.string(),
    organizationDisplayName: z.string(),
    checkedInAt: z.string(),
});
/**
 * The result of a donor deleting their own account.
 *
 * Deliberately not a bare `{ deleted: true }`. App Review requires an in-app deletion path, and a
 * donor who taps it deserves to know what actually happened rather than a checkmark: `retained`
 * names the categories that survive under the accounting-record exception in SPEC.md §16.6, so the
 * screen can say it in the donor's own language instead of the client inventing the list.
 */
export const donorAccountDeletionResponseSchema = z.object({
    deleted: z.literal(true),
    /** Human-readable categories kept as financial records. Empty when the donor never gave. */
    retained: z.array(z.string()),
});
export const donorSessionResponseSchema = z.object({
    /** Present only on issue, like the staff session token. */
    token: z.string(),
    expiresAt: z.string(),
    profile: donorProfileSchema,
    checkIn: donorCheckInStateSchema.nullable(),
    /**
     * GH-871a: how this sign-in made the account a member — an invite code, the big-screen join
     * token, a platform admin's account, or a waitlist admission code. Absent when it made none.
     */
    membershipJoined: z.enum(MEMBERSHIP_JOINED_AT_SIGN_IN).optional(),
});
/**
 * A door prize as the arrival saw it (GH-883): checking in enters the attendee in every door prize
 * the room is running. `number_already_entered` means the attendee's verified number already holds
 * a ticket under another account — the check-in still happened; there is no second ticket.
 */
export const doorPrizeEntrySchema = z.object({
    promptId: z.string().uuid(),
    title: z.string(),
    prize: z.string().nullable(),
    entryNumber: z.number().int().positive().nullable(),
    status: z.enum(["entered", "number_already_entered"]),
});
/**
 * `POST /v1/donor/check-ins` answers the session as before, plus the door prizes the arrival
 * entered (GH-883). Defaulted to empty so every caller that reads it as a plain session — and a
 * server before GH-883 — still parses.
 */
export const donorCheckInResponseSchema = donorSessionResponseSchema.extend({
    doorPrizes: z.array(doorPrizeEntrySchema).default([]),
});
/**
 * A donor's own lifetime giving, shown back to them after a gift (owner-directed 2026-08-17).
 * Giving history is a protected surface (SPEC.md §12.3), so this is deliberately a single
 * aggregate behind a phone-verified session: settled totals and a count, never a per-donation
 * list, never an organization breakdown, never another donor's data.
 */
export const donorGivingSummarySchema = z.object({
    totalGiven: moneySchema,
    giftCount: z.number().int().nonnegative(),
    /** ISO timestamp of the donor's first settled gift, null before any gift settles. */
    firstGiftAt: z.string().nullable(),
});
/**
 * A donor's own gifts, one row each (owner-directed 2026-08-22).
 *
 * This is a deliberate widening of the 2026-08-17 exception above, which was written as "an
 * aggregate only — no per-donation list". The owner asked for the list: the total on the account
 * screen was a number a donor could not act on, and "what did I give, to whom, and where is the
 * receipt" is a question every giving platform answers. Recorded in
 * `docs/evidence/GH-donor-giving-history.md` rather than left as a silent contradiction of the
 * older comment.
 *
 * The protections that made the aggregate acceptable are all still here and are the reason this
 * shape is safe:
 *
 *  - **The donor's own gifts and nobody else's.** `donor_profile_id` is part of the query, not a
 *    filter applied after the fact, so there is no parameter through which another donor's row
 *    could be reached.
 *  - **Behind a verified session.** Same gate as the aggregate: SPEC.md §12.3's protected-surface
 *    rule is about *who may read*, and that has not moved.
 *  - **Settled money only.** A pledge is not a gift and an in-flight payment is not either
 *    (CLAUDE.md). Neither appears here, so nothing in this list can be mistaken for money that
 *    has not arrived.
 *  - **No statement about tax deductibility**, exactly as `receiptSchema` carries none. That is a
 *    counsel decision (DEC-001, DEC-011) and a field here would be a legal claim made by a schema.
 *  - **Admission purchases are absent.** Tickets are not donations (DEC-016) and are ledgered
 *    separately; a ticket appearing in a list of gifts would be the conflation this platform
 *    spends the most effort avoiding.
 */
export const donorGiftSchema = z.object({
    donationId: z.string().uuid(),
    organizationDisplayName: z.string(),
    /** Snapshotted on the receipt, so a later rename cannot rewrite a gift a donor already made. */
    organizationLegalName: z.string(),
    eventName: z.string(),
    designationName: z.string().nullable(),
    donation: moneySchema,
    costSupport: moneySchema,
    total: moneySchema,
    givenAt: z.string(),
    /**
     * Null until the receipt is issued. A settled gift normally has one within seconds, but the
     * webhook that mints it is a separate step and a client must never render "receipt #null".
     */
    receiptNumber: z.string().nullable(),
    receiptId: z.string().uuid().nullable(),
    /** What the donor chose to be shown as publicly. Their own choice, shown back to them. */
    isAnonymous: z.boolean(),
});
export const donorGiftListSchema = z.object({
    gifts: z.array(donorGiftSchema),
    hasMore: z.boolean(),
});
/**
 * Whether the signed-in donor has given to one event (GH-802).
 *
 * The room collapses its giving card into a Give button beside the progress for a donor who has
 * already given there, so the prompts the organization is running move up the screen. That fact
 * is the server's: a phone that remembered "I gave here" could be wrong (a failed charge, another
 * device, a gift claimed later by receipt), and it could not be checked.
 *
 * Deliberately one boolean. No amount, no count, no date, no donation id — the room needs a
 * yes or no and nothing else, and every extra field would be one more thing about a donor's
 * giving on the wire. Settled gifts only (`completed`), the same line `/v1/donor/gifts` draws.
 * The answer for an event that does not exist is the same `false`, so the route is not an
 * oracle for which event ids are real.
 */
export const donorEventGivingSchema = z.object({
    hasGiven: z.boolean(),
});
export const donorProfileUpdateSchema = z.object({
    displayName: z.string().max(80).nullable().optional(),
    email: z.string().min(3).max(254).nullable().optional(),
    postalAddress: postalAddressSchema.nullable().optional(),
});
/**
 * The ways this donor has paid, derived from their own completed gifts. Methods and dates only:
 * no amounts, no organizations, no donation identifiers — giving *history* stays behind the
 * step-up path (SPEC.md §12.3), and no card number or wallet token exists anywhere to return.
 */
/**
 * A saved instrument: the display facts of a way this donor has actually paid, reusable in one
 * tap. Only what a receipt already shows — brand, last four, expiry, bank name. The id is an
 * opaque reference this server resolves; no provider token or number ever crosses the wire.
 */
export const savedInstrumentSchema = z.object({
    id: z.string().uuid(),
    kind: z.enum(["card", "us_bank_account"]),
    brand: z.string().nullable(),
    last4: z.string().regex(/^[0-9]{4}$/),
    expMonth: z.number().int().min(1).max(12).nullable(),
    expYear: z.number().int().nullable(),
    bankName: z.string().nullable(),
    wallet: z.string().nullable(),
    lastUsedAt: z.string(),
});
export const donorPaymentMethodsResponseSchema = z.object({
    /** The method the Give button should lead with: the donor's pick, else the most recent. */
    defaultMethod: chosenPaymentMethodSchema.nullable(),
    /** Saved instruments, most recently used first (GH-330). Empty for a donor with none. */
    instruments: z.array(savedInstrumentSchema),
    /** Set only when the donor chose one explicitly. */
    preferred: chosenPaymentMethodSchema.nullable(),
    methods: z.array(z.object({
        method: chosenPaymentMethodSchema,
        lastUsedAt: z.string(),
        uses: z.number().int(),
    })),
});
export const donorPaymentPreferenceSchema = z.object({
    /** Null clears the preference; the Give button falls back to the most recent method. */
    method: chosenPaymentMethodSchema.nullable(),
});
export const ENGAGEMENT_KINDS = ["raffle", "poll", "question", "comment_wall"];
export const engagementKindSchema = z.enum(ENGAGEMENT_KINDS);
export const engagementPromptSchema = z.object({
    id: z.string().uuid(),
    kind: engagementKindSchema,
    title: z.string(),
    choices: z.array(z.string()).nullable(),
    status: z.enum(["open", "closed"]),
    /** The announced outcome — a raffle winner, a poll result. Public once present. */
    resultSummary: z.string().nullable(),
    /** Whether this prompt is on the event's big screen (several may be; the screen rotates). */
    presented: z.boolean(),
    /** Shelved by the organization: results kept, but out of every working list. */
    archived: z.boolean(),
    /** Raffles only: what the winner takes home. Display copy, never money. */
    prize: z.string().nullable(),
    /** Raffles only: ISO deadline after which the server refuses entries. Null = until drawn. */
    closesAt: z.string().nullable(),
});
export const engagementListResponseSchema = z.object({
    prompts: z.array(engagementPromptSchema),
});
export const engagementSubmitSchema = z.object({
    choice: z.string().max(200).optional(),
    body: z.string().max(1000).optional(),
    /**
     * Ask without your name on the big screen (owner-directed 2026-08-22).
     *
     * Optional and defaulting to false, so a client that has never heard of it submits exactly
     * what it submitted before. The server honours it for questions and comment walls only — a
     * poll publishes no name and a raffle has to read the winner's out loud — and applies it in
     * the read queries rather than in a view, so the name is not merely hidden, it is not sent.
     */
    anonymous: z.boolean().optional(),
});
/**
 * What a submission hands back. `entryNumber` is the raffle ticket stub — per-prompt sequential,
 * allocated server-side, and stable: entering a raffle twice returns the same number rather than
 * a second one. Null for every non-raffle kind.
 */
export const engagementSubmitResponseSchema = z.object({
    submissionId: z.string().uuid(),
    entryNumber: z.number().int().positive().nullable(),
});
export const engagementQuestionSchema = z.object({
    id: z.string().uuid(),
    body: z.string(),
    displayName: z.string().nullable(),
    votes: z.number().int(),
    answered: z.boolean(),
    /**
     * The question the speaker is answering now (GH-878) — at most one per Q&A, marked from the
     * console. Defaulted so a response from a server before it still parses.
     */
    answering: z.boolean().default(false),
    /** Whether the viewing donor has upvoted this — their own state, never anyone else's. */
    mine: z.boolean(),
});
export const engagementDetailResponseSchema = z.object({
    prompt: engagementPromptSchema,
    /** The donor's own responses — never anyone else's. `entryNumber` is their raffle ticket. */
    own: z.array(z.object({
        choice: z.string().nullable(),
        body: z.string().nullable(),
        entryNumber: z.number().int().positive().nullable(),
    })),
    /** Poll only, and only after the donor has voted: counts, never voters. */
    tallies: z.array(z.object({ choice: z.string(), count: z.number().int() })).nullable(),
    /** Comment wall only: approved comments with the writer's chosen name. */
    comments: z
        .array(z.object({
        /** GH-958: what a reader's Report names. */
        id: z.string().uuid(),
        body: z.string(),
        displayName: z.string().nullable(),
    }))
        .nullable(),
    /** Q&A only: the room's approved questions, most-raised first, answered last. */
    questions: z.array(engagementQuestionSchema).nullable(),
});
export const upvoteResponseSchema = z.object({
    votes: z.number().int(),
    mine: z.boolean(),
});
/**
 * The reaction route's whole answer.
 *
 * Two bytes of "yes", and it is a contract rather than a client-side cast because donor-web takes
 * no direct dependency on zod — every schema that bundle validates against arrives from this
 * package, which is the rule that keeps one description of a payload rather than two.
 *
 * Nothing about the sender survives into the public read: the wall sees a kind and a sequence
 * number, so there is deliberately nothing else here to return.
 */
export const reactionAckSchema = z.object({ sent: z.boolean() });
export const presentedItemSchema = z.object({
    prompt: engagementPromptSchema,
    tallies: z.array(z.object({ choice: z.string(), count: z.number().int() })).nullable(),
    questions: z
        .array(z.object({
        body: z.string(),
        displayName: z.string().nullable(),
        votes: z.number().int(),
        answered: z.boolean(),
        /** The question being answered now (GH-878); the wall highlights it. */
        answering: z.boolean().default(false),
    }))
        .nullable(),
    comments: z.array(z.object({ body: z.string(), displayName: z.string().nullable() })).nullable(),
    entrantCount: z.number().int().nullable(),
});
/**
 * Who has the room's attention. An operator toggle, mirrored to the public display. The console
 * names them Fundraising ask (`active`), Between asks (`passive`) and Big Prompt (`qa`, once
 * "Q&A session"; GH-882 kept the stored value so every saved event keeps its mode).
 */
export const DISPLAY_MODES = ["active", "passive", "qa"];
export const displayModeSchema = z.enum(DISPLAY_MODES);
export const REACTION_KINDS_WIRE = ["heart", "clap", "tada", "dua", "laugh"];
export const reactionKindSchema = z.enum(REACTION_KINDS_WIRE);
/**
 * One raffle's public standing, independent of whether it is the presented item. The display
 * keeps a persistent info window from this — prize, entry count, countdown — even while a poll
 * or the comment wall owns the stage. Same donor-free discipline as everything else on this
 * screen: a status, a count, and organizer-written copy; no entrant appears here.
 */
export const raffleStatusSchema = z.object({
    promptId: z.string().uuid(),
    title: z.string(),
    status: z.enum(["open", "closed"]),
    prize: z.string().nullable(),
    entryCount: z.number().int().nonnegative(),
    /** ISO deadline the display counts down to; null means "open until drawn". */
    closesAt: z.string().nullable(),
    /** The announced winner line, once drawn. Entry number and chosen name only. */
    resultSummary: z.string().nullable(),
});
/**
 * The id of one of the fixed gradient presets in
 * `packages/contracts/src/trivia-background-presets.ts` — shape-checked here the same way
 * `eventCoverPresetIdSchema` is; membership in the actual catalogue is checked where the preset
 * is looked up, the same division `event-cover-presets.ts` already draws.
 */
export const TRIVIA_BACKGROUND_PRESET_ID_PATTERN = /^[a-z0-9]+(?:-[a-z0-9]+)*$/;
export const triviaBackgroundPresetIdSchema = z
    .string()
    .min(1)
    .max(64)
    .regex(TRIVIA_BACKGROUND_PRESET_ID_PATTERN);
export const TRIVIA_BACKGROUND_KINDS = ["preset", "custom"];
export const triviaBackgroundKindSchema = z.enum(TRIVIA_BACKGROUND_KINDS);
/**
 * `GET /v1/events/:eventId/trivia/background` — the event's own chosen background, or null when
 * none has ever been set. `backgroundUrl` is set only for `kind: "custom"` (serves the uploaded
 * bytes); a `preset` background has no bytes at all — the client renders `presetId`'s colors from
 * the shared contracts catalogue itself.
 */
export const triviaBackgroundSchema = z.object({
    kind: triviaBackgroundKindSchema,
    presetId: z.string().nullable(),
    backgroundUrl: z.string().nullable(),
});
/**
 * The event's big-screen display settings (GH-922), carried on the public presentation payload.
 * `background` is the same wire shape the trivia round payload has always carried (it now dresses
 * the WHOLE screen); `qrHidden` is the staff QR show/hide, default `false` (showing). Only these
 * two display-facing facts cross the wire -- never the settings row, its tenant, or its timestamps.
 */
export const eventDisplaySchema = z.object({
    // Each field tolerates absence on its own (not just the whole `display` member): a reader must
    // keep working against a server that grows or shrinks this object, and the safe reading of a
    // missing field is always "nothing special" — QR shown, no screen-wide background.
    qrHidden: z.boolean().default(false),
    background: triviaBackgroundSchema.nullable().default(null),
    // GH-927: the resolved big-screen arrangement. Absent / null = the wall keeps today's behaviour.
    // `.catch(null)`: a layout this client cannot read (a kind it does not know yet, a malformed
    // member) degrades to "no layout" — the wall's legacy path — instead of failing the WHOLE
    // presentation parse and blanking the room. Same tolerance rule as the raw display readers.
    layout: screenLayoutSchema.nullable().catch(null).default(null),
    // GH-926: the WEB-host short URL (`https://<web-host>/q/<code>`) the wall encodes instead of the
    // long tokened join URL — `wall encodes joinShortUrl ?? joinUrl` is the fixed contract. Minted
    // lazily on the first presentation read; same per-field tolerance as `layout`: a malformed or
    // absent member degrades to null (the wall falls back to the long URL), never blanks the room.
    joinShortUrl: z.string().nullable().catch(null).default(null),
});
/** `GET /v1/events/:eventId/display/qr` and `POST .../display/qr/show|hide` (GH-922). */
export const eventQrResponseSchema = z.object({ shown: z.boolean() });
/**
 * What the big screen shows beside (or instead of) the thermometer. Donor-free by
 * construction: counts, approved text, chosen display names, and anonymous reaction pulses.
 * `presentation` repeats `presentations[0]` for pre-rotation clients.
 */
export const presentationResponseSchema = z.object({
    presentation: presentedItemSchema.nullable(),
    presentations: z.array(presentedItemSchema),
    displayMode: displayModeSchema,
    /** The last few seconds of audience reactions; `sequence` is the dedupe high-water mark. */
    reactions: z.array(z.object({ sequence: z.number().int(), kind: reactionKindSchema })),
    /** Every raffle the room may see: presented and not archived (GH-878; hidden ones are withheld). */
    raffles: z.array(raffleStatusSchema).default([]),
    /**
     * GH-922: screen-wide display settings. Additive and defaulted so an older API's payload (or a
     * cached one) still parses: no `display` reads as "QR showing, no background".
     */
    display: eventDisplaySchema.default({
        qrHidden: false,
        background: null,
        layout: null,
        joinShortUrl: null,
    }),
});
/** A checked-in attendee reacting. The response is a bare acknowledgement. */
export const reactSchema = z.object({ kind: reactionKindSchema });
export const setDisplayModeSchema = z.object({ mode: displayModeSchema });
/* ------------------------------------------------------------------ trivia (GH-911, GH-912) */
export const TRIVIA_QUESTION_TYPES = ["multiple_choice", "jumble"];
export const triviaQuestionTypeSchema = z.enum(TRIVIA_QUESTION_TYPES);
export const TRIVIA_PACK_STATUSES = ["draft", "published", "archived"];
export const triviaPackStatusSchema = z.enum(TRIVIA_PACK_STATUSES);
/**
 * A library pack's own identity. `category` is free text on purpose — CLAUDE.md forbids
 * hard-coding any one faith, denomination, or ethnicity into schema, and a fixed enum here would
 * be exactly that. "Islamic History", "Arab & South Asian Modern Culture", and "General
 * Knowledge" (GH-913) are peer rows an organization opts into, never a platform default.
 */
export const triviaPackSchema = z.object({
    id: z.string().uuid(),
    title: z.string(),
    category: z.string(),
    description: z.string().nullable(),
    status: triviaPackStatusSchema,
    questionCount: z.number().int().nonnegative(),
});
/**
 * The correct-answer shape. Multiple-choice names the right option's index; jumble names the
 * correct permutation of `choices`' indices. Carried on the authoring schema only — see
 * `triviaQuestionPlayerSchema`, which deliberately has no field for this.
 */
export const triviaCorrectAnswerSchema = z.union([
    z.object({ choiceIndex: z.number().int().nonnegative() }),
    z.object({ order: z.array(z.number().int().nonnegative()).min(2) }),
]);
/** Admin-only. Carries the correct answer — never sent to a donor-facing client. */
export const triviaQuestionAuthoringSchema = z.object({
    id: z.string().uuid(),
    packId: z.string().uuid(),
    questionType: triviaQuestionTypeSchema,
    prompt: z.string(),
    choices: z.array(z.string()).min(2),
    correctAnswer: triviaCorrectAnswerSchema,
    points: z.number().int().positive(),
    timeLimitSeconds: z.number().int().positive(),
    sequence: z.number().int().nonnegative(),
});
/**
 * What a player's device may receive while a question is live: everything needed to render and
 * answer it, and nothing that reveals the answer. `correctAnswer` is intentionally absent —
 * adding it here would be the leak, not a missing field elsewhere.
 */
export const triviaQuestionPlayerSchema = z.object({
    id: z.string().uuid(),
    questionType: triviaQuestionTypeSchema,
    prompt: z.string(),
    choices: z.array(z.string()).min(2),
    points: z.number().int().positive(),
    timeLimitSeconds: z.number().int().positive(),
});
export const TRIVIA_ROUND_STATUSES = ["lobby", "active", "between_questions", "finished"];
export const triviaRoundStatusSchema = z.enum(TRIVIA_ROUND_STATUSES);
export const triviaLeaderboardEntrySchema = z.object({
    playerId: z.string().uuid(),
    displayName: z.string(),
    score: z.number().int().nonnegative(),
    /** This player's own rank, 1-based; ties share a rank. */
    rank: z.number().int().positive(),
});
/**
 * A round's own identity and status — never a question's content. Returned by the round-lifecycle
 * routes (create, start/next/reveal/end, the staff "active" check): everything a console needs to
 * know what state a round is in, with nothing a donor-facing surface would need to sanitize.
 *
 * `packId` is nullable (GH-914): an autoplay round pools every published pack's questions, so it
 * is never "of" one pack — `isAutoplay` is what a console or the big screen checks instead.
 */
export const triviaRoundSchema = z.object({
    id: z.string().uuid(),
    eventId: z.string().uuid(),
    organizationId: z.string().uuid(),
    packId: z.string().uuid().nullable(),
    isAutoplay: z.boolean(),
    status: triviaRoundStatusSchema,
    currentQuestionId: z.string().uuid().nullable(),
    /** ISO timestamp the current question started; null outside an active question. */
    questionStartedAt: z.string().nullable(),
});
/** `GET /v1/events/:eventId/trivia/rounds/active` — the event's live round, or none. */
export const triviaRoundResponseSchema = z.object({
    round: triviaRoundSchema.nullable(),
});
/**
 * One of the three fastest players to answer the current question correctly (GH-914) — a
 * display-name shoutout at reveal, never a score or a response time (the leaderboard already
 * carries score; this is the moment-of-reveal banner, not a second leaderboard).
 */
export const triviaFastestCorrectEntrySchema = z.object({
    displayName: z.string(),
});
export const triviaBackgroundResponseSchema = z.object({
    background: triviaBackgroundSchema.nullable(),
});
/** JSON body of `POST /v1/events/:eventId/trivia/background` when choosing a preset. */
export const chooseTriviaBackgroundPresetSchema = z
    .object({ presetId: triviaBackgroundPresetIdSchema })
    .strict();
/**
 * The round's display phase (GH-916). The round `status` says where the state machine is; the
 * phase says what a screen should be drawing right now, server-computed from the server's own
 * clock so no client ever re-derives it from raw answers:
 *
 *  - `question` — status `active`: the question and its countdown.
 *  - `reveal` — status `between_questions`, the first `REVEAL_PHASE_MS` (5s) after the question
 *    closed: the same choices, with the correct one highlighted in place (`reveal` below is
 *    non-null exactly here and in `leaderboard`).
 *  - `leaderboard` — status `between_questions` after the reveal window: this question's correct
 *    answerers in answer-speed order. On an autoplay round this phase ends on its own
 *    (`LEADERBOARD_PHASE_MS`, 5s) and the next question starts; on a manual round it holds until
 *    staff advance, and `phaseEndsAt` is null.
 *  - `final` — status `finished`: final standings, held on the public route for a bounded window.
 *
 * Null only in `lobby`, where there is nothing to draw.
 */
export const TRIVIA_ROUND_PHASES = ["question", "reveal", "leaderboard", "final"];
export const triviaRoundPhaseSchema = z.enum(TRIVIA_ROUND_PHASES);
/** The first player to answer the current question correctly — the reveal-moment name check. */
export const triviaRevealFirstCorrectSchema = z.object({
    displayName: z.string(),
    /** Seconds from the question appearing to the server receiving their answer, one decimal. */
    secondsAfterStart: z.number().nonnegative(),
});
/** One correct answerer on the post-question results flash: speed left, round total right. */
export const triviaRevealResponseSchema = z.object({
    displayName: z.string(),
    secondsAfterStart: z.number().nonnegative(),
    /** Their running total for the whole round, this question's points already included. */
    totalScore: z.number().int().nonnegative(),
});
/**
 * The reveal facts for the question that just closed (GH-916), server-computed and carried only
 * once the question can no longer be answered (status `between_questions`) — never while the
 * question is live, which is the same anti-cheat line `triviaQuestionPlayerSchema` draws. The
 * big screen highlights `correctAnswer` among the still-displayed choices rather than cutting to
 * a separate reveal card. `firstCorrect` is null when nobody answered correctly — no name is
 * shown. `correctResponses` is every correct answerer in answer-speed order, capped at ten (the
 * same bounded-render discipline the leaderboard already applies).
 */
export const triviaRevealSchema = z.object({
    correctAnswer: triviaCorrectAnswerSchema,
    firstCorrect: triviaRevealFirstCorrectSchema.nullable(),
    correctResponses: z.array(triviaRevealResponseSchema).max(10),
});
/**
 * The big screen's and a donor's shared live state — polled, not pushed (this stack has no
 * websocket/SSE channel yet; raised as an open question for GH-912 rather than decided here).
 * `currentQuestion` stays set through `between_questions` (the reveal highlights it in place,
 * GH-916) and is null in `lobby` and `finished`. Carries no `pack` — a surface that needs the
 * pack's own title or category reads it from the event's pack picker, not from here.
 *
 * `liveTally` (GH-914) is a per-choice vote count, index-aligned to `currentQuestion.choices` —
 * present only while the current question is `multiple_choice`; `null` for `jumble` (an ordering
 * has no discrete choice to tally) and whenever there is no current question. It never reveals
 * which index is correct while the question is live. `fastestCorrect` is the top three correct
 * respondents by the server's own `response_ms`, capped at three, empty — never padded — when
 * nobody has answered yet.
 *
 * `phase`/`phaseEndsAt`/`reveal` (GH-916): see `triviaRoundPhaseSchema` and `triviaRevealSchema`
 * above. `phaseEndsAt` is the server-clock ISO deadline the current phase is scheduled to end at
 * (a client may tick toward it locally between polls, exactly as it already does with
 * `questionStartedAt`), null when the phase holds indefinitely (a manual round's leaderboard,
 * and `lobby`).
 */
export const triviaRoundStateSchema = z.object({
    id: z.string().uuid(),
    eventId: z.string().uuid(),
    status: triviaRoundStatusSchema,
    isAutoplay: z.boolean(),
    phase: triviaRoundPhaseSchema.nullable(),
    phaseEndsAt: z.string().nullable(),
    currentQuestion: triviaQuestionPlayerSchema.nullable(),
    /** ISO timestamp the current question started; the client derives its own countdown from it. */
    questionStartedAt: z.string().nullable(),
    leaderboard: z.array(triviaLeaderboardEntrySchema),
    liveTally: z.array(z.number().int().nonnegative()).nullable(),
    fastestCorrect: z.array(triviaFastestCorrectEntrySchema).max(3),
    reveal: triviaRevealSchema.nullable(),
});
/**
 * `GET /v1/public/events/:eventId/trivia/round` — the big screen's poll. `background` rides along
 * on this same response (GH-914) rather than its own public route: the wall only ever needs it
 * exactly when it needs `round` too (no round live, nothing to paint a background behind), and this
 * is the poll it already runs every few seconds — a second public route and a second interval
 * would be a new moving part for data that changes on the order of "once per event," not "every
 * few seconds."
 */
export const triviaRoundStateResponseSchema = z.object({
    round: triviaRoundStateSchema.nullable(),
    background: triviaBackgroundSchema.nullable(),
    /**
     * GH-916: staff hid the event's trivia display. `round` is null while hidden — the wall shows
     * nothing — but donors' own play continues untouched on the donor routes. Defaulted so a
     * response from an API predating this field still parses as "showing".
     */
    hidden: z.boolean().default(false),
});
/**
 * `GET /v1/donor/events/:eventId/trivia/round` — the same round state, plus this donor's own
 * standing. All three trail into `null` together: a donor who has not joined this round's game
 * gets the room's state with nothing about themselves in it, never a fabricated zero score.
 */
export const triviaDonorRoundResponseSchema = z.object({
    round: triviaRoundStateSchema.nullable(),
    myPlayerId: z.string().uuid().nullable(),
    /**
     * GH-925: while a question is live this EXCLUDES the points that question paid this donor —
     * a score that moved on submit would tell them they were right before time is up. It catches
     * up the moment the round flips to `between_questions`.
     */
    myScore: z.number().int().nonnegative().nullable(),
    hasAnswered: z.boolean().nullable(),
    /**
     * GH-925: the donor's own selection for the CURRENT question (so a reload or a second device
     * highlights it too), echoed back verbatim. Carries no correctness. Null when the donor has
     * not answered the current question or there is none.
     */
    myAnswer: z
        .object({ questionId: z.string().uuid(), submittedAnswer: triviaCorrectAnswerSchema })
        .nullable(),
    /**
     * GH-925: the donor's own scored result for the current question, present ONLY once the round
     * has left `active` (the question can no longer be answered, same line as `reveal`). Null
     * while the question is live, and when the donor did not answer.
     */
    myResult: z
        .object({
        questionId: z.string().uuid(),
        isCorrect: z.boolean(),
        pointsAwarded: z.number().int().nonnegative(),
    })
        .nullable(),
});
/** A player's own row on the leaderboard — never another player's `donorProfileId`. */
export const triviaPlayerSchema = z.object({
    id: z.string().uuid(),
    roundId: z.string().uuid(),
    displayName: z.string(),
    score: z.number().int().nonnegative(),
});
/** `POST /v1/donor/events/:eventId/trivia/join` — a donor joining the event's live round. */
export const triviaJoinSchema = z.object({
    displayName: z.string().min(1).max(40),
});
/** `POST /v1/events/:eventId/trivia/rounds` — staff starting a lobby round from one of the event's own packs. */
export const triviaCreateRoundSchema = z.object({
    packId: z.string().uuid(),
});
/**
 * `PATCH /v1/events/:eventId/trivia/rounds/:roundId/autoplay` (GH-916) — flip autoplay on a live
 * round at any time, not just at creation. Turning it on hands the pacing to the server's
 * phase deadlines from the current question's own state; turning it off leaves the round where
 * it is for staff to drive manually. Refused on a finished round.
 */
export const setTriviaAutoplaySchema = z
    .object({
    isAutoplay: z.boolean(),
})
    .strict();
/**
 * `GET /v1/events/:eventId/trivia/display` and `POST .../display/present|unpresent` (GH-916) —
 * the event's trivia show/hide toggle, named like the engagement prompts' own present/unpresent
 * so the console treats every big-screen surface uniformly. `presented: true` is the default:
 * a live round shows unless staff took it off the wall.
 */
export const triviaDisplayResponseSchema = z.object({
    presented: z.boolean(),
});
/**
 * `POST /v1/donor/trivia/rounds/:roundId/answer` — a player's submitted answer. The server's own
 * receipt time is what scores it — the client never reports its own elapsed time, the same
 * never-trust-the-client discipline a donation's amount is held to. Field name matches
 * `trivia_answers.submitted_answer` on purpose, so the wire shape and the column never drift.
 */
export const triviaAnswerSubmitSchema = z.object({
    questionId: z.string().uuid(),
    submittedAnswer: triviaCorrectAnswerSchema,
});
/**
 * GH-925: the submit response ACKNOWLEDGES only — acceptance plus the donor's own selection
 * echoed back. It deliberately has no `isCorrect`, `pointsAwarded` or `correctAnswer` (points
 * betray correctness just as surely): an early submit would otherwise let a donor learn the
 * answer before time is up and share it. Correctness, points and the answer reach clients only
 * through the round poll, once the phase flips to reveal (`triviaRevealSchema`, and the donor's
 * own `myResult` on `triviaDonorRoundResponseSchema`).
 */
export const triviaAnswerAckSchema = z.object({
    accepted: z.literal(true),
    submittedAnswer: triviaCorrectAnswerSchema,
});
/* -------------------------------------------------------- trivia authoring (admin console) */
/** `GET /v1/admin/trivia/packs/:packId` — the pack with its full question list, answers included. */
export const triviaPackDetailSchema = z.object({
    pack: triviaPackSchema,
    questions: z.array(triviaQuestionAuthoringSchema),
});
export const triviaPackListResponseSchema = z.object({
    packs: z.array(triviaPackSchema),
});
/**
 * `POST /v1/admin/trivia/packs` and `PATCH .../:packId` share a shape: a pack's own facts, never
 * its status — status changes through the dedicated publish/archive/unarchive routes, each its
 * own audited act, never folded into an ordinary field edit.
 */
export const triviaPackWriteSchema = z.object({
    title: z.string().trim().min(1).max(200),
    category: z.string().trim().min(1).max(80),
    description: z.string().trim().max(2000).nullable(),
});
/**
 * `POST /v1/admin/trivia/packs/:packId/questions` and `PATCH .../questions/:questionId`.
 * `sequence` has no field here — see `createQuestion`'s own comment: it is always server-
 * assigned, never a caller-supplied number.
 */
export const TRIVIA_DEFAULT_TIME_LIMIT_SECONDS = 30;
export const triviaQuestionWriteSchema = z.object({
    questionType: triviaQuestionTypeSchema,
    prompt: z.string().trim().min(1).max(500),
    choices: z.array(z.string().trim().min(1).max(200)).min(2).max(8),
    correctAnswer: triviaCorrectAnswerSchema,
    points: z.number().int().min(1).max(10_000),
    timeLimitSeconds: z.number().int().min(5).max(300),
});
/** `GET /v1/events/:eventId/trivia/packs` — the event's own picker: what it may choose from. */
export const eventTriviaPacksResponseSchema = z.object({
    available: z.array(triviaPackSchema),
    activePackIds: z.array(z.string().uuid()),
});
/** `PUT /v1/events/:eventId/trivia/packs` — replaces the whole selection, never appends. */
export const setEventTriviaPacksRequestSchema = z.object({
    packIds: z.array(z.string().uuid()),
});
/* ------------------------------------------------- appeal ladder (org console) */
/** A rung's amount: whole minor units, more than zero, at most $10,000,000. */
const appealAmountMinorSchema = minorUnitsSchema.refine(
// The regex above does not abort the chain, so a non-integer reaches here and must not throw.
(value) => /^\d{1,10}$/.test(value) && BigInt(value) > 0n && BigInt(value) <= 1000000000n, { message: "An amount must be more than zero and at most 10,000,000.00." });
/** `PUT /v1/events/:eventId/appeal` — the ladder, in call order. Only before the appeal starts. */
export const setAppealRungsSchema = z.object({
    rungs: z
        .array(z.object({
        amountMinor: appealAmountMinorSchema,
        // No product ceiling (owner, 2026-10-03); the bound is the INTEGER column's.
        targetCount: z.number().int().min(1).max(2_147_483_647).nullable().default(null),
        /** "Feed 10 orphans @ $50.00 each" (GH-882); null or absent for a plain amount. */
        unit: appealUnitInputSchema.nullable().default(null),
    }))
        .min(1)
        .max(20),
});
/**
 * `POST /v1/events/:eventId/appeal/next` — the rung the caller believes is current. A stale press
 * names a rung the ladder has left and is refused, so one tap moves one step.
 */
export const advanceAppealSchema = z.object({ fromRungId: z.string().uuid() });
/**
 * `POST /v1/events/:eventId/appeal/hands` — "+1 at $1,000". An anonymous count at the rung being
 * called; `-1` takes back a mis-tap. Requires an `Idempotency-Key` header.
 */
export const recordAppealHandSchema = z.object({
    rungId: z.string().uuid(),
    delta: z.union([z.literal(1), z.literal(-1)]).default(1),
});
/** One rung for the console: the public shape plus how its commitments break down. */
export const appealRungStaffSchema = appealRungPublicSchema.extend({
    position: z.number().int().nonnegative(),
    hands: z.number().int().nonnegative(),
    pledges: z.number().int().nonnegative(),
    gifts: z.number().int().nonnegative(),
    committed: moneySchema,
});
/** The console's view of an event's appeal. Counts only — never a donor. */
export const appealStaffSchema = z.object({
    id: z.string().uuid(),
    eventId: z.string().uuid(),
    status: z.enum(["not_started", "running", "closed"]),
    currentRungId: z.string().uuid().nullable(),
    rungs: z.array(appealRungStaffSchema),
    /** Every commitment at its rung's amount. Not received money; see `appealPublicSchema`. */
    committed: moneySchema,
    commitmentCount: z.number().int().nonnegative(),
    startedAt: z.string().nullable(),
    closedAt: z.string().nullable(),
});
export const appealStaffResponseSchema = z.object({ appeal: appealStaffSchema.nullable() });
/* --------------------------------------------------- engagement (org console) */
/** Raffle deadline bounds: one minute (a countdown, not a race) to seven days. */
export const RAFFLE_CLOSES_MIN_MINUTES = 1;
export const RAFFLE_CLOSES_MAX_MINUTES = 7 * 24 * 60;
export const createPromptSchema = z.object({
    kind: engagementKindSchema,
    title: z.string().min(1).max(200),
    choices: z.array(z.string().min(1).max(200)).max(12).optional(),
    /** Raffles only: what the winner takes home. */
    prize: z.string().min(1).max(200).optional(),
    /**
     * Raffles only: entries close this many minutes from now. Minutes-from-now on purpose — the
     * server computes the absolute deadline from its own clock, so a client with a wrong clock
     * (or a hostile one) cannot schedule a close in the past or a decade out.
     */
    closesInMinutes: z
        .number()
        .int()
        .min(RAFFLE_CLOSES_MIN_MINUTES)
        .max(RAFFLE_CLOSES_MAX_MINUTES)
        .optional(),
});
export const closePromptSchema = z.object({
    resultSummary: z.string().max(500).optional(),
});
export const staffSubmissionSchema = z.object({
    id: z.string().uuid(),
    choice: z.string().nullable(),
    body: z.string().nullable(),
    /**
     * Null when the donor has no name on file **or** is anonymous to all organizations (DEC-049).
     * Since DEC-049 a donor who only asked not to be named on the screen is named here, with
     * `anonymous` set: the organization sees who asked; the wall does not.
     */
    displayName: z.string().nullable(),
    /**
     * The donor asked not to be named on the screen (DEC-019's sibling, owner-directed
     * 2026-08-22). Public anonymity only (DEC-049): staff see the name with this flag and must not
     * put it on the wall. Without it a moderator cannot tell "asked anonymously" from "named".
     */
    anonymous: z.boolean(),
    approvedAt: z.string().nullable(),
    /** Questions only: when a speaker marked it answered. */
    answeredAt: z.string().nullable(),
    /** Questions only (GH-878): the one the speaker is answering now. Defaulted for older servers. */
    answering: z.boolean().default(false),
    /** Questions only: how many attendees raised it. */
    votes: z.number().int(),
    createdAt: z.string(),
    /** Door prizes only: the entry's ticket number. Defaulted for older servers. */
    entryNumber: z.number().int().positive().nullable().default(null),
    /**
     * Door prizes only (GH-888): an entry staff typed by hand, and what keyed it — the guest's ticket,
     * a typed mobile number, or neither. Neither is shown as "Entered by staff without a number" so
     * the host can look for a duplicate. Null for an entry the donor made; `displayName` is the typed
     * name otherwise. Never the number, never the ticket code. Defaulted for older servers.
     */
    staffEntry: z.object({ byTicket: z.boolean(), byMobile: z.boolean() }).nullable().default(null),
});
/** The longest name staff may type for a door-prize guest (GH-888), in code points. */
export const DOOR_PRIZE_GUEST_NAME_MAX_LENGTH = RECOGNITION_NAME_MAX_LENGTH;
/**
 * A guest's name typed by staff for a door-prize hand entry (GH-888): trimmed, never blank, at most
 * 80 code points, one line of plain text — the public name's refusals, because a winner's name is
 * read out in the result line the big screen shows.
 */
export const doorPrizeGuestNameSchema = z
    .string()
    .transform((value) => value.trim())
    .refine((value) => value.length > 0, { message: "Enter the guest’s name." })
    .refine((value) => [...value].length <= DOOR_PRIZE_GUEST_NAME_MAX_LENGTH, {
    message: `A guest’s name can be at most ${DOOR_PRIZE_GUEST_NAME_MAX_LENGTH} characters.`,
})
    .refine((value) => !RECOGNITION_NAME_FORBIDDEN.test(value), {
    message: "A guest’s name must be plain text.",
});
/** An optional one-line field: trimmed, and blank means absent. */
const optionalTrimmed = (max) => z
    .string()
    .max(max)
    .nullish()
    .transform((value) => {
    const trimmed = value?.trim() ?? "";
    return trimmed === "" ? null : trimmed;
});
/**
 * `POST /v1/prompts/:promptId/hand-entries` (GH-888): enter a named guest in a door prize.
 * `ticketCode` is the guest's ticket as the door reads it (scanned or typed); `mobile` a number the
 * guest gives, matched against self check-ins by its keyed hash and never stored as typed. Both
 * optional; with neither the entry is flagged in the console.
 */
export const doorPrizeHandEntryRequestSchema = z.object({
    name: doorPrizeGuestNameSchema,
    ticketCode: optionalTrimmed(80),
    mobile: optionalTrimmed(32),
});
export const doorPrizeHandEntryResponseSchema = z.object({
    submissionId: z.string().uuid(),
    entryNumber: z.number().int().positive(),
    name: z.string(),
    staffEntry: z.object({ byTicket: z.boolean(), byMobile: z.boolean() }),
});
export const staffSubmissionListResponseSchema = z.object({
    submissions: z.array(staffSubmissionSchema),
    /**
     * Poll prompts only, null otherwise. Counts, never voters — the same projection donors see
     * after voting, given to staff without the have-to-vote-in-your-own-poll hurdle.
     */
    tallies: z.array(z.object({ choice: z.string(), count: z.number().int() })).nullable(),
    /**
     * Door prizes (raffles) only, null otherwise (GH-883): who is in, and who is here but not in.
     * Checking in enters the attendee; where a verified mobile is required, an attendee checked in
     * without one is counted in `checkedInWithoutVerifiedPhone` and is not entered. Counts only.
     * Defaulted so a response from a server before GH-883 still parses.
     */
    doorPrize: z
        .object({
        entered: z.number().int().nonnegative(),
        checkedIn: z.number().int().nonnegative(),
        checkedInWithoutVerifiedPhone: z.number().int().nonnegative(),
        /** Whether this deployment requires a verified mobile to check in and to be entered. */
        phoneRequired: z.boolean(),
    })
        .nullable()
        .default(null),
});
/** The outcome of a raffle draw: the prompt as closed, and the announced winner line. */
export const raffleDrawResponseSchema = z.object({
    prompt: engagementPromptSchema,
    winnerDisplayName: z.string().nullable(),
    entrantCount: z.number().int(),
});
/* ------------------------------------------------- offline / envelope batches */
export const OFFLINE_ITEM_TYPES_WIRE = ["cash", "check", "external_card"];
export const offlineItemTypeSchema = z.enum(OFFLINE_ITEM_TYPES_WIRE);
export const createOfflineBatchSchema = z.object({
    /** "Iftar table 3", "Friday envelopes" — whatever the room calls the shoebox. */
    label: z.string().min(1).max(80),
});
export const addOfflineEntrySchema = z.object({
    itemType: offlineItemTypeSchema,
    amountMinor: minorUnitsSchema,
    /** The name on the envelope, if the giver wrote one. Staff-visible only, never public. */
    donorName: z.string().min(1).max(120).optional(),
    /**
     * Checks only, and only the number in the corner — at most ten digits, which cannot hold a
     * routing or account number (SPEC.md §9.3). There is deliberately no field for anything else
     * off the paper.
     */
    checkNumber: z
        .string()
        .regex(/^\d{1,10}$/, "the check number in the corner, digits only")
        .optional(),
    /** YYYY-MM-DD; defaults to today on the server. */
    effectiveDate: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .optional(),
});
export const rejectOfflineBatchSchema = z.object({
    reason: z.string().min(1).max(500),
});
export const reverseOfflineEntrySchema = z.object({
    /** Required: a reversal without a why answers nothing at reconciliation time. */
    reason: z.string().min(1).max(500),
});
export const offlineEntrySchema = z.object({
    id: z.string().uuid(),
    itemType: offlineItemTypeSchema,
    amount: moneySchema,
    donorName: z.string().nullable(),
    checkNumber: z.string().nullable(),
    effectiveDate: z.string(),
    enteredByName: z.string(),
    posted: z.boolean(),
    reversedAt: z.string().nullable(),
    reversalReason: z.string().nullable(),
});
export const offlineBatchSchema = z.object({
    id: z.string().uuid(),
    label: z.string(),
    status: z.enum([
        "draft",
        "submitted",
        "verified",
        "posted",
        "reconciled",
        "closed",
        "rejected",
        "voided",
    ]),
    createdByName: z.string(),
    submittedByName: z.string().nullable(),
    verifiedByName: z.string().nullable(),
    postedAt: z.string().nullable(),
    rejectedReason: z.string().nullable(),
    total: moneySchema,
    entryCount: z.number().int(),
    /** True when verifying this batch will demand a second person (SPEC.md §9.2). */
    needsSecondVerifier: z.boolean(),
    entries: z.array(offlineEntrySchema),
});
export const offlineBatchListResponseSchema = z.object({
    batches: z.array(offlineBatchSchema),
    dualVerificationThreshold: moneySchema,
});
/** Consent copy shown to the donor, versioned so a stored consent record is evidence. */
export const CONSENT_LABELS = {
    event_updates: "Send me updates about this event",
    organization_updates: "Send me news from this organization",
    future_giving: "Tell me about future giving opportunities",
    pledge_reminders: "Remind me about my pledge",
    recurring_notices: "Notify me about my recurring gift",
    product_updates: "Send me updates about GiveHere",
};
/* --------------------------------------------------------- ticketing (DEC-016) */
/*
  Tickets are admission purchases, not donations (owner-decided 2026-08-18). Every surface
  that shows a paid tier repeats that characterization, ticket money never appears in any
  donation total, and the wire shapes here carry display facts only — the admission
  credential is the ticket code, random and meaningless outside the tickets table.
*/
/**
 * The general notice a *list* of ticket types carries — and the sentence it replaced.
 *
 * Until 2026-08-23 this constant read: *"Tickets are admission purchases, not donations — this
 * payment is not a tax-deductible charitable contribution."* That was DEC-025's original
 * characterization, and it was reversed. Tickets are deductible in the ordinary quid pro quo
 * way: a buyer who pays $250 for a dinner worth $60 has made a $190 charitable contribution, and
 * the old sentence told them they had made none.
 *
 * What replaces it could not be another blanket claim in either direction, because the answer
 * differs per tier and depends on a number only the organization can supply. So the work split
 * in two: this constant became a *pointer* to the specific statement, and the specific statement
 * is composed per tier and per order from `fairMarketValue` (see `@givehere/domain`'s
 * `quid-pro-quo.ts` and migration 0031).
 *
 * It still lives here for the reason `CONSENT_LABELS` does: it is copy the wire carries and every
 * client repeats, so a second copy anywhere is a second wording waiting to drift. Donor web, the
 * console, and the iOS app render what arrives rather than their own phrasing. Nobody gets to
 * characterize a buyer's tax position on their own authority.
 */
export const ADMISSION_NOTICE = "A ticket purchase can include a charitable contribution — where it does, the amount is shown " +
    "with the ticket type and stated in full on your receipt.";
export const TICKET_TIER_STATUSES = ["on_sale", "hidden", "closed"];
export const ticketTierSchema = z.object({
    id: z.string().uuid(),
    name: z.string().min(1).max(120),
    description: z.string().max(500).nullable(),
    price: moneySchema,
    /**
     * The organization's good-faith estimate, under IRC §6115, of what a buyer receives for the
     * price — the meal, the seat, the goods. `price - fairMarketValue` is the charitable
     * contribution in the purchase, and a tier where the two are equal contains none.
     *
     * On the wire because a buyer is entitled to see the basis for the number, not only the
     * number. A statement that says "$190 of this is deductible" without saying what the other $60
     * was for is the shape of disclosure that satisfies nobody.
     */
    fairMarketValue: moneySchema,
    /**
     * The one-line form of the disclosure, or null where there is nothing to disclose — a free
     * tier, or one worth exactly its price.
     *
     * Null rather than an empty string, and rather than a sentence saying "nothing is deductible",
     * because a tier list is a menu. The retired constant printed under every row including the
     * free ones, which is how a disclosure becomes wallpaper. The full §6115 statement reaches the
     * buyer on the order and the receipt, which is where the statute wants it.
     */
    contributionNote: z.string().nullable(),
    /** null = uncapped. */
    quantityTotal: z.number().int().positive().nullable(),
    /** Remaining sellable count; null when uncapped. Derived server-side, never stored. */
    quantityAvailable: z.number().int().min(0).nullable(),
    /** How many people one ticket admits — 1 for admission, 10 for a table. */
    seatsPerTicket: z.number().int().min(1).max(100).default(1),
    status: z.enum(TICKET_TIER_STATUSES),
});
/** The public pre-event page: what is on sale and where/when the event is. */
export const ticketPageResponseSchema = z.object({
    eventId: z.string().uuid(),
    eventName: z.string(),
    /**
     * The organization's public id, so the name under the event's title can open the
     * organization's own page (GH-806). The same id every public organization URL carries.
     * Nullable and defaulted only so a client reading an older server draws plain text.
     */
    organizationId: z.string().uuid().nullable().default(null),
    organizationDisplayName: z.string(),
    eventStatus: z.string(),
    startsAt: z.string().nullable(),
    endsAt: z.string().nullable(),
    location: z.string().nullable(),
    address: z.string().nullable(),
    /** The hybrid event's online link (GH-866), `https://` only; defaulted for an older server. */
    onlineUrl: z.string().nullable().default(null),
    /** Versioned public hero-image URL, or null when the organizer has not uploaded one. */
    heroImageUrl: z.string().nullable(),
    overview: z.string().nullable(),
    doorsOpenMinutes: z.number().int().nullable(),
    agePolicy: z.enum(AGE_POLICIES).nullable(),
    parking: z.enum(PARKING_OPTIONS).nullable(),
    faqs: z.array(eventFaqSchema),
    lineup: z.array(publicLineupEntrySchema),
    agenda: z.array(eventAgendaItemSchema),
    /** on_sale tiers only — hidden and closed tiers do not exist to the public. */
    tiers: z.array(ticketTierSchema),
    /** True when the organization can take card payments; free tiers work regardless. */
    paidTicketsAvailable: z.boolean(),
    /**
     * The general pointer (`ADMISSION_NOTICE`), server-composed so every client says the same
     * thing. The *specific* disclosure lives on each tier's `contributionNote` and, in full, on
     * the order — a page listing five tiers has five different answers and no single one to give.
     */
    admissionNotice: z.string(),
    /** The Stripe mode the API runs in (GH-823) — see `stripeModeSchema`. */
    stripeMode: stripeModeSchema.default("none"),
    /**
     * GH-884: whether tickets bought here come with GiveHere app invites (`included`, while
     * invite-only membership is on) and whether the checkout may ask for an email per extra ticket
     * (`guestEmails`, only where the guest email's purpose is enabled — otherwise every extra
     * invite goes to the buyer to pass on). Defaulted for an older server: neither.
     */
    appInvites: z
        .object({ included: z.boolean(), guestEmails: z.boolean() })
        .default({ included: false, guestEmails: false }),
});
export const ticketOrderRequestSchema = z.object({
    tierId: z.string().uuid(),
    quantity: z.number().int().min(1).max(10),
    purchaserEmail: z.string().email().max(320),
    purchaserName: z.string().min(1).max(120).optional(),
    /**
     * One-tap reuse of a saved card (GH-330 brought to tickets): requires the donor's session
     * bearer on the request, and the instrument must be a card the donor owns. Never client
     * arithmetic — the server still prices the order and confirms the charge itself.
     */
    savedInstrumentId: z.string().uuid().optional(),
    /**
     * GH-884: an optional email for each ticket after the buyer's own — at most `quantity - 1`,
     * distinct, never the buyer's. Each gets that guest's ticket and app invite in one email, and is
     * then deleted. Only where the ticket page offers it (`appInvites.guestEmails`); refused
     * otherwise. Blank fields are left out by the client.
     */
    guestEmails: z.array(z.string().trim().email().max(320)).max(9).optional(),
});
export const ticketSchema = z.object({
    id: z.string().uuid(),
    /** The admission credential, rendered as a QR at the door. */
    code: z.string().min(16).max(64),
    tierName: z.string(),
    status: z.enum(["issued", "checked_in", "voided"]),
    checkedInAt: z.string().nullable(),
    attendeeName: z.string().nullable(),
});
export const TICKET_ORDER_STATUSES = ["pending", "paid", "failed", "canceled", "refunded"];
/**
 * GH-953: a refund asked for and not yet made — waiting for the organization's second approver
 * (`second_approval`, GH-952) or for approval in the processor's dashboard (`processor_approval`,
 * GH-948). The console shows "Awaiting approval" (or "Awaiting Stripe approval") in place of
 * "Received" / "Paid". A display state only: the gift or order keeps its own status, because no
 * money has moved; nothing donor-facing carries it.
 *
 * Tolerant: absent, null, or a kind this client does not know all read as nothing waiting.
 */
export const REFUND_PENDING_KINDS = ["second_approval", "processor_approval"];
export const refundPendingSchema = z
    .enum(REFUND_PENDING_KINDS)
    .nullable()
    .optional()
    .catch(undefined);
/**
 * GH-884: one ticket's GiveHere app invite, as the buyer's own order shows it.
 *
 *  - `holder`: the buyer's own (ticket 1), a guest the buyer named, or one to pass on.
 *  - `code`: the invite code, or null where the buyer should not need it — a guest's that was
 *    emailed to them, and anything used, voided or expired. Shown only to the order's bearer and
 *    never stored: the server derives it again on each read.
 *  - `guestEmail`: the masked address a guest's went to ("a•••@example.com"), never the address.
 *  - `expiresOn`: the last day it is usable, `YYYY-MM-DD` (UTC).
 */
export const ticketAppInviteSchema = z.object({
    position: z.number().int().min(1).max(10),
    holder: z.enum(["buyer", "guest", "forward"]),
    status: z.enum(["open", "redeemed", "void", "expired"]),
    code: z
        .string()
        .regex(/^[0-9A-HJKMNP-TV-Z]{10}$/)
        .nullable(),
    guestEmail: z.string().max(80).nullable(),
    emailed: z.boolean(),
    expiresOn: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
});
export const ticketOrderResponseSchema = z.object({
    orderId: z.string().uuid(),
    status: z.enum(TICKET_ORDER_STATUSES),
    /** Bearer credential for the order-status page; carried in the confirmation link. */
    accessToken: z.string(),
    /** Present when a card payment must be completed in the browser. */
    clientSecret: z.string().nullable(),
    /**
     * True when a saved-card charge still needs the browser step (3-D Secure): finish it with
     * `clientSecret`. False for ordinary orders — the card form is the browser step.
     */
    requiresAction: z.boolean().default(false),
    amount: moneySchema,
    /**
     * The §6115 good-faith estimate for this whole order, frozen when it was paid (DEC-025).
     *
     * On the wire beside `amount` so every surface can recompose the statement from the two
     * numbers rather than trusting a sentence. The Wallet pass does exactly that: the wording
     * comes from `@givehere/domain`, only the figures come from here, so no caller can put its own
     * characterization of a buyer's tax position on a signed artifact.
     */
    fairMarketValue: moneySchema,
    quantity: z.number().int(),
    tierName: z.string(),
    eventName: z.string(),
    organizationDisplayName: z.string(),
    /** Issued tickets — present once the order is paid (immediately for free tiers). */
    tickets: z.array(ticketSchema),
    /**
     * The full IRC §6115 statement for *this* purchase: the rule, the organization's good-faith
     * estimate of what the buyer received, and the resulting contribution. Composed server-side
     * from the tier's `fairMarketValue` and the quantity ordered.
     *
     * Unlike the page's copy of this field, this one is specific and is the thing the statute
     * actually asks a charity to furnish in writing.
     */
    admissionNotice: z.string(),
    /**
     * GH-884: the app invite each ticket came with, in ticket order. Empty until the order is paid,
     * where invites are off, and always on the create-order answer (that answer is stored for
     * idempotent replay, and a code is never stored) — read the order to see them.
     */
    appInvites: z.array(ticketAppInviteSchema).default([]),
});
/**
 * A signed-in donor's own admission purchases — the in-app ticket wallet.
 *
 * ## Why it carries an access token per order
 *
 * A ticket order's credential has always been the token in the buyer's confirmation email, and
 * every ticket route is authorized by it rather than by a donor session (buying admission needs
 * no account, so most buyers have no session at all). Rather than add a second authorization
 * shape to those routes, this listing **mints a fresh order token for each order it returns** —
 * which the server may do precisely because it has just proved the donor owns the order.
 *
 * The practical effect: a donor who lost the email can still open the pass and the QR, and the
 * routes that serve them did not have to learn about sessions.
 *
 * ## Why it is derived from the order response rather than retyped
 *
 * `.omit()` on the canonical shape, so a contract change breaks this at compile time. Two fields
 * are dropped: `clientSecret` and `requiresAction` are both about *completing a payment*, and
 * every order here is already paid.
 */
export const donorTicketOrderSchema = ticketOrderResponseSchema
    .omit({ clientSecret: true, requiresAction: true })
    .extend({
    eventId: z.string().uuid(),
    /** For sorting and for saying "last March" beside an old ticket. Null when unscheduled. */
    eventStartsAt: z.string().nullable(),
    /**
     * The event's effective status (GH-863), so an undated event that is over is not read as
     * upcoming. Only donor-visible statuses ever appear: the wallet leaves out orders for an
     * archived, suspended, draft or in-review event.
     */
    eventStatus: z.enum(["published", "live", "ended"]),
    purchasedAt: z.string(),
});
export const donorTicketsResponseSchema = z.object({
    orders: z.array(donorTicketOrderSchema),
});
/* ------------------------------------------------------- pledges in the wallet */
/**
 * A promise the donor made, and how much of it they have since funded.
 *
 * ## What this deliberately is not
 *
 * A wallet holds things of value, and a promise is not one. `PledgeService` calls a promise that
 * reads like a payment "the single most dangerous confusion in this product", and putting one
 * inside a wallet is exactly where that confusion would arrive. Three properties push against it:
 *
 *  - **`amount` is what was promised, and `converted` is what has actually been paid.** Two
 *    separate numbers. There is no field here combining them into a balance, and no field
 *    combining a pledge with a ticket, because neither is a sum that means anything.
 *  - **`state` is the server's**, in the domain's own vocabulary, so a client cannot invent a
 *    friendlier word for a promise that is still owed.
 *  - **No receipt, and no receipt number.** A pledge is not a gift and has no tax character. The
 *    receipt appears only when a donation funds it, and it belongs to that donation.
 */
export const donorPledgeSchema = z.object({
    pledgeId: z.string().uuid(),
    organizationId: z.string().uuid(),
    organizationDisplayName: z.string(),
    eventId: z.string().uuid(),
    eventName: z.string(),
    /** Promised. Never a balance, and never added to anything. */
    amount: moneySchema,
    /** Settled money already credited against it. Zero for an untouched promise. */
    converted: moneySchema,
    /** What is still owed: `amount - converted`, computed server-side. */
    outstanding: moneySchema,
    state: z.enum(["active", "partially_converted", "fulfilled"]),
    promisedAt: z.string(),
});
export const donorPledgesResponseSchema = z.object({
    pledges: z.array(donorPledgeSchema),
});
/* Staff shapes: the tier list with sold counts, and the attendee roll. */
export const ticketTierAdminSchema = ticketTierSchema.extend({
    sold: z.number().int().min(0),
});
/* ------------------------------ organization interest (DEC-042, recruitment) */
/**
 * What somebody says they would give to an organization that has not claimed its listing.
 *
 * **Not a pledge and not a donation.** No money is authorized, accepted or held — DEC-042 refused
 * all three — and the wire carries no payment field of any kind, deliberately, so no client can
 * offer one.
 */
export const stateInterestSchema = z.object({
    organizationId: z.string().uuid(),
    amountMinor: minorUnitsSchema,
    currency: currencySchema.default("USD"),
    /**
     * Optional, and optional on purpose: a person may say what they would give without leaving any
     * way to reach them. Supplying one lets them withdraw it later and stops them being counted
     * twice; the server stores a keyed fingerprint and never the address itself.
     */
    contact: z.string().min(3).max(320).optional(),
});
/**
 * The aggregate, as a donor surface and a recruitment message both see it.
 *
 * A **count and a sum of intentions**. It is never a balance: the sentence the server supplies is
 * conditional in both branches, and the client renders that sentence rather than composing its
 * own, because the distance between "would give" and "has given" is one careless verb.
 */
export const interestSummarySchema = z.object({
    count: z.number().int().nonnegative(),
    total: moneySchema,
    /** Server-composed. Clients render this; they do not build their own from count and total. */
    sentence: z.string(),
    /**
     * The same fact in five words or fewer, for the line that stays on screen.
     *
     * Server-composed for the same reason `sentence` is, and *more* so: this is the half a client
     * would be tempted to build itself out of `count` and `total`, and the version it would build
     * ("14 people · $4,200") is exactly the one that drops the conditional and reads as money that
     * arrived. `describeInterestShort` keeps *would*. A surface rendering this must also offer the
     * full `sentence` — one tap away is fine; unreachable is not.
     */
    headline: z.string(),
    /** True when this caller already has a live statement, so the surface offers to change it. */
    viewerHasStated: z.boolean().default(false),
});
/* ------------------------------------------------- admin: designations (DEC-043) */
/** A designation as the console sees it — everything the donor surface gets, plus the operator's half. */
export const adminDesignationSchema = designationSchema.extend({
    templateKey: z.string().nullable(),
    sortOrder: z.number().int(),
    archivedAt: z.string().nullable(),
    /**
     * Money received against this designation to date, gross.
     *
     * A **received figure, not a balance.** Nothing here is an account and nothing is available to
     * grant: a designation records what a completed gift was restricted to. Naming it a balance
     * would be the presentation DEC-043 exists to avoid.
     */
    receivedToDate: moneySchema,
});
export const createDesignationSchema = z.object({
    name: z.string().min(1).max(60),
    description: z.string().max(240).nullable().optional(),
    /**
     * Which library entry this started from, or absent for free-form.
     *
     * Stored for aggregate telemetry — which templates earn their place — and for nothing else. It
     * never crosses a tenant boundary and never shapes what another organization is offered, because
     * choosing what to surface from something inferred about a tenant is the inference `CLAUDE.md`
     * forbids.
     */
    templateKey: z
        .string()
        .regex(/^[a-z][a-z0-9_]{1,39}$/, "templateKey must be a lower-case library key")
        .nullable()
        .optional(),
});
/**
 * Renaming does **not** rewrite receipts already issued — `receipts.designation_name` is a
 * snapshot, on purpose, so a later edit cannot restate what a past donor gave to. The console says
 * so in a sentence, because an administrator will otherwise assume the opposite.
 */
export const updateDesignationSchema = z.object({
    name: z.string().min(1).max(60).optional(),
    description: z.string().max(240).nullable().optional(),
});
/** The whole active order, in one transaction. A partial reorder is two designations at rank 3. */
export const reorderDesignationsSchema = z.object({
    designationIds: z.array(z.string().uuid()).min(1).max(100),
});
/** A name and nothing else. No description carrying religious law — the organization writes its own. */
export const designationTemplateSchema = z.object({
    key: z.string(),
    name: z.string(),
});
export const createTicketTierSchema = z.object({
    name: z.string().min(1).max(120),
    description: z.string().max(500).optional(),
    priceMinor: z.string().regex(/^\d{1,10}$/, "priceMinor must be a non-negative integer string"),
    /**
     * The organizer's IRC §6115 good-faith estimate of what a buyer receives (DEC-025).
     *
     * Optional, and omitting it means "worth what it costs" — no deduction claimed for anybody.
     * That is the only safe default: the server cannot estimate the value of a dinner it did not
     * cater, and a guess here would end up on a receipt as a number the charity vouched for.
     */
    fairMarketValueMinor: z
        .string()
        .regex(/^\d{1,10}$/, "fairMarketValueMinor must be a non-negative integer string")
        .optional(),
    quantityTotal: z.number().int().positive().max(100000).nullable().optional(),
    seatsPerTicket: z.number().int().min(1).max(100).optional(),
});
/** Revising an existing tier's estimate — the edit organizations actually make (DEC-025). */
export const setTicketTierFairMarketValueSchema = z.object({
    fairMarketValueMinor: z
        .string()
        .regex(/^\d{1,10}$/, "fairMarketValueMinor must be a non-negative integer string"),
});
export const attendeeOrderSchema = z.object({
    orderId: z.string().uuid(),
    status: z.enum(TICKET_ORDER_STATUSES),
    tierName: z.string(),
    quantity: z.number().int(),
    amount: moneySchema,
    purchaserName: z.string().nullable(),
    purchaserEmail: z.string(),
    createdAt: z.string(),
    tickets: z.array(ticketSchema),
    /** GH-953: a refund of this order is waiting; the order is still paid. */
    refundPending: refundPendingSchema,
});
/**
 * `GET /v1/events/:eventId/attendees`. Every count is the server's and is defined once, here
 * (GH-818 audit — `docs/evidence/GH-818.md`):
 *
 *   - `orders`: paid and refunded orders for this event, newest first. Pending, failed and
 *     canceled checkouts are payment attempts, not orders, and are not listed.
 *   - `ticketCount`: tickets on this event that still admit or have admitted — every ticket not
 *     voided. A refund or a lost chargeback voids a ticket and takes it out.
 *   - `ticketCheckedInCount`: tickets scanned at the door.
 *   - `roomCheckIns`: donors checked in through the app, checked out ones gone.
 *   - `checkedInCount`: scanned tickets plus app check-ins of donors with no scanned ticket of
 *     their own — one person, once.
 *   - `invitedCount`: invitations that went out (`sent`). A recorded invitation that messaging
 *     suppressed, or one that failed, is in `invites` with its status and not in the count.
 */
export const attendeesResponseSchema = z.object({
    orders: z.array(attendeeOrderSchema),
    /** Invitations that were actually sent. */
    invitedCount: z.number().int().nonnegative(),
    invites: z.array(z.object({
        email: z.string(),
        status: z.enum(["recorded", "sent", "failed"]),
        createdAt: z.string(),
    })),
    /** Issued (non-voided) tickets across paid orders. */
    ticketCount: z.number().int(),
    /**
     * Everybody through the door, however they arrived (GH-818): tickets scanned, plus donors checked
     * in to the room through the app who have no scanned ticket of their own, so nobody counts twice.
     */
    checkedInCount: z.number().int(),
    /** Tickets scanned at the door — what `checkedInCount` alone meant before GH-818. */
    ticketCheckedInCount: z.number().int().default(0),
    /**
     * Donors checked in to the room through the app ("You're here" — taking part checks a donor
     * in). Checking out removes them. `count` is everyone; `named` lists only donors who set a
     * display name and gave no anonymous gift at this event — no donor id, no contact, nothing to
     * join on. The rest are in `count` and nowhere else.
     */
    roomCheckIns: z
        .object({
        count: z.number().int().nonnegative(),
        named: z.array(z.object({
            displayName: z.string(),
            // DEC-049. Optional so clients and fixtures built before GH-857 still parse.
            publiclyAnonymous: z.boolean().optional(),
            checkedInAt: z.string(),
        })),
    })
        .default({ count: 0, named: [] }),
    /**
     * GH-951 (DEC-058): false when the organization turned refunds off; the console then says so in
     * place of the Refund button. The server refuses the refund either way. Optional — absent reads
     * as on — so older responses and clients (the iOS attendee list offers no refunds) are unaffected.
     */
    refundsEnabled: z.boolean().optional(),
});
/* ------------------------------------------- tickets & check-in console (GH-830) */
/**
 * `PATCH /v1/events/:eventId/ticket-tiers/:tierId` — edit a tier. Every field optional, at least
 * one required. Visibility and sales ("Hidden", "Close sales") stay on the status route.
 *
 * Nothing already sold moves: an order froze its price, §6115 estimate and seat count when it was
 * placed. `quantityTotal` cannot go below what is already committed (paid plus checkouts in
 * progress); `null` removes the cap.
 *
 * The estimate follows a price change only where it was the "worth what it costs" default (equal
 * to the old price) — so a price edit never invents a deductible contribution nobody estimated. An
 * explicit estimate above a new, lower price is refused; revise it first on its own route.
 */
export const updateTicketTierSchema = z
    .object({
    name: z.string().trim().min(1).max(120).optional(),
    description: z.string().max(500).nullable().optional(),
    priceMinor: z
        .string()
        .regex(/^\d{1,10}$/, "priceMinor must be a non-negative integer string")
        .optional(),
    seatsPerTicket: z.number().int().min(1).max(100).optional(),
    quantityTotal: z.number().int().positive().max(100000).nullable().optional(),
})
    .strict()
    .refine((patch) => Object.values(patch).some((value) => value !== undefined), {
    message: "Change at least one field.",
});
/**
 * `GET /v1/events/:eventId/ticketing-summary` — the four figures across the top of "Tickets &
 * check-in" and the door's "n / m inside".
 *
 *   - `seats.sold`: seats on paid orders' tickets that are not voided, at each order's own seat
 *     count (a table of ten is ten). `seats.capacity`: Σ cap × seats over tiers with a cap, null
 *     when no tier has one; `uncappedTierCount` says how many tiers sit outside it.
 *   - `tickets.sold`: the same tickets, counted as tickets.
 *   - `checkedIn`: GH-818's definitions unchanged — `count` is scanned tickets plus app check-ins
 *     of donors with no scanned ticket of their own (one person, once), `ticketScans` the scans
 *     alone, `roomOnly` the app check-ins not already counted. `seats` is scanned tickets at their
 *     seat count plus `roomOnly`, the number to put over `seats.sold`.
 *   - `admissionsRevenue`: paid orders, less any whose chargeback was lost. **Tickets are not
 *     donations** (DEC-016): this is never part of an event's raised total or goal, and nothing
 *     here is in any donation figure.
 *   - `invites.sent`: invitations that went out (as `invitedCount` on the attendees route);
 *     `bounced`: invited addresses now on the email suppression list for a bounce (GH-811);
 *     `failed`: sends that errored.
 */
export const ticketingSummarySchema = z.object({
    seats: z.object({
        sold: z.number().int().nonnegative(),
        capacity: z.number().int().nonnegative().nullable(),
        uncappedTierCount: z.number().int().nonnegative(),
    }),
    tickets: z.object({ sold: z.number().int().nonnegative() }),
    checkedIn: z.object({
        count: z.number().int().nonnegative(),
        ticketScans: z.number().int().nonnegative(),
        roomOnly: z.number().int().nonnegative(),
        seats: z.number().int().nonnegative(),
    }),
    admissionsRevenue: moneySchema,
    invites: z.object({
        sent: z.number().int().nonnegative(),
        bounced: z.number().int().nonnegative(),
        failed: z.number().int().nonnegative(),
    }),
});
/**
 * Statuses the orders list may be filtered to. No filter means paid and refunded (GH-818).
 * `awaiting_approval` (GH-953) is paid orders with a refund waiting for a second approver; `paid`
 * still includes them, because their money has not moved.
 */
export const TICKET_ORDER_LIST_STATUSES = [
    "paid",
    "refunded",
    "pending",
    "awaiting_approval",
];
/**
 * `GET /v1/events/:eventId/ticket-orders` and `…/ticket-orders.csv` query. `q` matches the
 * purchaser's name or email anywhere, or (4+ characters) the start of a ticket code on the order.
 */
export const ticketOrdersQuerySchema = z.object({
    q: z.string().trim().max(120).optional(),
    status: z.enum(TICKET_ORDER_LIST_STATUSES).optional(),
    limit: z.coerce.number().int().min(1).max(500).default(200),
});
/** One row of the console's orders table. No ticket codes: the list is for finding a buyer. */
export const consoleTicketOrderSchema = z.object({
    orderId: z.string().uuid(),
    status: z.enum(TICKET_ORDER_STATUSES),
    tierName: z.string(),
    quantity: z.number().int(),
    /** The seat count frozen on the order when it was placed. */
    seatsPerTicket: z.number().int().min(1),
    amount: moneySchema,
    purchaserName: z.string().nullable(),
    purchaserEmail: z.string(),
    createdAt: z.string(),
    /** "x of y": tickets admitted, of tickets not voided. A refunded order is 0 of 0. */
    checkedInCount: z.number().int().nonnegative(),
    ticketCount: z.number().int().nonnegative(),
    /** GH-884: app invites the order's tickets came with — that they went out, never the codes. */
    appInvitesIssued: z.number().int().nonnegative().default(0),
    /** GH-884: of those, the ones emailed to a guest the buyer named. */
    appInvitesEmailed: z.number().int().nonnegative().default(0),
    /** GH-953: a refund of this order is waiting for a second approver; the order is still paid. */
    refundPending: refundPendingSchema,
});
export const ticketOrdersResponseSchema = z.object({
    orders: z.array(consoleTicketOrderSchema),
    /** True when more orders matched than `limit`; narrow the search or export the CSV. */
    hasMore: z.boolean(),
    /**
     * GH-951 (DEC-058): false when the organization turned refunds off, so the console says so in
     * place of the Refund button. The server refuses the refund either way. Optional; absent reads
     * as on, so an older response still parses.
     */
    refundsEnabled: z.boolean().optional(),
});
/**
 * The orders CSV's header row, in order. The organization's own buyer list: name and email the
 * buyer gave, never a card detail (none is held), never a ticket code (a door credential).
 */
export const TICKET_ORDERS_CSV_COLUMNS = [
    "purchaser_name",
    "purchaser_email",
    "ticket_type",
    "tickets",
    "status",
    "amount",
    "currency",
    "checked_in",
    "ordered_at_utc",
];
/**
 * `POST /v1/events/:eventId/door/admit` — the camera's QR payload or the typed code. The ticket QR
 * on the phone, in the email and on the Wallet pass all encode the bare code; surrounding
 * whitespace is ignored.
 */
export const doorAdmitRequestSchema = z.object({ code: z.string().min(1).max(200) });
export const DOOR_OUTCOMES = ["admitted", "already_used", "voided", "not_found"];
/** What the door screen shows about the ticket a code resolved to. */
export const doorAdmissionSchema = z.object({
    /** The name on the ticket, else the name the buyer gave; null when none was given. */
    name: z.string().nullable(),
    tierName: z.string(),
    seatsPerTicket: z.number().int().min(1),
    /** First admission time (ISO). On `already_used`, the "already used at HH:MM" time. */
    admittedAt: z.string().nullable(),
    /** "n of m on this order are in": tickets admitted, of tickets not voided. */
    orderAdmitted: z.number().int().nonnegative(),
    orderTotal: z.number().int().nonnegative(),
});
/**
 * The door's answer, always 200 for a code read in this event's scope. `outcome`:
 *   - `admitted`: let them in. Scanning the same code again answers `already_used`.
 *   - `already_used`: refused; `admission.admittedAt` is when it was first used.
 *   - `voided`: refused; refunded or charged back.
 *   - `not_found`: refused; not a ticket of this event (another event's or organization's code
 *     answers this too, and learns nothing more). `admission` is null.
 * `message` is a short server sentence for screens that show text alone; the time is left to the
 * device, which is at the venue and knows its local clock.
 */
export const doorAdmitResponseSchema = z.object({
    outcome: z.enum(DOOR_OUTCOMES),
    admitted: z.boolean(),
    message: z.string(),
    scannedAt: z.string(),
    admission: doorAdmissionSchema.nullable(),
});
/** `GET /v1/events/:eventId/door/recent?limit=N` — the door's last scans, refusals flagged. */
export const doorRecentResponseSchema = z.object({
    entries: z.array(z.object({
        scannedAt: z.string(),
        outcome: z.enum(DOOR_OUTCOMES),
        refused: z.boolean(),
        /** At most the code's first four characters, for a refusal row ("3HD9-…"). */
        codeHint: z.string().nullable(),
        admission: doorAdmissionSchema.nullable(),
    })),
});
/* --------------------------------------- platform defaults + directory import */
/**
 * Platform-managed default ticket types (owner-decided 2026-08-18): every new event starts
 * with these tiers already on sale, so an organizer types nothing to sell standard seats.
 */
export const defaultTicketTypeSchema = z.object({
    name: z.string().min(1).max(120),
    priceMinor: z.string().regex(/^\d{1,10}$/, "priceMinor must be a non-negative integer string"),
    seatsPerTicket: z.number().int().min(1).max(100),
});
export const defaultTicketTypesSchema = z.object({
    defaults: z.array(defaultTicketTypeSchema).max(10),
});
/**
 * The platform fee schedule: a percentage plus a fixed amount per gift (owner decision
 * 2026-10-02, GH-839; replaces the flat rate and $20 minimum of DEC-030).
 *
 * Both numbers travel as decimal strings for the reason all money on this wire does: they are
 * integers JSON would happily hand back as floats.
 *
 * The bounds are not decoration, and they are the server's as well as the form's. A rate is one
 * keystroke from ten times itself — 290 and 2900 differ by a character. 10% and 50¢ together keep
 * the fee below every gift the platform accepts (the floor is a fixed $1.00): the largest permitted
 * fee on a $1 gift is 60¢. There is no minimum-gift field any more.
 */
export const platformFeeSchema = z
    .object({
    /** Basis points. 290 = 2.9%. Capped at 10%; above that, stop and talk to counsel. */
    rateBps: z
        .string()
        .regex(/^\d{1,5}$/, "rateBps must be a non-negative integer string")
        .refine((value) => Number(value) <= 1000, "A platform fee above 10% needs a decision, not a form"),
    /** Minor units added to every gift's fee. 30 = $0.30. Capped at 50¢. */
    fixedMinor: z
        .string()
        .regex(/^\d{1,5}$/, "fixedMinor must be a non-negative integer string")
        .refine((value) => Number(value) <= 50, "A fixed part above $0.50 would let the fee approach a $1 gift"),
})
    .strict();
/* ------------------------------------------------- sanctions screening (DEC-029) */
/**
 * One candidate on a screening: a designated name the subject's name is close to.
 *
 * `sharedWords` is on the wire because a reviewer must never be asked to trust a verdict. "These
 * two names share *rahma* and *foundation*" is a fact somebody can weigh and write down; a
 * similarity score is not, and a reviewer who cannot say why they cleared a case has not really
 * cleared it.
 */
export const sanctionsMatchSchema = z.object({
    listedName: z.string(),
    listName: z.string(),
    /** OFAC's own identifier, so a reviewer can check the entry at the source. */
    listEntryId: z.string(),
    strength: z.enum(["exact", "same_words", "partial"]),
    sharedWords: z.array(z.string()),
});
export const SANCTIONS_OUTCOMES = [
    "clear",
    "review_required",
    "cleared_by_review",
    "confirmed_match",
];
/**
 * Everything the platform knows about the organization behind a case, for the reviewer.
 *
 * A sanctions case is produced by comparing two *names*, so a name is the one piece of evidence
 * that cannot settle it. What settles it is the identifiers a name does not carry — an EIN that
 * resolves to an IRS listing, a street address on the wrong continent from the designation, a
 * registry name from an incorporation two decades before the listing, a source citation for how
 * the record got here at all. Those are all already stored; until now none of them reached the
 * screen where the decision is made, so a reviewer either opened four other views or decided on
 * the name. This block exists so that the second option stops being the easy one.
 *
 * Every field is nullable and most are usually null: the directory is imported in bulk and a
 * typical unclaimed record has a name and a city. A thin dossier is itself a finding — it means
 * there is little to distinguish this organization from the listed one, which is a reason to look
 * harder rather than a reason to clear.
 */
export const sanctionsSubjectSchema = z.object({
    organizationId: z.string().uuid(),
    legalName: z.string(),
    displayName: z.string(),
    claimStatus: z.string(),
    description: z.string().nullable(),
    /** Nine digits, unformatted. The console renders the hyphen and offers it for copying. */
    ein: z.string().nullable(),
    websiteUrl: z.string().nullable(),
    addressLine1: z.string().nullable(),
    city: z.string().nullable(),
    region: z.string().nullable(),
    postalCode: z.string().nullable(),
    country: z.string().nullable(),
    phone: z.string().nullable(),
    /** When a reviewer confirmed the EIN against the IRS listing, and the registry's own spelling. */
    irsVerifiedAt: z.string().nullable(),
    irsRegistryLegalName: z.string().nullable(),
    candidProfileUrl: z.string().nullable(),
    candidSealLevel: z.string().nullable(),
    candidVerifiedAt: z.string().nullable(),
    directorySourceName: z.string().nullable(),
    sourceRecordId: z.string().nullable(),
    sourceNote: z.string().nullable(),
    createdAt: z.string(),
    archivedAt: z.string().nullable(),
    paymentsEnabled: z.boolean(),
});
export const sanctionsScreeningSchema = z.object({
    id: z.string().uuid(),
    subjectType: z.enum(["organization", "person"]),
    subjectId: z.string().uuid().nullable(),
    /** The organization's display name, resolved for the queue so a reviewer sees who this is. */
    subjectDisplayName: z.string().nullable(),
    /** The name as screened, verbatim. An organization renames itself; a screening does not. */
    screenedName: z.string(),
    outcome: z.enum(SANCTIONS_OUTCOMES),
    /**
     * Which publication of the list this was screened against.
     *
     * The field that turns a row into evidence. "Clear" is a claim about a moment — an organization
     * clear yesterday can be designated today — and a screening with no list version attached
     * cannot tell anybody what it was clear *against*.
     */
    listPublishedAt: z.string(),
    screenedAt: z.string(),
    reviewedByName: z.string().nullable(),
    reviewedAt: z.string().nullable(),
    reviewNote: z.string().nullable(),
    /**
     * The organization the case is about, or null when the record no longer exists.
     *
     * On a *person* case this is the organization the person's screening can hold, not the
     * person — the platform stores no dossier on a natural person beyond the name that was
     * screened, and a sanctions review is not a licence to start building one.
     */
    subject: sanctionsSubjectSchema.nullable(),
    matches: z.array(sanctionsMatchSchema),
});
export const sanctionsQueueResponseSchema = z.object({
    /** Waiting on a person, oldest first — the one that has waited longest is the one to do. */
    open: z.array(sanctionsScreeningSchema),
    /** Recently decided, so the screen shows what review actually did rather than only a backlog. */
    recent: z.array(sanctionsScreeningSchema),
    /**
     * When the stored list was last refreshed, or null on a deployment that has never loaded one.
     *
     * Surfaced prominently because a stale list is the silent failure mode of this whole control:
     * everything screens clear against a list from six months ago, and nothing looks wrong.
     */
    listPublishedAt: z.string().nullable(),
    /** How many organizations are currently held from payout by an undecided screening. */
    heldOrganizations: z.number().int().min(0),
});
/**
 * A reviewer's decision.
 *
 * The note is required and has a floor, because it is the field an examiner reads. "Different
 * organization" is not a reason; "listed entity is a Somali money-transfer business designated
 * under SDGT, subject is a Michigan organization incorporated 1998, no common officers" is. A ten-
 * character minimum does not make somebody write the second one, but it stops the empty string
 * from being the path of least resistance.
 */
export const sanctionsDecisionSchema = z.object({
    decision: z.enum(["cleared_by_review", "confirmed_match"]),
    note: z.string().min(10).max(2000),
});
/* --------------------------------------------- platform users (global access view) */
/**
 * One person's access across the whole platform.
 *
 * Distinct from the per-organization team list, which answers "who is on my team". This answers
 * "who can reach this platform, where, and is their address actually theirs" — a question that
 * had no screen before 2026-08-25 and no answer short of a database query.
 */
export const platformUserMembershipSchema = z.object({
    organizationId: z.string().uuid(),
    organizationName: z.string(),
    role: z.string(),
});
export const platformUserSchema = z.object({
    id: z.string().uuid(),
    email: z.string(),
    displayName: z.string(),
    /** Cross-tenant authority. The one flag that grants anything outside an organization. */
    isPlatformStaff: z.boolean(),
    emailVerifiedAt: z.string().nullable(),
    /**
     * How the address was proved: `oidc` is an identity provider's verified claim, `challenge` is
     * a code we mailed and they returned. Reported rather than flattened into a tick, because the
     * two are not equally strong and a reviewer looking at who can move money should see which.
     */
    emailVerifiedVia: z.enum(["oidc", "challenge"]).nullable(),
    createdAt: z.string(),
    memberships: z.array(platformUserMembershipSchema),
});
export const platformUsersResponseSchema = z.object({ users: z.array(platformUserSchema) });
/**
 * Granting or removing cross-tenant platform access (owner-directed 2026-09-01).
 *
 * A bare boolean, and the account is named in the path rather than the body — an id in a URL is
 * what the audit log, the access log and the route all agree about, where an id in a body is one
 * more thing that can disagree with the path it arrived on.
 *
 * No `reason` field. Impersonation demands one (`impersonateRequestSchema`) because it is an act
 * taken *as* somebody, invisible in what follows, and the reason is the only record of why. This
 * is a durable state change on a row anyone can see, in a list built to be read — the grant is its
 * own evidence, and a mandatory free-text box on it collects "access" a hundred times.
 */
export const platformAccessUpdateSchema = z.object({ granted: z.boolean() });
/**
 * The account as it now stands, so the console re-renders one row rather than re-fetching all.
 *
 * Shared by all three writes on this screen — create, rename, access — rather than one shape each.
 * They all answer the same question ("what does this row look like now?"), and three schemas that
 * must stay identical are three chances for two of them to drift.
 */
export const platformUserResponseSchema = z.object({ user: platformUserSchema });
/**
 * Adding a person who is not in any organization yet (owner-directed 2026-09-02).
 *
 * Until now the only way to bring an account into existence was to grant it a role in some
 * organization, which is the wrong shape for the case that prompted this: a new **platform**
 * administrator belongs to no organization at all, so the owner had to run a database CLI to
 * create one. This creates the account and nothing else — no membership, no platform access.
 *
 * That separation is the point rather than an omission. `/access` still refuses to conjure an
 * account, so no single request both invents a person and hands them reach; making somebody a
 * platform administrator remains two deliberate acts, the second of them step-up gated.
 *
 * `.email()` where the membership grant next door settles for `min(3)`: this route mints a durable
 * identity, and an address that cannot receive mail is an account that can never sign in or
 * confirm itself.
 */
export const platformUserCreateSchema = z.object({
    email: z.string().email().max(254),
    displayName: z.string().min(1).max(120),
});
/**
 * Correcting the name on an account.
 *
 * Not cosmetic. An account created by a membership grant with no name given takes the local part
 * of its address — `ab@bedier.com` becomes "ab" — and that string is what every members list, every
 * audit reader, and this screen shows from then on. There was no way to fix it anywhere in the
 * product.
 *
 * Deliberately cannot change the address. The address is the identity: it is the sign-in key, the
 * unique index, and what an identity provider asserts. A route that could rewrite it would let one
 * console session point an existing account's history at a different person.
 */
export const platformUserRenameSchema = z.object({
    displayName: z.string().min(1).max(120),
});
/**
 * Correcting the address an account signs in with (owner-directed 2026-09-05).
 *
 * Same shape as creation's `email`, deliberately: an address that could not have been created is
 * not one anything should be able to change *to*. The consequences of applying it are severe
 * enough to be documented where they happen — see `changeStaffUserEmail`, which clears the
 * verification alongside the write, because the proof belonged to the old mailbox.
 */
export const platformUserEmailSchema = z.object({
    email: z.string().email().max(254),
});
/**
 * The result of confirming an address from the link in a verification message.
 *
 * Deliberately says nothing about the account beyond the address it just proved: the confirm route
 * is unauthenticated by construction — whoever opens the link holds the mailbox and nothing else —
 * so a payload naming the person's roles, their organizations, or even their display name would
 * hand every one of those facts to anyone who came across the URL.
 */
export const staffEmailConfirmationSchema = z.object({
    email: z.string().email(),
    confirmedAt: z.string(),
});
/**
 * The org-payouts switch, and why it might refuse.
 *
 * `blockingDecisions` travels with the current state so the console can explain the refusal
 * *before* the operator presses anything, rather than after.
 */
export const orgPayoutsResponseSchema = z.object({
    enabled: z.boolean(),
    blockingDecisions: z.array(z.string()),
});
export const orgPayoutsSchema = z.object({ enabled: z.boolean() });
/* ------------------------------------------------------ decisions (owner console) */
/**
 * One open question, as the console shows it.
 *
 * Generated from `docs/decisions-needed.md` and `docs/decision-register.md` — never hand-written
 * — so the question the owner answers on screen is the question the documents ask.
 * `packages/contracts/src/decision-catalogue.test.ts` fails if the two drift.
 */
export const decisionCatalogueEntrySchema = z.object({
    id: z.string().regex(/^DEC-\d{3}$/),
    title: z.string(),
    status: z.string(),
    summary: z.string(),
    owner: z.string().nullable(),
    /** What has been assumed while the question is open. Answering it replaces this. */
    workingAssumption: z.string().nullable(),
    gate: z.string().nullable(),
    /** Who this needs, inferred from the register's own wording rather than a hand-kept list. */
    audience: z.enum(["owner", "counsel", "cpa", "counsel_and_cpa"]),
    open: z.boolean(),
});
export const DECISION_DISPOSITIONS = ["answered", "provisional", "needs_professional"];
export const decisionAnswerSchema = z.object({
    decisionId: z.string(),
    answer: z.string(),
    disposition: z.enum(DECISION_DISPOSITIONS),
    advisedBy: z.string().nullable(),
    answeredByName: z.string().nullable(),
    answeredAt: z.string(),
});
export const decisionsResponseSchema = z.object({
    decisions: z.array(decisionCatalogueEntrySchema),
    answers: z.array(decisionAnswerSchema),
    /**
     * Decisions that currently block the org-payouts switch and have no usable answer.
     *
     * Surfaced here so the decisions screen can say *why* a switch elsewhere is refusing, rather
     * than leaving an operator to discover it by pressing the switch and reading an error.
     */
    blockingOrgPayouts: z.array(z.string()),
});
/**
 * Recording an answer.
 *
 * The floor on `answer` is the same argument as the sanctions review note: this is the entire
 * record of a position that money and legal exposure will rest on, and an empty string must not
 * be the path of least resistance. `needs_professional` is a real answer — "I looked and cannot
 * decide this without a lawyer" is information, and it is deliberately *not* enough to clear a
 * blocker.
 */
export const recordDecisionAnswerSchema = z.object({
    answer: z.string().min(10).max(20000),
    disposition: z.enum(DECISION_DISPOSITIONS),
    advisedBy: z.string().max(200).nullable().default(null),
});
/**
 * Bulk directory upsert (platform staff only): how the claimable-organization candidate list gets
 * in. Idempotent by (source, sourceRecordId) — re-importing updates, never duplicates — and
 * every record lands `unclaimed`, so nothing imported can accept a donation.
 */
export const directoryImportRecordSchema = z.object({
    sourceRecordId: z.string().min(1).max(80),
    legalName: z.string().min(1).max(300),
    displayName: z.string().min(1).max(300),
    addressLine1: z.string().max(300).nullable().default(null),
    city: z.string().max(120).nullable().default(null),
    region: z.string().max(60).nullable().default(null),
    postalCode: z.string().max(20).nullable().default(null),
    ein: z
        .string()
        .regex(/^\d{9}$/)
        .nullable()
        .default(null),
    latitude: z.number().min(-90).max(90).nullable().default(null),
    longitude: z.number().min(-180).max(180).nullable().default(null),
    sourceNote: z.string().max(500).nullable().default(null),
});
export const directoryImportSchema = z.object({
    sourceName: z.string().min(1).max(160),
    sourceUrl: z.string().url().max(500).nullable().default(null),
    records: z.array(directoryImportRecordSchema).min(1).max(500),
});
export const directoryImportResponseSchema = z.object({
    inserted: z.number().int(),
    updated: z.number().int(),
    /** Candidates whose EIN an existing record already holds — already represented, not added. */
    skipped: z.number().int(),
});
/* ==================================================== accounting and money out */
/**
 * Money that may be negative, as a decimal string.
 *
 * `moneySchema` is unsigned because almost everything on this wire is an amount rather than a
 * change — a gift, a fee, a goal. A ledger row's effect on a balance is a *change*, and refusing to
 * represent its sign would force the client to infer direction from the transaction kind. Inferring
 * a sign is how a payout ends up displayed as income.
 */
export const signedMinorUnitsSchema = z
    .string()
    .regex(/^-?\d+$/, "amount must be a whole number of minor units, optionally negative");
export const signedMoneySchema = z.object({
    amountMinor: signedMinorUnitsSchema,
    currency: currencySchema,
});
/**
 * The three figures an organization sees, and the reason there are three.
 *
 * A single "balance" would be wrong for two of them in ways that cost real money — pay out a gift
 * before its ACH return window closes and the platform funds the return itself; let envelope cash
 * reach the payout balance and an organization is sent money it already has in a cash box. The wire shape
 * makes the distinction impossible to collapse by accident: three named fields, not one number and
 * two footnotes.
 */
export const organizationBalanceSchema = z.object({
    /** `organization_payable` net of gifts still inside their return window. Payable today. */
    available: moneySchema,
    /** Confirmed and not yet safe to send. Becomes available as return windows close. */
    pending: moneySchema,
    /**
     * Offline gifts. **Never** part of `available` — the organization already holds this cash.
     * Present so the screen can show the whole picture without the payout figure absorbing it.
     */
    recordedElsewhere: moneySchema,
});
export const LEDGER_TRANSACTION_KINDS = [
    "gift",
    "offline",
    "settlement",
    "payout",
    "dispute",
    "reversal",
    // Postage for a mailed payment request (GH-846).
    "postage",
    "adjustment",
];
export const accountingTransactionSchema = z.object({
    /** Every row carries this. When an accountant asks "what is this line", there is one answer. */
    transactionId: z.string().uuid(),
    occurredAt: z.string(),
    kind: z.enum(LEDGER_TRANSACTION_KINDS),
    memo: z.string(),
    eventName: z.string().nullable(),
    /**
     * The donor's name as the organization may see it (DEC-049), or null where there is none to show.
     *
     * Decided in SQL (`accounting.listTransactions`, through the identity view), never by a
     * formatter: a donor anonymous to all organizations is never selected and reads `donorHidden`.
     */
    donorDisplayName: z.string().nullable(),
    /** The donor asked not to be named in public — the console's badge. */
    donorPubliclyAnonymous: z.boolean(),
    /** The donor is anonymous to all organizations: the console shows "Anonymous donor". */
    donorHidden: z.boolean(),
    /** What this transaction did to the organization's balance. Negative for money going out. */
    payableDelta: signedMoneySchema,
    /**
     * Set when this row reverses an earlier one.
     *
     * Reversals appear as their own rows and never as an edited original. That will look odd to an
     * accountant expecting a corrected figure, so the pairing is on the wire and the console says it
     * in a sentence.
     */
    reversesTransactionId: z.string().uuid().nullable(),
});
export const accountingTransactionsResponseSchema = z.object({
    transactions: z.array(accountingTransactionSchema),
    /**
     * Opaque keyset cursor, or null at the end.
     *
     * Opaque rather than "page 3" because money lists get appended to while somebody reads them, and
     * offset paging silently duplicates and skips rows when that happens.
     */
    nextCursor: z.string().nullable(),
});
/**
 * One gift, opened from its row in the console's transaction list (GH-793).
 *
 * The **only** response in the API that carries a donor's note. It is a staff read behind
 * `reconciliation.view` — the permission the list itself needs — and it is keyed by the ledger
 * transaction the console already shows, so it adds no identifier to the list and no route a
 * donor-facing surface could reach.
 *
 * The donor is shown exactly as in the list (DEC-049), from the same identity view. The note is the
 * donor's own words to staff and is shown whatever their choices; a donor who hid from
 * organizations and then signed their note has told the organization their name themselves.
 */
export const giftDetailResponseSchema = z.object({
    transactionId: z.string().uuid(),
    occurredAt: z.string(),
    eventName: z.string().nullable(),
    donorDisplayName: z.string().nullable(),
    donorPubliclyAnonymous: z.boolean(),
    donorHidden: z.boolean(),
    /** The donation the donor chose, before processing support. */
    donation: moneySchema,
    /** What the gift was restricted to, by the name it carried when given. Null when unrestricted. */
    designationName: z.string().nullable(),
    donorNote: z.string().nullable(),
});
/* ------------------------------------ what arrived and what the fee took (DEC-034) */
/**
 * One line of the "from whom" breakdown.
 *
 * Since DEC-049 a publicly anonymous donor is an ordinary named line with `publiclyAnonymous`.
 * `displayName` null is the **hidden aggregate**: every gift in the period from a donor anonymous to
 * all organizations, and every gift that left no name, collapsed into one row with no identifier. There is deliberately no id, no hash, and no
 * stable pseudonym on this type, because a pseudonym stable enough to be useful is stable enough
 * to join across periods and rebuild a giving history for somebody who asked not to have one.
 *
 * `giftCount` is null on that row when the period holds too few anonymous gifts to publish a
 * count. A bucket of one is not an aggregate; it is a record of a person.
 */
export const receiptsDonorSchema = z.object({
    displayName: z.string().nullable(),
    /** The donor asked not to be named in public. Optional for clients built before GH-857. */
    publiclyAnonymous: z.boolean().optional(),
    gross: moneySchema,
    giftCount: z.number().int().nullable(),
});
/**
 * What arrived in a period, what the platform fee took, and what was left.
 *
 * The fee is reported **split**, and the split is the point. Donor-volunteered cost support and
 * fee netted from the grant are the same fee funded two different ways, and an organization shown
 * only the total cannot tell whether asking its congregation to cover the fee is working — which
 * is the one thing it can actually do about the number.
 */
export const organizationReceiptsResponseSchema = z.object({
    from: z.string(),
    to: z.string(),
    /** Gross gifts: what donors gave, before any fee. Reversals inside the period subtract. */
    gross: moneySchema,
    /** The whole platform fee against those gifts. */
    fee: moneySchema,
    feeCoveredByDonors: moneySchema,
    feeNettedFromGrant: moneySchema,
    /** Gross less the netted part — what was added to the balance. */
    credited: moneySchema,
    /** Processor fees on this organization's gifts. Absorbed by the platform, shown for candour. */
    processorFees: moneySchema,
    /** Envelope cash recorded in the period. The organization already holds it. */
    recordedElsewhere: moneySchema,
    giftCount: z.number().int(),
    /** The rate of the schedule in force now. Kept for clients built before GH-839. */
    rateBps: z.number().int(),
    /**
     * The schedules the period's gifts were actually charged, in words — "2.9% + $0.30 per gift"
     * (GH-839). Read from each gift's snapshot, never today's setting. Optional: an older server.
     */
    feeScheduleLabel: z.string().optional(),
    feeSchedules: z
        .array(z.object({ rateBps: z.number().int(), fixedMinor: z.number().int() }))
        .optional(),
    /** True when some gift in the period predates per-gift schedules (flat percentage then). */
    includesEarlierFlatRate: z.boolean().optional(),
    donors: z.array(receiptsDonorSchema),
    /** True when the anonymous row's count was withheld for being too small to aggregate. */
    anonymousCountWithheld: z.boolean(),
    /** The balance as it stands now — the same figures the accounting screen shows. */
    balance: z.object({
        available: moneySchema,
        pending: moneySchema,
        recordedElsewhere: moneySchema,
    }),
});
/**
 * One window of an organization's giving at a glance (GH-818): gross gifts, net of refunds and
 * reversals, and how many gifts still carry money. Never a pledge — a promise is not received money.
 */
export const givingWindowSchema = z.object({
    amount: moneySchema,
    giftCount: z.number().int().nonnegative(),
});
/**
 * `GET /v1/organizations/:organizationId/giving-stats` — the admin screen's four figures.
 *
 * Computed on the server from the ledger with the same gross rule as "What came in"
 * (`organizationReceiptsResponseSchema.gross`). A gift is placed in a window by when it was given;
 * a later refund or lost dispute reduces that gift rather than the day it was posted, so no window
 * goes negative. A gift under a pending dispute still counts — the processor holds the money, it has
 * not taken it — and counts again if a lost dispute is won on appeal (GH-868).
 * Behind `reconciliation.view`, like the receipts it summarises. Totals only: no donor, no gift.
 */
export const organizationGivingStatsSchema = z.object({
    /** The instant the windows end at. */
    asOf: z.string(),
    total: givingWindowSchema,
    last30Days: givingWindowSchema,
    last7Days: givingWindowSchema,
    last24Hours: givingWindowSchema,
});
/* ------------------------------------------------------ gifts & ledger (GH-826) */
/**
 * The windows the gifts screen offers. `year` is the calendar year to date in UTC — what a
 * treasurer closing a tax year means by "this year" — and `all` is no lower bound. An explicit
 * `from`/`to` on the query overrides the window.
 */
export const GIFT_WINDOWS = ["24h", "7d", "30d", "year", "all"];
/**
 * How a gift arrived. Online gifts read the rail from their payment attempt (Apple Pay and Google
 * Pay are the wallet button the donor pressed); `daf` is a donor-advised-fund grant; `cash` and
 * `check` are envelope entries. A card taken outside the platform and entered by staff is `card`.
 * `other` is a wallet payment whose button was not recorded.
 */
export const GIFT_METHODS = [
    "card",
    "apple_pay",
    "google_pay",
    "ach",
    "daf",
    "cash",
    "check",
    "other",
];
/**
 * Where a gift stands. `processing` is a payment not yet confirmed (a bank transfer in flight, a
 * DAF grant submitted to its sponsor); `refunded` covers a full or partial refund; `disputed` is a
 * chargeback that is open or was lost; `reversed` is an ACH return or a corrected envelope entry;
 * `failed` never became money. A pledge is never a gift and never appears.
 *
 * DEC-056 (GH-947): `authorized` is an end-of-day card gift whose money is held on the card and
 * captured after the organization's local midnight — cancellable until then; `cancelled` is one
 * staff withdrew (or that lapsed) before capture — nothing was ever charged. Read only by the
 * console (no shipped native client decodes this list).
 */
export const GIFT_STATUSES = [
    "completed",
    "processing",
    "authorized",
    "refunded",
    "disputed",
    "reversed",
    "failed",
    "cancelled",
];
/**
 * What the gift list may be filtered to: a row status, or GH-953's `awaiting_approval` — received
 * gifts with a refund waiting (`refundPending`). `completed` still includes those: the money has
 * not moved, and the CSV and the books agree.
 */
export const GIFT_STATUS_FILTERS = [...GIFT_STATUSES, "awaiting_approval"];
/** Which record a gift row was read from. Online gifts are the only ones with a receipt. */
export const GIFT_SOURCES = ["online", "daf", "offline"];
/**
 * `GET /v1/organizations/:organizationId/gifts/summary` — the five cards above the list.
 *
 * `received` is the same figure as `giving-stats`' `last30Days` (one SQL definition,
 * `accounting.GIVEN_GIFTS_CTE`): online gifts net of refunds and lost disputes (a pending dispute
 * still counts; GH-868), placed by when they were given, never a pledge. `processing` is online gifts whose payment has not been confirmed — not yet money.
 * `refunded` is refunds posted in the last 30 days; `disputesOpened` is chargebacks first seen in
 * the same window. `donorsCoveredFees` is over the received gifts. `lastPayout` is the most recent
 * grant sent to the organization's bank, `matchedToLedger` true once its settlement is posted.
 */
export const giftsSummaryResponseSchema = z.object({
    asOf: z.string(),
    received: z.object({ amount: moneySchema, giftCount: z.number().int().nonnegative() }),
    processing: z.object({ amount: moneySchema, giftCount: z.number().int().nonnegative() }),
    refunded: z.object({
        amount: moneySchema,
        giftCount: z.number().int().nonnegative(),
        disputesOpened: z.number().int().nonnegative(),
    }),
    donorsCoveredFees: z.object({
        /** Whole percent of received gifts whose donor covered the fee, 0–100. Null with no gifts. */
        sharePercent: z.number().int().min(0).max(100).nullable(),
        giftCount: z.number().int().nonnegative(),
        amountCovered: moneySchema,
    }),
    lastPayout: z
        .object({
        amount: moneySchema,
        paidAt: z.string(),
        state: z.enum(["submitted", "settled"]),
        matchedToLedger: z.boolean(),
    })
        .nullable(),
});
/**
 * The gift list's query string. Every filter is optional; the window defaults to `30d`. `from` and
 * `to` are ISO instants and override `window`. `limit` is 1–50 (default 25). `cursor` is the
 * opaque `nextCursor` of the previous page — keyset, never offset, because money lists grow while
 * somebody reads them.
 */
export const giftSearchQuerySchema = z.object({
    window: z.enum(GIFT_WINDOWS).optional(),
    from: z.string().datetime({ offset: true }).optional(),
    to: z.string().datetime({ offset: true }).optional(),
    eventId: z.string().uuid().optional(),
    designationId: z.string().uuid().optional(),
    method: z.enum(GIFT_METHODS).optional(),
    status: z.enum(GIFT_STATUS_FILTERS).optional(),
    /** An exact receipt number, e.g. from a donor's email. Matches online gifts only. */
    receiptNumber: z.string().trim().min(1).max(64).optional(),
    limit: z.coerce.number().int().min(1).max(50).optional(),
    cursor: z.string().max(200).optional(),
});
/**
 * One row of the gift list. Since DEC-049 (owner, 2026-10-03) "anonymous" hides a donor from the
 * public, not from the organization: `donorDisplay` is the donor's name in full,
 * `donorPubliclyAnonymous` says they asked not to be named in public (the console's badge),
 * "Anonymous donor" is a donor who is anonymous to all organizations — whose name is never selected
 * — and "No name given" a gift that left none. No email, phone, address or donor id is on a row.
 */
export const giftRowSchema = z.object({
    id: z.string().uuid(),
    source: z.enum(GIFT_SOURCES),
    at: z.string(),
    donorDisplay: z.string(),
    donorPubliclyAnonymous: z.boolean(),
    method: z.enum(GIFT_METHODS),
    status: z.enum(GIFT_STATUSES),
    amount: moneySchema,
    eventId: z.string().uuid().nullable(),
    eventName: z.string().nullable(),
    designationName: z.string().nullable(),
    receiptNumber: z.string().nullable(),
    /** GH-953: a refund of this received gift is waiting; `status` stays `completed`. */
    refundPending: refundPendingSchema,
});
export const giftsListResponseSchema = z.object({
    /** The window actually applied, so the screen can say what it is showing. Null `from` is "all". */
    from: z.string().nullable(),
    to: z.string(),
    gifts: z.array(giftRowSchema),
    /** Every gift matching the filters, across all pages. */
    totalCount: z.number().int().nonnegative(),
    nextCursor: z.string().nullable(),
});
export const GIFT_TRAIL_STEPS = [
    "quote_issued",
    "payment_confirmed",
    "posted_to_ledger",
    "receipt_issued",
    "receipt_emailed",
    "refunded",
    "disputed",
    "recorded_offline",
    "payout",
];
/**
 * One step of a gift's trail, read from existing records. `at` is when it happened, or null while
 * it has not; `expectedAt` is set only on `payout`, from the organization's payout schedule — gifts
 * are granted in aggregate, so no single gift is tied to a single payout.
 */
export const giftTrailStepSchema = z.object({
    step: z.enum(GIFT_TRAIL_STEPS),
    at: z.string().nullable(),
    expectedAt: z.string().nullable(),
});
export const giftActionSchema = z.object({
    available: z.boolean(),
    /** Why not, as a code the console words: `no_receipt`, `no_email`, `not_supported`, … */
    reason: z.string().nullable(),
});
/**
 * The refund action (GH-840): whether the console can refund this gift, and — whenever the gift's
 * stored figures allow it — the arithmetic, so the confirm can say "Donor receives $Y (they paid
 * $T; the $F platform fee is kept)". Reasons: `not_received`, `already_refunded`,
 * `awaiting_second_approval`, `awaiting_processor_approval` (GH-948), `held_gift`, `disputed`,
 * `window_closed`, `rail_not_supported`, `nothing_to_refund`, `no_permission`, `not_online`,
 * `refunds_disabled` (GH-951, DEC-058: the organization turned refunds off; a cancel of an
 * uncaptured authorization is still offered).
 *
 * `processorApproval` (GH-948): the processor's approval request while the reason is
 * `awaiting_processor_approval`; null otherwise. Optional so an older response still parses.
 */
export const giftRefundActionSchema = giftActionSchema.extend({
    figures: z
        .object({ charged: moneySchema, refund: moneySchema, platformFeeKept: moneySchema })
        .nullable()
        .default(null),
    processorApproval: processorApprovalSchema.nullable().optional(),
});
/**
 * `GET /v1/organizations/:organizationId/gifts/:giftId` — one gift, opened from its row.
 *
 * The same masking as the row. `emailUpdates` is yes/no only — whether the donor currently agrees
 * to this organization's updates — never the address. `processorFee` is null: the processor's fee
 * is posted per settlement, not per gift, and the platform absorbs it; `netToOrganization` is what
 * the ledger credited the organization for this gift after any refund, null until it posts.
 *
 * `actions.refund` (GH-840) says whether the console can refund the gift and for how much: the
 * donor gets back what they paid minus the gift's platform fee, which is kept.
 */
export const giftsDetailResponseSchema = z.object({
    id: z.string().uuid(),
    source: z.enum(GIFT_SOURCES),
    at: z.string(),
    donorDisplay: z.string(),
    donorPubliclyAnonymous: z.boolean(),
    recognition: z
        .enum(["anonymous", "donor_name", "family_or_organization", "dedication"])
        .nullable(),
    method: z.enum(GIFT_METHODS),
    status: z.enum(GIFT_STATUSES),
    /** GH-953: as on the row — a refund waiting; the gift is still received. */
    refundPending: refundPendingSchema,
    receiptNumber: z.string().nullable(),
    eventName: z.string().nullable(),
    designationName: z.string().nullable(),
    amounts: z.object({
        gift: moneySchema,
        feesCoveredByDonor: moneySchema.nullable(),
        platformFee: moneySchema.nullable(),
        processorFee: moneySchema.nullable(),
        netToOrganization: moneySchema.nullable(),
    }),
    emailUpdates: z.boolean().nullable(),
    /**
     * GH-864f: the gift came through a member's share link (`donations.share_code_id` is set). A
     * yes/no only — never which member shared it. Shown whatever `FEATURE_MEMBERSHIP` says.
     */
    sharedByMember: z.boolean().default(false),
    trail: z.array(giftTrailStepSchema),
    /**
     * `cancel` (DEC-056): withdraw an `authorized` end-of-day gift before capture. Reasons:
     * `not_authorized` (nothing held to withdraw), `no_permission`, `not_online`. Always sent by
     * this server; optional so a response from before the field still parses (absent = no cancel).
     */
    actions: z.object({
        resendReceipt: giftActionSchema,
        refund: giftRefundActionSchema,
        cancel: giftActionSchema.optional(),
    }),
    /** DEC-056: how and when the card is captured; absent for gifts with no card attempt. */
    capture: giftCaptureSchema.optional(),
});
/**
 * `POST /v1/organizations/:organizationId/gifts/:giftId/receipt/resend` — send the receipt again
 * to the address the donor gave, through the ordinary receipt mailer. The address is never
 * returned. `suppressed` means this deployment's `EMAIL_PURPOSES` (or a bounce) stopped it, and the
 * delivery row says so.
 */
export const giftReceiptResendResponseSchema = z.object({
    status: z.enum(["queued", "sent", "delivered", "failed", "suppressed"]),
});
/** The columns of `gifts.csv`, in order. No donor contact data, ever. */
export const GIFTS_CSV_COLUMNS = [
    "gift_id",
    "date_utc",
    "source",
    "method",
    "status",
    "donor",
    "event",
    "designation",
    "receipt_number",
    "amount",
    "currency",
    // DEC-049: the donor is named to the organization; this says not to name them in public.
    "anonymous_publicly",
];
/* ------------------------------------------------------------------- home (GH-841) */
/**
 * Home's period selector: the last 7 days, the last 30 days, or the calendar year to date (UTC).
 * Each is the gift list's own window (`7d`, `30d`, `year`), so Home's figures, its Export and
 * Gifts & ledger describe the same gifts.
 */
export const HOME_PERIODS = ["7d", "30d", "ytd"];
export const homeQuerySchema = z.object({
    period: z.enum(HOME_PERIODS).default("30d"),
    /** The event the live card is about — the console's own pick (`currentEvent`). */
    featured: z.string().uuid().optional(),
});
/**
 * Where a gift came in through (GH-841). The first four are attribution tags set where the link is
 * minted; `tap_to_pay` is a card a staff member's iPhone took in person (GH-848); `envelopes` is posted cash and checks, `pledges_paid` a gift paying an earlier pledge,
 * `daf` a completed donor-advised-fund grant, and `unknown` every online gift with no tag — every
 * gift before the tag existed and every link minted by an older client. Never guessed.
 *
 * `embed_widget` and `email_button` (GH-847, migration 0091): the give box framed on another site,
 * and the email-safe Give button.
 *
 * `payment_button` and `printed_qr` (GH-852, migration 0095): the console's copy-paste button on
 * another website, and its QR code for flyers. Its plain and Markdown links count as `share_link`.
 */
export const HOME_GIFT_SOURCES = [
    "table_qr",
    "display_qr",
    "share_link",
    "app",
    "tap_to_pay",
    "embed_widget",
    "email_button",
    "payment_button",
    "printed_qr",
    "envelopes",
    "pledges_paid",
    "daf",
    "unknown",
];
const homeCountedSchema = z.object({
    amount: moneySchema,
    count: z.number().int().nonnegative(),
});
/**
 * The live card's event, in the ledger's own figures at `asOf`. `received` is `receivedOnline` plus
 * `envelopesPosted`, added in SQL — the client never adds money. `processing` is not yet money and
 * `pledged` is a promise; neither is ever inside `received`.
 */
export const homeFeaturedSchema = z.object({
    eventId: z.string().uuid(),
    goal: moneySchema,
    received: moneySchema,
    receivedOnline: moneySchema,
    envelopesPosted: moneySchema,
    processing: moneySchema,
    pledged: moneySchema,
    /** Completed online gifts, the goal display's own count. */
    giftCount: z.number().int().nonnegative(),
});
export const homeDaySchema = z.object({
    /** A UTC calendar day, `YYYY-MM-DD`. */
    date: z.string().regex(/^\d{4}-\d{2}-\d{2}$/),
    online: moneySchema,
    envelopes: moneySchema,
    daf: moneySchema,
    /** The three added together in SQL, so a bar's height needs no client arithmetic. */
    total: moneySchema,
});
/**
 * Home's money, present only for a role holding `reconciliation.view` (null otherwise).
 *
 * - `raised` — online gifts given in the period, net of refunds and lost disputes: the same SQL
 *   as `giving-stats` and the gifts summary (`accounting.GIVEN_GIFTS_CTE`), so the figures match
 *   to the cent.
 * - `priorRaised` — the same for the equal period immediately before; `changePercent` the whole
 *   percent between them, null when the prior period had nothing.
 * - `donors.count` — distinct donor accounts behind `raised`; `firstTime` how many of them gave
 *   this organization their first gift in the period; `guestGifts` gifts with no account, which
 *   cannot be told apart and are not counted as donors. Counts only, never an identity.
 * - `openPledges` — every promise still open (never received money), and those due in the next 7
 *   days. `dafInFlight` — grants submitted to the sponsor: receivable, not received.
 * - `daily` — one row per UTC day of the period; each column sums to `raised`, `envelopesPosted`
 *   and `dafReceived` respectively.
 * - `sources` — the period's received amount (online + envelopes + DAF, summed in SQL) by where it
 *   came in, `sharePercent` whole percent of `sources.total`.
 * - `recentGifts` — the newest rows of the gift list for the period, masked exactly as there.
 */
/**
 * The Home sources line for gifts through members' share links (GH-864f, DEC-051): a count and a
 * gross, or `withheld` when fewer than 5 gifts (k = 5, DEC-049's threshold). Counts only — no
 * sharer, member, giver or code. `membership.ts` re-exports it as `memberSharesSchema`.
 */
export const homeMemberSharesSchema = z.union([
    z.object({ giftCount: z.number().int().nonnegative(), grossMinor: minorUnitsSchema }).strict(),
    z.object({ withheld: z.literal(true) }).strict(),
]);
export const homeMoneySchema = z.object({
    raised: homeCountedSchema,
    priorRaised: homeCountedSchema,
    changePercent: z.number().int().nullable(),
    envelopesPosted: homeCountedSchema,
    dafReceived: homeCountedSchema,
    donors: z.object({
        count: z.number().int().nonnegative(),
        firstTime: z.number().int().nonnegative(),
        guestGifts: z.number().int().nonnegative(),
    }),
    openPledges: z.object({
        amount: moneySchema,
        count: z.number().int().nonnegative(),
        dueThisWeek: homeCountedSchema,
    }),
    dafInFlight: homeCountedSchema,
    daily: z.array(homeDaySchema),
    sources: z.object({
        total: moneySchema,
        rows: z.array(z.object({
            source: z.enum(HOME_GIFT_SOURCES),
            amount: moneySchema,
            sharePercent: z.number().int().min(0).max(100),
        })),
        /**
         * GH-864f: gifts that came through members' share links (`donations.share_code_id`), withheld
         * below 5 gifts. Present only while `FEATURE_MEMBERSHIP` is on. Never names a sharer.
         */
        memberShares: homeMemberSharesSchema.optional(),
    }),
    recentGifts: z.array(giftRowSchema),
});
/**
 * One thing waiting on somebody, each pointing at the screen where it is handled. Every kind is
 * included only for a role that may act on it (offline batches: `offline_batch.verify`; held
 * refunds: `donation.refund`; pledges and DAF: `reconciliation.view`; events: `event.publish`).
 */
export const homeNeedsYouItemSchema = z.discriminatedUnion("kind", [
    z.object({
        kind: z.literal("offline_batch"),
        batchId: z.string().uuid(),
        eventId: z.string().uuid(),
        /** Tenant-authored. */
        label: z.string(),
        amount: moneySchema,
        /** At or above the two-person line: someone who did not enter or submit it must verify. */
        needsSecondPerson: z.boolean(),
        submittedAt: z.string().nullable(),
    }),
    z.object({
        kind: z.literal("held_refund"),
        requestId: z.string().uuid(),
        /** GH-954: a refund of a gift (no event) waits too; Home opens Gifts & ledger for it. */
        subject: z.enum(["ticket_order", "gift"]).default("ticket_order"),
        eventId: z.string().uuid().nullable(),
        amount: moneySchema,
        requestedAt: z.string(),
    }),
    z.object({
        kind: z.literal("pledges_due"),
        count: z.number().int().positive(),
        amount: moneySchema,
    }),
    z.object({
        kind: z.literal("daf_in_flight"),
        grantId: z.string().uuid(),
        amount: moneySchema,
        trackingId: z.string().nullable(),
        daysInFlight: z.number().int().nonnegative(),
    }),
    z.object({
        kind: z.literal("missing_cover_photo"),
        eventId: z.string().uuid(),
        eventName: z.string(),
        startsAt: z.string(),
    }),
    z.object({
        // GH-958: readers reported donor-written text at this event; a host keeps or removes each.
        kind: z.literal("reported_content"),
        eventId: z.string().uuid(),
        eventName: z.string(),
        /** Reported items waiting on a decision. */
        count: z.number().int().positive(),
    }),
    z.object({
        kind: z.literal("draft_starting_soon"),
        eventId: z.string().uuid(),
        eventName: z.string(),
        startsAt: z.string(),
    }),
]);
/**
 * `GET /v1/organizations/:organizationId/home?period=7d|30d|ytd[&featured=<eventId>]` — Home's
 * aggregates in one tenant-guarded read. Each section is the caller's to see or null/empty: a role
 * without `reconciliation.view` gets `money: null` and no money items, never a refusal of Home.
 */
export const homeResponseSchema = z.object({
    asOf: z.string(),
    period: z.enum(HOME_PERIODS),
    from: z.string(),
    to: z.string(),
    priorFrom: z.string(),
    featured: homeFeaturedSchema.nullable(),
    money: homeMoneySchema.nullable(),
    needsYou: z.object({
        /** Every item waiting, which can exceed `items` (each kind is capped at five). */
        count: z.number().int().nonnegative(),
        items: z.array(homeNeedsYouItemSchema),
    }),
    /**
     * Tickets sold for each upcoming event that sells tickets (`ticketing.manage`); an event without
     * ticket tiers is absent, and so is everything for a role without the permission.
     */
    ticketsSold: z.array(z.object({ eventId: z.string().uuid(), sold: z.number().int().nonnegative() })),
});
export const accountingSummarySchema = z.object({
    balance: organizationBalanceSchema,
    /** Null where no bank account has been attached. */
    bankAccount: z
        .object({
        /** Last four only. The account number itself never reaches this database, let alone a wire. */
        last4: z.string().length(4),
        bankName: z.string().nullable(),
        accountHolderName: z.string(),
        verifiedAt: z.string().nullable(),
        /** When the cooling-off period after adding this account ends. */
        payableFrom: z.string().nullable(),
        /**
         * How it was verified (GH-885, DEC-054): automatically (Plaid, owner name exactly the IRS
         * legal name for the EIN), by a platform reviewer, waiting for one, or refused.
         */
        verificationStatus: z.enum([
            "verified_automatically",
            "pending_review",
            "verified_by_staff",
            "rejected",
        ]),
        verificationMethod: z.enum(["plaid_identity", "manual_entry"]),
        /** Codes for why it was not verified automatically; the console says each in a sentence. */
        verificationReasons: z.array(z.string()),
    })
        .nullable(),
    /**
     * Whether this deployment can connect a bank through Plaid (GH-885), and in which environment.
     * Off where no Plaid keys are configured, or where the environment is not allowed (a production
     * deployment never connects through Plaid's sandbox).
     */
    bankLink: z.object({
        available: z.boolean(),
        environment: z.enum(["sandbox", "development", "production"]).nullable(),
    }),
    /** Why a grant cannot be requested right now, or null when one can. */
    payoutRefusal: z.string().nullable(),
    payoutRefusalMessage: z.string().nullable(),
});
/* --------------------------------------------------------------------- payouts */
export const PAYOUT_STATES_WIRE = [
    "requested",
    "approved",
    "rejected",
    "submitted",
    "settled",
    "failed",
    "returned",
];
export const payoutSchema = z.object({
    id: z.string().uuid(),
    organizationId: z.string().uuid(),
    organizationDisplayName: z.string().nullable(),
    amount: moneySchema,
    state: z.enum(PAYOUT_STATES_WIRE),
    /** Where it went. Last four only, resolved from the bank account the payout named. */
    destinationLast4: z.string().nullable(),
    /** NACHA return code on a failed credit. R02–R04 halt the organization. */
    returnCode: z.string().nullable(),
    requestedAt: z.string(),
    submittedAt: z.string().nullable(),
    settledAt: z.string().nullable(),
});
export const payoutRequestSchema = z.object({
    /** Minor units as a decimal string. Money never becomes a float on this wire. */
    amountMinor: z.string().regex(/^[1-9][0-9]{0,15}$/),
    currency: z.literal("USD"),
});
export const payoutListResponseSchema = z.object({
    payouts: z.array(payoutSchema),
});
export const payoutDecisionSchema = z.object({
    decision: z.enum(["approved", "rejected"]),
    /**
     * Required on a rejection and optional on an approval.
     *
     * A rejection an organization cannot read a reason for produces a support conversation that costs
     * more than the field does.
     */
    note: z.string().max(2000).nullable().default(null),
    /**
     * The charitable-purpose finding, required on an approval (DEC-028, outside counsel 2026-08-28).
     *
     * `purposeAffirmed` has **no default**. A default of `false` would be safe but would let a
     * client omit the field and receive a refusal it did not ask for; a default of `true` would
     * certify on a reviewer's behalf, which is the whole thing this exists to prevent. Requiring it
     * explicitly on an approval means the affirmation is always something a caller *said*.
     */
    purposeAffirmed: z.boolean().optional(),
    purposeStatement: z.string().max(2000).optional(),
});
export const bankAccountRequestSchema = z.object({
    /** A Plaid Link public token exchange result — never an account number. */
    providerItemId: z.string().max(200).nullable().default(null),
    /**
     * The rail's handle on the account, from Link.
     *
     * Nullable so an organization on a deployment with no approved credit rail can still record where
     * its money should go — bank name, holder, and last four, which is everything this schema holds.
     * The server mints a synthetic handle in that case and refuses to on a real one. What is *not*
     * offered, on any deployment, is a routing or account number field: instant verification is the
     * whole reason Link exists, and a manual form would reintroduce exactly the data this table has
     * nowhere to put.
     */
    providerAccountId: z.string().min(1).max(200).nullable().default(null),
    bankName: z.string().max(200).nullable().default(null),
    last4: z.string().regex(/^[0-9]{4}$/),
    accountHolderName: z.string().min(1).max(200),
});
/**
 * Sending an approved grant.
 *
 * `providerObjectId` is the manual path: a credit originated by hand at the vendor's dashboard,
 * whose transfer id is recorded here. Omitted, the selected rail originates. It is optional rather
 * than two separate routes because the resulting payout is the same object either way, and a second
 * route would be a second place for the eligibility re-check to be forgotten.
 */
export const payoutSendSchema = z.object({
    providerObjectId: z.string().min(1).max(200).nullable().default(null),
});
/**
 * `refusedReason` is a successful response, not an error.
 *
 * The rail declining is an ordinary outcome the caller has to render: the grant really was
 * submitted, really was then reversed, and the organization's balance really is back. A status code
 * cannot say that, and mapping it to a 4xx would tell an operator the request was malformed.
 */
export const payoutSendResponseSchema = z.object({
    providerObjectId: z.string().nullable(),
    refusedReason: z.string().nullable(),
});
/** What a rail reported about a transfer, recorded by hand where no webhook delivers it. */
export const payoutRailEventSchema = z
    .object({
    providerObjectId: z.string().min(1).max(200),
    event: z.enum(["settled", "returned"]),
    /** NACHA return code. R02–R04 halt the organization; see `dispositionForReturnCode`. */
    returnCode: z
        .string()
        .regex(/^R[0-9]{2}$/)
        .nullable()
        .default(null),
})
    .refine((value) => value.event !== "returned" || value.returnCode !== null, {
    message: "A returned credit must carry the return code that came back with it.",
    path: ["returnCode"],
});
/* ---------------------------------------------------------- scheduled sweeps */
export const PAYOUT_CADENCE_VALUES = ["weekly", "biweekly", "monthly"];
/**
 * An organization's standing instruction to sweep its balance to its bank.
 *
 * `description` and `nextRunAt` are server-composed rather than derived in the console, for the
 * same reason every other money sentence is: this describes a standing instruction to move
 * somebody's money, and a client computing its own wording is a client that can describe the
 * instruction differently from the one that will actually run.
 */
export const payoutScheduleSchema = z.object({
    enabled: z.boolean(),
    cadence: z.enum(PAYOUT_CADENCE_VALUES),
    /** Weekly and biweekly: 0=Sunday … 6=Saturday. Monthly: 1–28. */
    dayOfPeriod: z.number().int().min(0).max(28),
    minimumAmount: moneySchema,
    maximumAmount: moneySchema.nullable(),
    description: z.string(),
    nextRunAt: z.string().datetime().nullable(),
    lastRunAt: z.string().datetime().nullable(),
    lastRunOutcome: z.string().nullable(),
});
export const payoutScheduleRequestSchema = z.object({
    enabled: z.boolean(),
    cadence: z.enum(PAYOUT_CADENCE_VALUES),
    dayOfPeriod: z.number().int().min(0).max(28),
    minimumAmountMinor: z.string().regex(/^[0-9]{1,16}$/),
    maximumAmountMinor: z
        .string()
        .regex(/^[1-9][0-9]{0,15}$/)
        .nullable()
        .default(null),
});
/** Null where an organization has never chosen one — distinct from having turned one off. */
export const payoutScheduleResponseSchema = z.object({
    schedule: payoutScheduleSchema.nullable(),
});
/* -------------------------------------------------------- year-end documents */
/** The donee, in the words that go on a statement. Server-composed under Notice 2012-52. */
export const doneeIdentitySchema = z.object({
    legalName: z.string(),
    ein: z.string().nullable(),
    addressLines: z.array(z.string()),
    disregardedEntityName: z.string().nullable(),
});
export const statementLineSchema = z.object({
    occurredAt: z.string().datetime(),
    amount: moneySchema,
    receiptNumber: z.string(),
    organizationName: z.string().nullable(),
});
/**
 * A donor's year-end summary.
 *
 * `explanations` carries only the exclusions that actually applied, and `caveat` is present on
 * every statement — it is what stops a donor discarding the per-gift acknowledgments that are the
 * documents §170(f)(8) actually requires.
 */
export const donorStatementResponseSchema = z.object({
    year: z.number().int(),
    donee: doneeIdentitySchema,
    lines: z.array(statementLineSchema),
    total: moneySchema,
    admissions: z.array(statementLineSchema),
    admissionsTotal: moneySchema,
    explanations: z.array(z.string()),
    caveat: z.string(),
});
export const grantStatementResponseSchema = z.object({
    year: z.number().int(),
    donee: doneeIdentitySchema,
    grants: z.array(z.object({
        settledAt: z.string().datetime(),
        amount: moneySchema,
        reference: z.string(),
    })),
    total: moneySchema,
    notes: z.array(z.string()),
});
/** Working papers for Form 990 Schedule I Part II, not a finished schedule — see `caveats`. */
export const scheduleIResponseSchema = z.object({
    year: z.number().int(),
    donee: doneeIdentitySchema,
    rows: z.array(z.object({
        organizationName: z.string(),
        ein: z.string().nullable(),
        address: z.string().nullable(),
        amount: moneySchema,
        requiresDetail: z.boolean(),
    })),
    total: moneySchema,
    detailedTotal: moneySchema,
    caveats: z.array(z.string()),
});
/* ------------------------------------------------------------- reconciliation */
export const reconciliationVarianceSchema = z.object({
    id: z.string().uuid(),
    kind: z.string(),
    severity: z.enum(["info", "warn", "critical"]),
    organizationId: z.string().uuid().nullable(),
    organizationDisplayName: z.string().nullable(),
    provider: z.string().nullable(),
    providerObjectId: z.string().nullable(),
    expected: moneySchema.nullable(),
    actual: moneySchema.nullable(),
    detail: z.string(),
    /** What to do about it. A classification without a next action is a list, not a control. */
    suggestion: z.string(),
    /**
     * When this problem was first seen, not when it was last seen.
     *
     * Ageing is the field that makes the queue usable: a variance open for three days is a different
     * object from one opened this morning, and a report that cannot tell them apart is a report
     * people stop opening.
     */
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
    seenCount: z.number().int().min(1),
    resolvedAt: z.string().nullable(),
    resolvedByName: z.string().nullable(),
    resolution: z.string().nullable(),
    resolutionNote: z.string().nullable(),
});
export const reconciliationRunSchema = z.object({
    id: z.string().uuid(),
    windowStart: z.string(),
    windowEnd: z.string(),
    startedAt: z.string(),
    completedAt: z.string().nullable(),
    matchedCount: z.number().int(),
    varianceCount: z.number().int(),
    /**
     * Set when the run itself crashed, as opposed to succeeding and finding nothing.
     *
     * Those two look identical in a variance count and only one of them is good news — a job that has
     * been throwing for a fortnight under a clean dashboard is the silent failure mode of this whole
     * control.
     */
    failedReason: z.string().nullable(),
    triggeredBy: z.string(),
});
export const reconciliationResponseSchema = z.object({
    open: z.array(reconciliationVarianceSchema),
    recentlyResolved: z.array(reconciliationVarianceSchema),
    runs: z.array(reconciliationRunSchema),
});
export const varianceResolutionSchema = z.object({
    /**
     * `closed` means fixed; `accepted` means permanent and known.
     *
     * Both are needed. Without `accepted`, a list of known-permanent differences grows until it is
     * ignored, and the day a real one appears nobody notices.
     */
    resolution: z.enum(["closed", "accepted", "superseded"]),
    note: z.string().min(10).max(2000),
});
/**
 * Running the reconciliation over a window, on purpose.
 *
 * The window is optional because the ordinary case is "the one that should have run last night",
 * and making an operator type two dates to answer "has anything been checked at all" is how a
 * control ends up never being exercised. Supplied, it is the re-run of a past window the plan
 * requires — same window, same answer, findings aged rather than duplicated.
 */
export const reconciliationRunRequestSchema = z.object({
    windowStart: z.string().datetime().nullable().default(null),
    windowEnd: z.string().datetime().nullable().default(null),
});
export const reconciliationRunResultSchema = z.object({
    runId: z.string().uuid(),
    windowStart: z.string(),
    windowEnd: z.string(),
    matched: z.number().int(),
    variances: z.number().int(),
    /** New findings only. A re-run that ages ten existing ones and finds nothing new reports zero. */
    newFindings: z.number().int(),
});
/* ------------------------------------------------------------- sweeps, by hand */
export const SWEEP_OUTCOME_VALUES = [
    "swept",
    "below_minimum",
    "nothing_available",
    "refused",
    "error",
];
/**
 * One run of a standing instruction.
 *
 * Recorded even when nothing moved, which is the point: without the quiet runs, "we have not been
 * paid in a month" cannot be told apart from "the job has been dead for a month".
 */
export const payoutScheduleRunSchema = z.object({
    id: z.string().uuid(),
    ranAt: z.string(),
    outcome: z.enum(SWEEP_OUTCOME_VALUES),
    /** Why, in the server's words. Null where the outcome says everything. */
    detail: z.string().nullable(),
    amount: moneySchema.nullable(),
    payoutId: z.string().uuid().nullable(),
    /** True where a person pressed the button rather than the calendar coming round. */
    manual: z.boolean(),
});
export const payoutScheduleRunsResponseSchema = z.object({
    runs: z.array(payoutScheduleRunSchema),
});
/**
 * What a hand-pressed run did.
 *
 * `needsPlatformApproval` is on the wire because it is the difference between "the money is on its
 * way" and "a reviewer has to look at this first", and a screen that says the former when the
 * latter is true produces a phone call three days later.
 */
export const sweepNowResponseSchema = z.object({
    outcome: z.enum(SWEEP_OUTCOME_VALUES),
    detail: z.string().nullable(),
    amount: moneySchema.nullable(),
    payoutId: z.string().uuid().nullable(),
    needsPlatformApproval: z.boolean(),
});
/** The whole batch, for the platform button that stands in for a timer nobody has deployed yet. */
export const sweepBatchResponseSchema = z.object({
    examined: z.number().int(),
    swept: z.number().int(),
    skipped: z.number().int(),
    refused: z.number().int(),
    errors: z.number().int(),
});
/* --------------------------------------------------------------- the daily close */
export const closeCheckSchema = z.object({
    id: z.string(),
    label: z.string(),
    /**
     * Both sides signed.
     *
     * A check comparing what we hold against what we owe has to be able to say that what we hold is
     * negative — that is the single most important sentence this page could ever print, and an
     * unsigned field would render it as a healthy-looking positive number.
     */
    expected: signedMoneySchema,
    actual: signedMoneySchema,
    /** Actual minus expected. Signed, because which way it is wrong changes what happened. */
    difference: signedMoneySchema,
    ok: z.boolean(),
    /** A failing `critical` check blocks the close outright; a `warn` is reported and signed over. */
    severity: z.enum(["critical", "warn"]),
    explanation: z.string(),
});
export const accountPositionSchema = z.object({
    account: z.string(),
    accountClass: z.enum(["asset", "liability", "revenue", "expense"]),
    /**
     * In the account's natural direction, so cash reads positive when we hold money.
     *
     * Signed rather than unsigned: an asset that has gone negative is one of the most important
     * things this page can say, and an absolute value would hide it behind a sign nobody sees.
     */
    balance: signedMoneySchema,
    /** True where nothing has ever posted here. Shown, because an empty account can be the finding. */
    empty: z.boolean(),
});
export const accountMovementSchema = z.object({
    account: z.string(),
    debit: moneySchema,
    credit: moneySchema,
    net: signedMoneySchema,
});
/** What donors authorized on the day, by how they paid, and where each attempt ended up. */
export const methodTotalsSchema = z.object({
    methodClass: z.string(),
    authorizedCount: z.number().int(),
    authorized: moneySchema,
    confirmedCount: z.number().int(),
    confirmed: moneySchema,
    outstandingCount: z.number().int(),
    outstanding: moneySchema,
    failedCount: z.number().int(),
    failed: moneySchema,
    reversedCount: z.number().int(),
    reversed: moneySchema,
});
export const takeRateSchema = z.object({
    /** The schedule configured now — informational; the check reads per-gift snapshots (GH-839). */
    rateBps: z.number().int(),
    /** Optional: a close signed before GH-839 replays without it. */
    fixedMinor: z.number().int().optional(),
    /** What each gift's own schedule says the day's gifts should have yielded, summed. */
    nominal: signedMoneySchema,
    /** What was actually kept. */
    retained: signedMoneySchema,
    /**
     * Nominal less retained. Read `roundingAllowance` before reading anything into it.
     *
     * Since GH-839 this is exactly zero when every gift was charged its own snapshotted schedule;
     * before it, a rounding residue of up to a cent per gift was allowed.
     */
    shortfall: signedMoneySchema,
    /**
     * The three below are optional **because a signed day is replayed from its stored snapshot**.
     * A close signed before DEC-034 has no such fields, and making them required would make that
     * day unreadable — which is the one thing a close must never become.
     */
    coveredByDonors: signedMoneySchema.optional(),
    nettedFromGrants: signedMoneySchema.optional(),
    roundingAllowance: signedMoneySchema.optional(),
});
export const dailyCloseSignatureSchema = z.object({
    closedAt: z.string(),
    closedByName: z.string().nullable(),
    note: z.string(),
    /** 1 for the first close of a date; higher where it was re-closed after a correction. */
    sequence: z.number().int(),
});
/**
 * Everything a day's close asserts.
 *
 * Returned live for an unclosed day and replayed verbatim from the stored snapshot for a closed
 * one — which is the point of storing it. A closed day must not silently re-answer from a ledger
 * that has had corrections posted into it since somebody signed.
 */
export const dailyCloseResponseSchema = z.object({
    date: z.string(),
    /** Present once the day has been signed; null while it is still open. */
    signature: dailyCloseSignatureSchema.nullable(),
    /** True when the figures came from the stored snapshot rather than from a live query. */
    fromSnapshot: z.boolean(),
    position: z.object({
        accounts: z.array(accountPositionSchema),
        organizationCount: z.number().int(),
        owedToOrganizations: signedMoneySchema,
        availableToOrganizations: signedMoneySchema,
        pendingForOrganizations: signedMoneySchema,
        inFlightToOrganizations: signedMoneySchema,
        /** Cash in our bank plus whatever is still sitting at the processor. */
        heldByUs: signedMoneySchema,
        /** Envelope cash the organizations physically hold. Never payable, and never ours. */
        recordedElsewhere: signedMoneySchema,
    }),
    movement: z.object({
        accounts: z.array(accountMovementSchema),
        byMethod: z.array(methodTotalsSchema),
        giftsReceived: signedMoneySchema,
        giftCount: z.number().int(),
        feeRetained: signedMoneySchema,
        settledGross: moneySchema,
        settledFees: moneySchema,
        settledNet: moneySchema,
        settlementCount: z.number().int(),
        unpostedSettlementCount: z.number().int(),
        unpostedSettlementNet: moneySchema,
        grantsSubmitted: moneySchema,
        grantsSettled: moneySchema,
        reversed: moneySchema,
        reversalCount: z.number().int(),
        disputesOpened: moneySchema,
        disputesResolved: moneySchema,
    }),
    outstanding: z.object({
        count: z.number().int(),
        amount: moneySchema,
        /** The oldest authorization still in flight. An old one is the item worth chasing. */
        oldestAt: z.string().nullable(),
    }),
    takeRate: takeRateSchema,
    checks: z.array(closeCheckSchema),
    /** Empty means the day may be signed. Anything here blocks it. */
    blocking: z.array(z.string()),
});
export const dailyCloseRequestSchema = z.object({
    /** Required and required to say something — the note is the part an examiner reads. */
    note: z.string().min(10).max(2000),
});
export const dailyCloseListResponseSchema = z.object({
    closes: z.array(dailyCloseSignatureSchema.extend({
        date: z.string(),
        owedToOrganizations: signedMoneySchema,
        giftsReceived: signedMoneySchema,
    })),
});
/**
 * Organizations whose grants are stopped.
 *
 * A halt is set by a machine and cleared by a person, so it needs somewhere a person looks. The
 * refusal an organization sees says "contact support"; without this list, support has no button
 * either and the sentence points nowhere.
 */
export const haltedOrganizationsResponseSchema = z.object({
    organizations: z.array(z.object({
        organizationId: z.string().uuid(),
        displayName: z.string(),
        haltedAt: z.string(),
        /** The return code and what it meant, as the halt recorded it. Null on an older halt. */
        reason: z.string().nullable(),
    })),
});
/* ------------------------------------------------------------------- reports
 *
 * The console's report shapes, defined **once**.
 *
 * These lived in two hand-kept copies — `services/api/src/reports-routes.ts` and
 * `apps/admin-web/src/api.ts` — because this file was owned by another change when reports landed.
 * Both files said so and both called it temporary. Two hand-maintained copies of a wire shape is
 * exactly the drift this package exists to prevent: the server can widen a field and the console
 * keeps parsing against yesterday's shape, and nothing fails until a reader sees a blank column.
 *
 * The ranges are **half-open `[from, to)` UTC instants**. Both bounds are always sent; the server
 * refuses a request that omits one rather than guessing, so a report can never mean a different
 * window from the one its heading claims.
 */
export const reportRangeSchema = z.object({
    /** Inclusive lower bound, ISO-8601 UTC instant. */
    from: z.string(),
    /** **Exclusive** upper bound, ISO-8601 UTC instant. */
    to: z.string(),
    /** Named for the reader, never parsed: the sentence the screen shows above the numbers. */
    label: z.string(),
});
export const transactionsReportSchema = z.object({
    range: reportRangeSchema,
    totalCount: z.number().int(),
    /**
     * Net effect on what the organization is owed, over the window. **Signed**: a window holding a
     * refund of an earlier gift and nothing else nets negative, and a report must still render.
     */
    net: signedMoneySchema,
    /** Everything that increased the payable balance, and everything that decreased it. */
    credited: moneySchema,
    debited: moneySchema,
    byKind: z.array(z.object({
        kind: z.string(),
        count: z.number().int(),
        /** Signed: refunds and payouts are kinds whose net is below zero. */
        net: signedMoneySchema,
    })),
});
export const donorsReportSchema = z.object({
    range: reportRangeSchema,
    /**
     * One row per donor who chose to be named, keyed by that name.
     *
     * Grouped case-insensitively on the name the donor put on their own gift. Two different people
     * who typed the same name merge into one row — accepted deliberately, because the alternative is
     * emitting a donor identifier the console has no route for and every export would then carry.
     */
    named: z.array(z.object({
        name: z.string(),
        giftCount: z.number().int(),
        total: moneySchema,
        firstGiftAt: z.string(),
        lastGiftAt: z.string(),
    })),
    /**
     * Every anonymous gift in the window, as one figure.
     *
     * No identifier, no dates, and no split by donor. A per-donor anonymous row — even under a hash
     * — would be a stable pseudonym joinable across reports, and a bucket of one carrying an amount
     * and a timestamp is a person.
     */
    anonymous: z.object({
        giftCount: z.number().int(),
        total: moneySchema,
    }),
    /** True when more named donors exist than were returned; the CSV carries the rest. */
    truncated: z.boolean(),
});
export const reconciliationRunReportSchema = z.object({
    id: z.string(),
    windowStart: z.string(),
    windowEnd: z.string(),
    startedAt: z.string(),
    completedAt: z.string().nullable(),
    matchedCount: z.number().int(),
    varianceCount: z.number().int(),
    failedReason: z.string().nullable(),
    triggeredBy: z.string(),
});
export const reconciliationFindingSchema = z.object({
    id: z.string(),
    kind: z.string(),
    severity: z.enum(["info", "warn", "critical"]),
    organizationDisplayName: z.string().nullable(),
    expected: moneySchema.nullable(),
    actual: moneySchema.nullable(),
    detail: z.string(),
    suggestion: z.string(),
    firstSeenAt: z.string(),
    lastSeenAt: z.string(),
    seenCount: z.number().int(),
    resolvedAt: z.string().nullable(),
    resolvedByName: z.string().nullable(),
    resolution: z.string().nullable(),
    resolutionNote: z.string().nullable(),
});
export const reconciliationReportSchema = z.object({
    range: reportRangeSchema,
    runs: z.array(reconciliationRunReportSchema),
    findings: z.array(reconciliationFindingSchema),
    totals: z.object({
        runs: z.number().int(),
        failedRuns: z.number().int(),
        matched: z.number().int(),
        findings: z.number().int(),
        unresolved: z.number().int(),
        critical: z.number().int(),
    }),
    /**
     * True when no reconciliation run touched this window at all.
     *
     * The distinction the whole report exists for: an empty findings list under a window nothing
     * checked means nothing has been checked, not that everything agrees.
     */
    neverChecked: z.boolean(),
});
export const sweepRunSchema = z.object({
    id: z.string(),
    organizationId: z.string(),
    organizationDisplayName: z.string().nullable(),
    ranAt: z.string(),
    outcome: z.enum(["swept", "below_minimum", "nothing_available", "refused", "error"]),
    detail: z.string().nullable(),
    amount: moneySchema.nullable(),
    payoutId: z.string().nullable(),
    manual: z.boolean(),
});
export const sweepsReportSchema = z.object({
    range: reportRangeSchema,
    runs: z.array(sweepRunSchema),
    /**
     * Every standing instruction, whether or not it ran in the window.
     *
     * `overdueDays` is the number an operator is actually looking for: a schedule whose next run is
     * in the past has not been executed, and on a deployment where no worker is running that is the
     * normal state rather than a surprise. It is the difference between "we have not been paid" and
     * "the timer has been dead for a month".
     */
    schedules: z.array(z.object({
        organizationId: z.string(),
        displayName: z.string(),
        enabled: z.boolean(),
        description: z.string(),
        minimum: moneySchema,
        maximum: moneySchema.nullable(),
        nextRunAt: z.string(),
        overdueDays: z.number().int(),
        lastRunAt: z.string().nullable(),
        lastRunOutcome: z.string().nullable(),
    })),
    totals: z.object({
        runs: z.number().int(),
        swept: z.number().int(),
        belowMinimum: z.number().int(),
        nothingAvailable: z.number().int(),
        refused: z.number().int(),
        errors: z.number().int(),
        manual: z.number().int(),
        sweptAmount: moneySchema,
        schedulesEnabled: z.number().int(),
        schedulesOverdue: z.number().int(),
    }),
});
export const closeReconciliationSchema = z.object({
    date: z.string(),
    windowStart: z.string(),
    windowEnd: z.string(),
    /** Runs whose window contains the whole UTC day. */
    coveringRuns: z.array(reconciliationRunReportSchema),
    /** Runs that overlap the day without containing it — part of the day was checked. */
    partialRuns: z.array(reconciliationRunReportSchema),
    findings: z.array(reconciliationFindingSchema),
    totals: z.object({
        unresolved: z.number().int(),
        critical: z.number().int(),
        resolved: z.number().int(),
    }),
    /** No run touched this day. An empty findings list below this flag means nothing. */
    neverChecked: z.boolean(),
    /** A run touched the day and crashed. Checked, and the check did not finish. */
    checkFailed: z.boolean(),
});
/* ---------------------------------------------------- platform settings (GH-829) */
/**
 * A reason, required wherever a platform financial control changes. The audit row without one
 * answers nothing later (SPEC.md §12.3: "actor, subject, tenant, before/after, reason ...").
 */
export const controlChangeReasonSchema = z.string().trim().min(3).max(500);
/** A minor-unit amount an operator types. Up to $100,000,000.00; never negative, never a float. */
const WHOLE_CENTS = /^\d{1,10}$/;
// The refinement guards its own BigInt: zod runs it even after the regex has failed, and
// `BigInt("10.5")` throws — which would turn a typo into a 500.
const controlAmountMinorSchema = z
    .string()
    .regex(WHOLE_CENTS, "must be a whole number of cents")
    .refine((value) => !WHOLE_CENTS.test(value) || BigInt(value) <= 10000000000n, "is above the largest limit allowed");
/** Who last moved a setting, from the audit log. Null where it was never changed (the default). */
export const settingLastChangeSchema = z.object({
    changedAt: z.string(),
    changedByName: z.string().nullable(),
});
export const paymentsRiskSettingsSchema = z.object({
    achRail: z.object({
        rail: z.enum(["plaid", "stripe"]),
        /** "Switched to Plaid Sep 14 by Osama Bedier". Null when the rail has never been switched. */
        lastChange: settingLastChangeSchema.extend({ rail: z.enum(["plaid", "stripe"]) }).nullable(),
    }),
    twoPersonRule: z.object({
        /** Cash plus check total at or above which a second, different person must verify a batch. */
        threshold: moneySchema,
        defaultThreshold: moneySchema,
        isDefault: z.boolean(),
        lastChange: settingLastChangeSchema.nullable(),
    }),
    refundLimits: z.object({
        singleRefund: moneySchema,
        perOrganizationPerDay: moneySchema,
        /** The per-organization limit counts refunds over a rolling 24 hours, not a calendar day. */
        window: z.literal("rolling_24_hours"),
        isDefault: z.boolean(),
        lastChange: settingLastChangeSchema.nullable(),
    }),
    /** Whether a change here demands a fresh re-authentication on this deployment. */
    stepUpRequired: z.boolean(),
});
export const updateTwoPersonRuleSchema = z
    .object({ thresholdMinor: controlAmountMinorSchema, reason: controlChangeReasonSchema })
    .strict();
export const updateRefundLimitsSchema = z
    .object({
    singleRefundMinor: controlAmountMinorSchema,
    perOrganizationPerDayMinor: controlAmountMinorSchema,
    reason: controlChangeReasonSchema,
})
    .strict()
    .refine((value) => !WHOLE_CENTS.test(value.singleRefundMinor) ||
    !WHOLE_CENTS.test(value.perOrganizationPerDayMinor) ||
    BigInt(value.singleRefundMinor) <= BigInt(value.perOrganizationPerDayMinor), {
    message: "The single-refund limit cannot be above the daily limit.",
    path: ["singleRefundMinor"],
});
/* ------------------------------------------------ the platform fee, edited (GH-839) */
/**
 * Saving the fee schedule. Critical (`platform.manage_fee`, step-up) and carries the operator's
 * reason, because this is the one setting that changes what every organization is paid.
 */
export const updatePlatformFeeSchema = platformFeeSchema
    .extend({
    reason: controlChangeReasonSchema,
    /**
     * The operator's "I understand" (GH-844). Required — and only meaningful — when the schedule
     * would leave the platform below the processor's recorded card price on some gift ≥ $1; the
     * server refuses such a save without it (409 `conflict`, naming the first losing gift).
     */
    acknowledgeLoss: z.literal(true).optional(),
})
    .strict();
/* -------------------------------------- what the processor charges us (GH-844) */
const costDigits = (field, max, message) => z
    .string()
    .regex(/^\d{1,5}$/, `${field} must be a non-negative integer string`)
    .refine((value) => Number(value) <= max, message);
/**
 * What the card processor actually charges this account — a recorded fact, separate from the fee
 * schedule, so the preview and the loss warning use the real price rather than a literal. Cards
 * and wallets: a percentage (half-up to the cent) plus a fixed part. Bank debits: a percentage up
 * to a cap. Never shown to a donor.
 */
export const processorCostSchema = z
    .object({
    rateBps: costDigits("rateBps", 1000, "A card price above 10% is not a processor price"),
    fixedMinor: costDigits("fixedMinor", 100, "A fixed card price above $1.00 is not plausible"),
    roundingRule: z.literal("half_up"),
    achRateBps: costDigits("achRateBps", 1000, "A bank-debit price above 10% is not plausible"),
    achCapMinor: costDigits("achCapMinor", 2000, "A bank-debit cap above $20.00 is not plausible"),
})
    .strict();
/** Recording a new processor price: critical (`platform.manage_processor_cost`) and reasoned. */
export const updateProcessorCostSchema = processorCostSchema
    .extend({ reason: controlChangeReasonSchema })
    .strict();
/** Covered and declined, side by side — the two numbers every row of the preview compares. */
const coveredDeclinedSchema = z.object({ covered: signedMoneySchema, declined: signedMoneySchema });
/** A signed whole number of basis points: 70 = 0.7%, −12 = −0.12%. */
const signedBpsSchema = z.string().regex(/^-?\d+$/, "basis points must be a whole number");
/**
 * One gift size in the preview table, computed by the server with the same functions the quote
 * uses. `processorFee` is the **recorded** processor card price (`processor_cost`) on the amount
 * actually charged — an estimate for this screen only; nothing in the money path reads it.
 */
export const platformFeePreviewRowSchema = z.object({
    gift: moneySchema,
    /** The platform fee charged: fee(T) when covered, fee(gift) when declined. */
    fee: coveredDeclinedSchema,
    /** What the donor pays: the gross-up T when covered, the gift when declined. */
    donorTotal: coveredDeclinedSchema,
    /** The card price on what was charged. */
    processorFee: coveredDeclinedSchema,
    /** The platform margin: fee less the card price. Negative means the platform loses money. */
    platformNet: coveredDeclinedSchema,
    /** The margin as basis points of the gift, rounded half away from zero. */
    marginBps: z.object({ covered: signedBpsSchema, declined: signedBpsSchema }),
    /** The same gift paid by bank debit: the ACH price, and the margin after it. Informational. */
    achProcessorFee: coveredDeclinedSchema,
    achPlatformNet: coveredDeclinedSchema,
    organizationNet: coveredDeclinedSchema,
});
export const platformFeePreviewSchema = z.object({
    fee: platformFeeSchema,
    /** The schedule in words, as every surface states it: "2.9% + $0.30 per gift". */
    label: z.string(),
    /** The processor price the preview used, and in words: "2.2% + $0.30", "0.8%, capped at $5.00". */
    processorCost: processorCostSchema,
    processorCostLabel: z.string(),
    achProcessorCostLabel: z.string(),
    rows: z.array(platformFeePreviewRowSchema),
    /**
     * The smallest gift ≥ $1 — at any size, not only the rows above — on which the fee is below the
     * card price, computed exactly by the server; null when there is none.
     */
    firstLoss: z.object({ gift: moneySchema, choice: z.enum(["covered", "declined"]) }).nullable(),
    /** True exactly when `firstLoss` is set — the console shows a warning and asks for a confirmation. */
    losesMoney: z.boolean(),
});
/**
 * What the console shows: the schedule in force, and the server-computed preview at $1, $5, $10,
 * $20, $100 and $1,000. Computed server-side so the browser never holds a second copy of the fee
 * arithmetic or of Stripe's price list.
 */
export const platformFeeResponseSchema = platformFeePreviewSchema.extend({
    /** True when no operator value is stored and the built-in default is in force. */
    isDefault: z.boolean(),
    /** True when no processor price is recorded and Stripe's standard price is assumed. */
    processorCostIsDefault: z.boolean(),
    /** Whether saving needs a fresh step-up on this deployment. */
    stepUpRequired: z.boolean(),
});
/** Saving the default ticket types (GH-352), now with an optional reason for the audit row. */
export const updateDefaultTicketTypesSchema = defaultTicketTypesSchema.extend({
    reason: z.string().trim().max(500).optional(),
});
/* ------------------------------------------------ refunds held for a second approver */
export const HELD_REFUND_STATUSES = ["awaiting_second_approval", "approved", "declined"];
export const heldRefundSchema = z.object({
    id: z.string().uuid(),
    /**
     * What the refund is of (GH-840): a ticket order, or a gift. Exactly one of the two ids is set.
     * `subject` defaults to `ticket_order` so a response from before gifts could be held still reads.
     */
    subject: z.enum(["ticket_order", "gift"]).default("ticket_order"),
    ticketOrderId: z.string().uuid().nullable(),
    donationId: z.string().uuid().nullable().default(null),
    /** On a gift: the platform fee the refund keeps, so the approver sees the whole arithmetic. */
    platformFeeKept: moneySchema.nullable().default(null),
    amount: moneySchema,
    status: z.enum(HELD_REFUND_STATUSES),
    /**
     * A platform limit (`single_limit`, `daily_limit`), or — GH-952, DEC-059 — the organization's own
     * rule: `org_every_refund` (a second person approves every refund) or `org_above_amount` (above
     * `approvalThreshold`). A platform limit is named when both apply.
     */
    heldBecause: z.enum(["single_limit", "daily_limit", "org_every_refund", "org_above_amount"]),
    /** The limits and the 24-hour total as they stood when the refund was held. */
    singleRefundLimit: moneySchema,
    perOrganizationPerDayLimit: moneySchema,
    refundedInWindow: moneySchema,
    /** GH-952: the organization's amount when its rule was `above_amount` at the hold; else null. */
    approvalThreshold: moneySchema.nullable().default(null),
    /**
     * GH-952: whether anyone in the organization other than the requester holds the permission to
     * approve it. False means it can only be withdrawn or declined until somebody is added — the
     * console says `NO_OTHER_REFUND_APPROVER_MESSAGE`. Computed when read.
     */
    approverAvailable: z.boolean().default(true),
    /**
     * GH-953 (DEC-059 amended): the requester may approve their own — they are an owner or admin
     * and nobody else in the organization holds the permission to approve. Computed when read, so
     * adding someone in Team turns it off and removing the last other approver turns it on.
     */
    canSelfApprove: z.boolean().default(false),
    /**
     * GH-954: who else can approve it — display names as Team shows them, sorted, at most five, the
     * requester and platform staff left out. Empty on a decided request. The console tells the
     * requester who to ask. `approversMore` counts the names beyond the list.
     */
    approvers: z.array(z.string()).default([]),
    approversMore: z.number().int().nonnegative().default(0),
    /** GH-953: approved by the requester themself, as the sole approver. */
    selfApproved: z.boolean().default(false),
    requestedByName: z.string().nullable(),
    requestedAt: z.string(),
    reason: z.string().nullable(),
    decidedByName: z.string().nullable(),
    decidedAt: z.string().nullable(),
    decisionReason: z.string().nullable(),
});
export const heldRefundListResponseSchema = z.object({ requests: z.array(heldRefundSchema) });
/** Asking for a ticket refund. The body is optional; a reason helps the second approver. */
export const requestTicketRefundSchema = z
    .object({ reason: z.string().trim().max(500).optional() })
    .strict();
/**
 * The answer to a refund request: done (200), or held for a second person (202). `refunded` is
 * kept from the earlier `{ refunded: true }` so an older console reading only that key is not told
 * a held refund went through.
 */
export const ticketRefundResponseSchema = z.discriminatedUnion("status", [
    z.object({ status: z.literal("refunded"), refunded: z.literal(true) }),
    z.object({
        status: z.literal("awaiting_second_approval"),
        refunded: z.literal(false),
        request: heldRefundSchema,
        message: z.string(),
        /** GH-952: false when nobody else here can approve it (and `message` says so). */
        approverAvailable: z.boolean().default(true),
        /** GH-953: the requester, as the sole approver, may approve it themself (`message` says so). */
        canSelfApprove: z.boolean().default(false),
        /** GH-954: the same names as on `request`, for a reader that skips the nested request. */
        approvers: z.array(z.string()).default([]),
        approversMore: z.number().int().nonnegative().default(0),
    }),
    /** GH-948: 202, held by the processor; the order stays paid until it completes. */
    processorApprovalSchema.extend({
        status: z.literal("awaiting_processor_approval"),
        refunded: z.literal(false),
        message: z.string(),
    }),
]);
/* ------------------------------------------------------------ gift refunds (GH-840) */
/**
 * Why a gift is being refunded (DEC-006, amended 2026-08-27): only a transaction that should not
 * have happened. A change of mind is not one of them.
 */
export const GIFT_REFUND_REASON_CODES = ["duplicate", "wrong_amount", "unauthorized"];
/** `POST /v1/organizations/:organizationId/gifts/:giftId/refund` */
export const requestGiftRefundSchema = z
    .object({
    reason: z.enum(GIFT_REFUND_REASON_CODES),
    note: z.string().trim().max(500).optional(),
})
    .strict();
/** The figures of a gift refund: what the donor paid, gets back, and the fee kept. */
export const giftRefundFiguresSchema = z.object({
    charged: moneySchema,
    refund: moneySchema,
    platformFeeKept: moneySchema,
});
/**
 * The answer: refunded (200), held for a second person (202), or held by the processor for
 * approval in its dashboard (202, GH-948 — nothing refunded yet). `posted` is false when the
 * processor accepted the refund but has not yet confirmed it; the books follow its confirmation.
 */
export const giftRefundResponseSchema = z.discriminatedUnion("status", [
    z.object({
        status: z.literal("refunded"),
        refunded: z.literal(true),
        posted: z.boolean(),
        figures: giftRefundFiguresSchema,
    }),
    z.object({
        status: z.literal("awaiting_second_approval"),
        refunded: z.literal(false),
        request: heldRefundSchema,
        message: z.string(),
        /** GH-952: false when nobody else here can approve it (and `message` says so). */
        approverAvailable: z.boolean().default(true),
        /** GH-953: the requester, as the sole approver, may approve it themself (`message` says so). */
        canSelfApprove: z.boolean().default(false),
        /** GH-954: the same names as on `request`, for a reader that skips the nested request. */
        approvers: z.array(z.string()).default([]),
        approversMore: z.number().int().nonnegative().default(0),
    }),
    processorApprovalSchema.extend({
        status: z.literal("awaiting_processor_approval"),
        refunded: z.literal(false),
        message: z.string(),
        figures: giftRefundFiguresSchema,
    }),
]);
export const approveHeldRefundSchema = z
    .object({ reason: z.string().trim().max(500).optional() })
    .strict();
export const declineHeldRefundSchema = z.object({ reason: controlChangeReasonSchema }).strict();
/* ------------------------------------------------------------- system health */
export const HEALTH_STATUSES = ["healthy", "degraded", "down", "not_configured"];
export const healthStatusSchema = z.enum(HEALTH_STATUSES);
export const webhookProviderHealthSchema = z.object({
    /** stripe, plaid, chariot, ses — and `fake` on a deployment running the synthetic rail. */
    provider: z.string(),
    /** False where the provider's notifications never pass through the inbox (Plaid, SES). */
    usesInbox: z.boolean(),
    status: healthStatusSchema,
    receivedLast24h: z.number().int().nonnegative(),
    processedLast24h: z.number().int().nonnegative(),
    failed: z.number().int().nonnegative(),
    stuck: z.number().int().nonnegative(),
    oldestFailedAt: z.string().nullable(),
    lastReceivedAt: z.string().nullable(),
    /** One sentence for the row: what the counts mean, or why there are none. */
    detail: z.string(),
});
export const scheduledJobHealthSchema = z.object({
    job: z.string(),
    label: z.string(),
    recorded: z.boolean(),
    runCount: z.number().int().nonnegative(),
    lastRanAt: z.string().nullable(),
    lastStatusCode: z.number().int().nullable(),
    lastSucceededAt: z.string().nullable(),
    lastFailedAt: z.string().nullable(),
});
/**
 * Read-only state of the deployment. **Never a secret**: modes and counts only — no key, no
 * token, no address, no provider identifier. A test asserts no configured secret appears in it.
 */
export const platformHealthSchema = z.object({
    checkedAt: z.string(),
    appEnvironment: z.string(),
    database: z.object({
        reachable: z.boolean(),
        latencyMs: z.number().int().nonnegative().nullable(),
    }),
    webhooks: z.array(webhookProviderHealthSchema),
    /** Failed or stuck inbox rows a replay would try now. */
    replayable: z.number().int().nonnegative(),
    scheduledJobs: z.array(scheduledJobHealthSchema),
    email: z.object({
        status: healthStatusSchema,
        deliverable: z.boolean(),
        /** The EMAIL_PURPOSES allowlist; null means every purpose ("*"). */
        purposes: z.array(z.string()).nullable(),
        suppressedAddresses: z.number().int().nonnegative().nullable(),
        bounceNotificationsConfigured: z.boolean(),
    }),
    sms: z.object({ status: healthStatusSchema, deliverable: z.boolean() }),
    providers: z.object({
        paymentProvider: z.enum(["stripe", "fake"]),
        stripeMode: stripeModeSchema,
        plaid: z.object({
            configured: z.boolean(),
            environment: z.enum(["sandbox", "development", "production"]).nullable(),
        }),
        daf: z.object({
            configured: z.boolean(),
            environment: z.enum(["sandbox", "production"]).nullable(),
        }),
    }),
});
export const replayWebhooksSchema = z
    .object({
    reason: controlChangeReasonSchema,
    /** At most this many rows in one press. */
    limit: z.number().int().min(1).max(200).optional(),
})
    .strict();
export const REPLAY_OUTCOMES = [
    "applied",
    "duplicate",
    "ignored_stale",
    "rejected_illegal",
    "unknown_account",
    "failed",
    "skipped",
];
const replayCountSchema = z.number().int().nonnegative();
export const replayWebhooksResponseSchema = z.object({
    attempted: z.number().int().nonnegative(),
    /** How many rows landed in each outcome. `skipped`: another environment's or provider's row. */
    outcomes: z.object({
        applied: replayCountSchema,
        duplicate: replayCountSchema,
        ignored_stale: replayCountSchema,
        rejected_illegal: replayCountSchema,
        unknown_account: replayCountSchema,
        failed: replayCountSchema,
        skipped: replayCountSchema,
    }),
    stillFailing: z.number().int().nonnegative(),
});
/* ------------------------------------------------------------- feature gates */
export const featureGateDecisionSchema = z.object({
    id: z.string(),
    title: z.string().nullable(),
    owner: z.string().nullable(),
    /** RESOLVED only when the decision is in the code's evidenced RESOLVED_DECISIONS list. */
    status: z.enum(["OPEN", "RESOLVED"]),
    /** The register's own status text, which can say DECIDED-DIRECTION while the gate stays shut. */
    registerStatus: z.string().nullable(),
});
export const featureGateSchema = z.object({
    gate: z.string(),
    label: z.string(),
    /** Where the switch lives: an environment variable, or a platform setting held in the console. */
    kind: z.enum(["environment", "platform_setting"]),
    decisions: z.array(featureGateDecisionSchema),
    /** Is it on in this deployment, right now. */
    openHere: z.boolean(),
    /** Could production boot with it on today (every blocking decision resolved with evidence). */
    permittedInProduction: z.boolean(),
});
export const featureGatesResponseSchema = z.object({
    gates: z.array(featureGateSchema),
    /** The rule, said on the screen: "A gate opens only when its decision has an owner and evidence." */
    rule: z.string(),
});
//# sourceMappingURL=v1.js.map